// DispatchDeck - JSA (Job Safety Analysis) Module
// Daily pre-job safety form: hazards, PPE, crew signatures, work plan

import {
  db,
  functions,
  httpsCallable,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  orderBy,
  where,
  onSnapshot,
  Timestamp
} from '../firebase-config.js?v=1868';
import '../utils/recordDate.js?v=9923'; // TASK-9923 JSA date = company calendar, never UTC

// ============================================
// JSA STATE
// ============================================
const JsaState = {
  reports: [],
  reportsLoaded: false, // TASK-9581 COD r9 — first jsaReports snapshot arrived
  reportsFailed: false, // TASK-9581 COD r9 — the jsaReports listener errored (archive says so)
  people: [],
  customers: [],       // from tenants/{tenantId}/customers
  jobs: [],            // from tenants/{tenantId}/jobs (active only)
  jobDays: [],
  trucks: [],          // for resolving truck display names
  unsubscribers: [],
  crewSignatures: {},  // personId -> { name, signature base64, acknowledgedAt }
  taskRows: [],        // dynamic task/hazard/control rows
  selectedHazards: new Set(),
  selectedPPE: new Set(),
  formMode: 'tits',           // 'tits' (Standard) | 'xto' (Oilfield) | 'template' (TASK-4839 per-tenant template)
  templates: [],               // TASK-4839: tenants/{tid}/jsaTemplates (subscribed only when settings.enableJsaTemplates)
  activeTemplate: null,        // TASK-4839: the live template doc a NEW sheet is being filled from
  templateSnapshot: null,      // TASK-4839: frozen {name, version, sections} the open form renders from
  templateChecks: new Set(),   // TASK-4839: checked field ids in checkbox-grid sections
  selectedTopics: new Set(),   // multi-select tailgate topics (Standard)
  emergencyFacility: null,     // { name, address, phone, distance } selected facility
  facilityResults: [],         // search results from Places API
  userLocation: null,          // { lat, lng } from GPS
  lsraChecks: {},              // { cardId: { applicable: bool, items: [] } } (Oilfield)
  hazardCategories: new Set(), // XTO back-page hazard-type chips (Oilfield)
  lessonsLearned: '',          // XTO back-page free-text reflection (Oilfield)
  xtoSignatures: {             // Oilfield signatures
    issuer: { name: '', signature: null },
    receiver: { name: '', signature: null }
  },
  editingJsaId: null,           // Set when editing existing report
  editingReport: null,          // Cached report data when editing
  prefillJobId: null,           // TASK-794: job to pre-link a NEW JSA to (from dispatch/jobboard "Start JSA")
  prefillJobDayId: null,        // TASK-794: exact jobDay to pre-link (multi-day correctness)
  returnTo: null,               // TASK-9511: {hash, jobId, jobDayId, surface?} — Cancel goes back to the job that opened the form (TASK-10011: surface 'dispatch' = the office Job Details)
  // TASK-10011 — which of initJSA's listeners have delivered their FIRST
  // snapshot since the view mounted. startForJob waits on these (bounded) so a
  // form opened from a job has the job to prefill and the company's templates
  // on the first open. Reset by cleanupJSA.
  firstSnap: { jobs: false, people: false, templates: false },
  prefillApplied: false,        // TASK-10011: the job prefill landed on THIS new form
  pendingJobForm: null,         // TASK-10011: token of the one startForJob wait in flight
  prefillJobDay: null,          // TASK-10011 COD r1: the prefill jobDay doc, kept so the save binds THAT day (not only today's)
  modeChosenByUser: false,      // TASK-9511: user picked Standard/Oilfield/template on THIS new form
  newFormTouched: false,        // TASK-9511: user typed into THIS new form (never auto-switch under them)
  // TASK-398: stale-async-write guard. editJSAReport prefills the SHARED #modal-jsa
  // form via setTimeout (with a nested setTimeout that writes #jsa-job from a
  // captured report.jobId). Without a guard, editJSAReport(A) then editJSAReport(B)
  // within ~150ms lets A's (nested) timer stamp A's job onto B's form → saveJSA reads
  // #jsa-job from the DOM and persists it onto editingJsaId(=B) → report B corrupted.
  // jsaFormGen is bumped on every JSA form open/close; the prefill timers capture it
  // and abort if it moved. _jsaPrefillTimers holds the handles for clearTimeout.
  jsaFormGen: 0,
  _jsaPrefillTimers: []
};

// TASK-398: invalidate any in-flight JSA-form prefill (outer + nested setTimeout)
// and cancel pending timers. Call at the top of every JSA form open AND on close.
// ---------------------------------------------------------------------------
// TASK-4529 — fleet ref resolver. A jobDay/ticket ref may be a Firestore doc
// ID, a unit number, a nickname, or a legacy name — and on a live tenant one
// truck's DOC ID can equal another truck's UNIT number (TITS: doc id "15" =
// Dove, while Blue Jay's unit = "15"). A single-pass
// `find(t => t.id === ref || t.unit === ref)` returns whichever happens to
// sort first, so ID matches must be exhausted across the WHOLE list before
// any unit/nickname/name match is considered.
// KEEP IN SYNC — identical copies live in dispatch.js / jobboard.js /
// clock.js / billing.js / bi.js / jsa.js (cross-imports forbidden: Pitfall #1
// — ?v= cache busters make duplicate module instances). Twin registered in
// docs/audits/twin-audit-2026-08-19.md. A seventh SERVER copy lives in
// functions/src/services/ticketGateway.js. Byte-equality of all copies is
// asserted by tests/task4529-fleet-ref-resolver.unit.cjs.
function _resolveFleetRef(ref, list) {
  if (ref === null || ref === undefined || ref === '') return null;
  const s = String(ref);
  const arr = list || [];
  const byId = arr.find(x => x && x.id !== null && x.id !== undefined && String(x.id) === s);
  if (byId) return byId;
  const eq = (v) => v !== null && v !== undefined && v !== '' && String(v) === s;
  return arr.find(x => x && (eq(x.unit) || eq(x.nickname) || eq(x.name) || eq(x.partNumber))) || null;
}

function bumpJsaFormGen() {
  JsaState.jsaFormGen++;
  if (JsaState._jsaPrefillTimers.length) {
    JsaState._jsaPrefillTimers.forEach(t => clearTimeout(t));
    JsaState._jsaPrefillTimers = [];
  }
}

// ============================================
// CONSTANTS
// ============================================
const JSA_HAZARDS = [
  { id: 'gravity', label: 'Gravity', icon: 'arrow_downward' },
  { id: 'motion', label: 'Motion', icon: 'speed' },
  { id: 'mechanical', label: 'Mechanical', icon: 'settings' },
  { id: 'electrical', label: 'Electrical', icon: 'bolt' },
  { id: 'pressure', label: 'Pressure', icon: 'compress' },
  { id: 'temperature', label: 'Temperature', icon: 'thermostat' },
  { id: 'biological', label: 'Biological', icon: 'coronavirus' },
  { id: 'radiation', label: 'Radiation', icon: 'radio_button_checked' },
  { id: 'people', label: 'People', icon: 'groups' },
  { id: 'location', label: 'Location', icon: 'place' },
  { id: 'chemical', label: 'Chemical', icon: 'science' },
  { id: 'sound', label: 'Sound', icon: 'volume_up' },
  { id: 'other', label: 'Other', icon: 'more_horiz' }
];

const JSA_PPE = [
  { id: 'hard_hat', label: 'Hard Hat', icon: 'engineering' },
  { id: 'steel_toed_boots', label: 'Steel Toed Boots', icon: 'do_not_step' },
  { id: 'glasses', label: 'Safety Glasses', icon: 'visibility' },
  { id: 'gas_monitor', label: 'Gas Monitor', icon: 'sensors' },
  { id: 'grinding_shield', label: 'Grinding Shield', icon: 'shield' },
  { id: 'welding_leathers', label: 'Welding Leathers', icon: 'local_fire_department' },
  { id: 'full_face_shield', label: 'Full Face Shield', icon: 'face_retouching_natural' },
  { id: 'chemical_gloves', label: 'Chemical Gloves', icon: 'back_hand' },
  { id: 'gloves', label: 'Gloves', icon: 'pan_tool' },
  { id: 'dust_mask', label: 'Dust Mask', icon: 'masks' },
  { id: 'body_harness', label: 'Body Harness', icon: 'accessibility_new' },
  { id: 'goggles', label: 'Goggles', icon: 'preview' },
  { id: 'ear_plugs', label: 'Ear Plugs', icon: 'hearing_disabled' },
  { id: 'h2s_monitor', label: 'H2S Monitor', icon: 'air' },
  { id: 'kevlar_gloves', label: 'Kevlar Gloves', icon: 'front_hand' },
  { id: 'other', label: 'Other', icon: 'more_horiz' }
];

const LIFE_SAVING_RULES = [
  'Working at Heights',
  'Confined Space',
  'Lockout/Tagout (LOTO)',
  'Hot Work',
  'Line of Fire',
  'Driving Safety',
  'Bypassing Safety Controls',
  'Work Authorization',
  'Lifting Operations'
];

// XTO JSA back-page hazard-type categories — meta-categories that apply
// across task steps. Operator toggles any that are present on the job.
// Matches the icon row at the top of the XTO JSA back page.
const XTO_HAZARD_CATEGORIES = [
  { id: 'electrical_energy', label: 'Electrical Energy', icon: 'bolt' },
  { id: 'stored_energy', label: 'Stored Energy', icon: 'battery_charging_full' },
  { id: 'thermal_energy', label: 'Thermal Energy', icon: 'local_fire_department' },
  { id: 'moving_objects', label: 'Moving Objects', icon: 'directions_run' },
  { id: 'hazardous_substances', label: 'Hazardous Substances', icon: 'science' },
  { id: 'radiation', label: 'Radiation', icon: 'radio' },
  { id: 'lighting_visibility', label: 'Lighting & Visibility', icon: 'visibility' },
  { id: 'noise_vibration', label: 'Noise & Vibration', icon: 'graphic_eq' },
  { id: 'simops_stacked', label: 'Simultaneous Ops / Stacked Work', icon: 'layers' },
];

// Google Places API key (uses same Firebase project key — enable Places API on dispatch-deck-be88c)
const GOOGLE_PLACES_API_KEY = 'AIzaSyArVYfyFlw-heeo300We_CxcHtE_JaJVpc';

// Oilfield LSRA Cards — matches the industry-standard XTO LSRA form
const XTO_LSRA_CARDS = [
  { key: 'confined_space_entry', title: 'Confined Space Entry', subtitle: 'Obtain authorization before entering a confined space', icon: 'meeting_room', checklist: [
    { text: 'I confirm energy sources are isolated' },
    { text: 'I confirm the atmosphere has been tested and is monitored' },
    { text: 'I check and use my breathing apparatus when required' },
    { text: 'I confirm there is an attendant standing by' },
    { text: 'I confirm a rescue plan is in place' },
    { text: 'I obtain authorization to enter' }
  ]},
  { key: 'bypassing_safety_controls', title: 'Bypassing Safety Controls', subtitle: 'Obtain authorization before overriding or disabling safety controls', icon: 'block', checklist: [
    { text: 'I understand and use safety-critical equipment and procedures which apply to my task' },
    { text: 'I obtain authorization before:', subitems: ['Disabling or overriding safety equipment', 'Deviating from procedures', 'Crossing a barrier'] }
  ]},
  { key: 'energy_isolation', title: 'Energy Isolation', subtitle: 'Verify isolation and zero energy before work begins', icon: 'lock', checklist: [
    { text: 'I have identified all energy sources' },
    { text: 'I confirm that hazardous energy sources have been isolated, locked, and tagged' },
    { text: 'I have checked there is zero energy and tested for residual or stored energy' },
    { text: 'I never remove or tamper with energy control devices without authorization' },
    { text: 'I confirm equipment is back to safe operating condition prior to re-energizing' }
  ]},
  { key: 'excavation', title: 'Excavation', subtitle: 'Obtain authorization before digging or entering excavations', icon: 'construction', checklist: [
    { text: 'I obtain authorization prior to performing excavation/trenching/ground disturbance activities' },
    { text: 'I confirm underground utilities and structures are located, de-energized or protected' },
    { text: 'I confirm hazard controls and confined space entry as required' },
    { text: 'I monitor for changing conditions and exit as appropriate' }
  ]},
  { key: 'safe_mechanical_lifting', title: 'Safe Mechanical Lifting', subtitle: 'Plan lifting operations and control the area', icon: 'fitness_center', checklist: [
    { text: 'I confirm that the equipment and load have been inspected and are fit for purpose' },
    { text: 'I only operate equipment that I am qualified to use' },
    { text: 'I establish and obey barriers and exclusion zones' },
    { text: 'I never walk under a suspended load' },
    { text: 'I understand the lift plan prior to starting work and keep hands off load unless authorized' },
    { text: 'I establish and maintain communication with lifting and rigging personnel' }
  ]},
  { key: 'hot_work', title: 'Hot Work', subtitle: 'Control flammables and ignition sources', icon: 'local_fire_department', checklist: [
    { text: 'I identify and control ignition sources' },
    { text: 'Before starting any hot work:', subitems: ['I confirm flammable material has been removed or isolated', 'I obtain authorization'] },
    { text: 'Before starting hot work in a hazardous area I confirm:', subitems: ['A gas test has been completed', 'Gas will be monitored continually'] }
  ]},
  { key: 'working_around_mobile_equipment', title: 'Working Around Mobile Equipment', subtitle: 'See and be seen; maintain a safe distance', icon: 'local_shipping', checklist: [
    { text: 'I plan work to separate workers and mobile equipment' },
    { text: 'I only operate mobile equipment if qualified and use spotters where required' },
    { text: 'I verify safety devices are functional on mobile equipment' },
    { text: 'I establish clear means of communication with equipment operator' },
    { text: 'I stay out of the equipment\'s exclusion zone while in operation' },
    { text: 'I confirm controls are in place to prevent unintentional movement of mobile equipment and/or loads' }
  ]},
  { key: 'working_at_height', title: 'Working at Height', subtitle: 'Protect yourself against a fall when working at height', icon: 'straight', checklist: [
    { text: 'I inspect my fall protection equipment before use' },
    { text: 'I secure tools and work materials to prevent dropped objects' },
    { text: 'I tie-off 100% to approved anchor points while outside protected area' },
    { text: 'I have validated prompt rescue capability when wearing fall protection' },
    { text: 'I will follow safe work practices when climbing, descending or working from ladders' }
  ]},
  { key: 'breaking_containment_ope', title: 'Breaking Containment/OPE', subtitle: 'Manage ignition sources and exposure to energy & hazardous substances', icon: 'plumbing', checklist: [
    { text: 'I verify opening/break location(s)' },
    { text: 'I confirm energy isolation is complete' },
    { text: 'I manage ignition sources and confirm gas testing is completed when required' },
    { text: 'I use appropriate PPE and defensive behaviors when opening process equipment' }
  ]},
  { key: 'driving', title: 'Driving', subtitle: 'Wear a seat-belt and do not speed; no distractions', icon: 'directions_car', checklist: [
    { text: 'I always wear a seatbelt' },
    { text: 'I do not exceed the speed limit, and reduce my speed for road conditions' },
    { text: 'I do not use phones or operate devices while driving' },
    { text: 'I am fit, rested and fully alert while driving' },
    { text: 'I follow journey management requirements' }
  ]},
  { key: 'work_authorization', title: 'Work Authorization', subtitle: 'Work with a valid work permit when required', icon: 'assignment', checklist: [
    { text: 'I have confirmed if a permit is required' },
    { text: 'I am authorized to perform the work' },
    { text: 'I understand the permit' },
    { text: 'I have confirmed that hazards are controlled and it is safe to start' },
    { text: 'I stop and reassess if conditions change' }
  ]},
  { key: 'line_of_fire', title: 'Line of Fire', subtitle: 'Keep yourself and others out of the line of fire', icon: 'warning', checklist: [
    { text: 'I position myself to avoid:', subitems: ['Moving objects', 'Vehicles', 'Pressure releases', 'Dropped objects'] },
    { text: 'I establish and obey barriers and exclusion zones' },
    { text: 'I take action to secure loose objects and report potential dropped objects' }
  ]}
];

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

// Returns the XTO_LSRA_CARDS filtered by tenant settings.lsraRules.
// Missing key OR true = enabled (preserves behavior for tenants who never
// touched the setting). Only an explicit `false` hides the rule.
// IMPORTANT: only applied to the NEW-form render path (getXTOFormHTML).
// getXTODetailHTML and printXTOReport must keep iterating the full
// XTO_LSRA_CARDS so historical reports never drop rules retroactively.
function getEnabledLsraCards() {
  const rulesMap = window.AppState?.settings?.lsraRules;
  if (!rulesMap || typeof rulesMap !== 'object') return XTO_LSRA_CARDS;
  return XTO_LSRA_CARDS.filter(c => rulesMap[c.key] !== false);
}

// ============================================
// INITIALIZE JSA
// ============================================
export function initJSA(container, tenantId) {
  console.log('[JSA] Initializing for tenant:', tenantId);
  if (!tenantId) return;

  const jsaPanel = container.querySelector('#jsa-panel');
  if (!jsaPanel) return;

  const onSubError = (label) => (error) => {
    console.error(`[JSA] ${label} subscription failed:`, error.code, error.message);
  };

  // Subscribe to JSA reports
  const jsaRef = collection(db, 'tenants', tenantId, 'jsaReports');
  const jsaQuery = query(jsaRef, orderBy('date', 'desc'));
  JsaState.reportsLoaded = false;
  JsaState.reportsFailed = false;
  const unsubJSA = onSnapshot(jsaQuery, (snapshot) => {
    JsaState.reports = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    JsaState.reportsLoaded = true;
    JsaState.reportsFailed = false;
    renderJSAList();
    renderMyJsaSection();
    renderJSAArchive(); // TASK-9581 — the archive builds its own filter options
    // TASK-9371 COD r6: the OPEN detail follows the server-owned re-sign flag
    // (the trigger clears it a moment after an Oilfield re-sign / sign-in).
    syncOpenJsaDetail(JsaState.reports);
  }, (error) => {
    onSubError('jsaReports')(error);
    // TASK-9581 COD r9 — the archive says the read failed, never "No JSA reports".
    JsaState.reportsFailed = true;
    renderJSAList(); // COD r12 — the recent list says it too
    renderJSAArchive();
  });
  JsaState.unsubscribers.push(unsubJSA);

  // Subscribe to people
  const peopleRef = collection(db, 'tenants', tenantId, 'people');
  JsaState.firstSnap = { jobs: false, people: false, templates: false };
  const unsubPeople = onSnapshot(peopleRef, (snapshot) => {
    JsaState.people = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    JsaState.firstSnap.people = true;
  }, (e) => { JsaState.firstSnap.people = true; onSubError('people')(e); });
  JsaState.unsubscribers.push(unsubPeople);

  // Subscribe to today's jobDays (widen range to catch UTC-midnight legacy dates)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const dayAfter = new Date(today); dayAfter.setDate(dayAfter.getDate() + 2);
  const todayStr = window.RecordDate.today(); // TASK-9923 (was the UTC date of local midnight)

  const jobDaysRef = collection(db, 'tenants', tenantId, 'jobDays');
  const jobDaysQuery = query(
    jobDaysRef,
    where('date', '>=', Timestamp.fromDate(yesterday)),
    where('date', '<', Timestamp.fromDate(dayAfter))
  );
  const unsubJobDays = onSnapshot(jobDaysQuery, (snapshot) => {
    JsaState.jobDays = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(jd => {
      const d = jd.date?.toDate ? jd.date.toDate() : new Date(jd.date);
      return d.toISOString().split('T')[0] === todayStr || d.toLocaleDateString('en-CA') === todayStr;
    });
  }, onSubError('jobDays'));
  JsaState.unsubscribers.push(unsubJobDays);

  // Subscribe to trucks (for resolving truck display names on JSA cards)
  const trucksRef = collection(db, 'tenants', tenantId, 'trucks');
  const unsubTrucks = onSnapshot(trucksRef, (snapshot) => {
    JsaState.trucks = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.active !== false);
  }, onSubError('trucks'));
  JsaState.unsubscribers.push(unsubTrucks);

  // Subscribe to customers — TASK-9694 S6a: the crew-safe customerDirectory
  // (name + flags; the JSA picker reads name/type only). Drivers open JSAs on a
  // PIN session, which can no longer read the full `customers` docs.
  const unsubCustomers = window.CustomerSourceCore.create({ db, collection, doc, getDoc, getDocs, onSnapshot, query, orderBy })
    .subscribe(tenantId, (list) => {
      JsaState.customers = list;
    }, { label: 'JSA customers', orderByName: true, onError: onSubError('customers') });
  JsaState.unsubscribers.push(unsubCustomers);

  // Subscribe to jobs (active only, for Job dropdown)
  const jobsRef = collection(db, 'tenants', tenantId, 'jobs');
  const unsubJobs = onSnapshot(jobsRef, (snapshot) => {
    JsaState.jobs = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(j => j.status !== 'finished');
    JsaState.firstSnap.jobs = true;
    // TASK-10011 — a form opened from a job before this list arrived could not
    // prefill it (applyPrefillJobToForm found no job and gave up silently).
    lateApplyJobPrefill();
  }, (e) => { JsaState.firstSnap.jobs = true; onSubError('jobs')(e); });
  JsaState.unsubscribers.push(unsubJobs);

  // TASK-4839 — per-tenant JSA templates. Subscribed ONLY when the tenant flag is
  // on (85 of 86 tenants never pay this read). The flag, not a tenantId compare,
  // is the switch; the toggle button also requires >= 1 active template.
  if (isJsaTemplatesEnabled()) {
    const tplRef = collection(db, 'tenants', tenantId, 'jsaTemplates');
    const unsubTpl = onSnapshot(tplRef, (snapshot) => {
      JsaState.templates = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      JsaState.firstSnap.templates = true;
      // The new-JSA form can open before this first snapshot lands (job-board
      // "Start JSA" routes to #/pretrip and opens the form ~200ms after initJSA
      // subscribes). Add the template buttons to the already-open form.
      refreshNewJsaTemplateButtons();
    }, (e) => { JsaState.firstSnap.templates = true; onSubError('jsaTemplates')(e); });
    JsaState.unsubscribers.push(unsubTpl);
  }

  renderJSAList();
}

// ============================================
// GET JSA TAB HTML
// ============================================
export function getJSATabHTML() {
  return `
    <div id="jsa-panel" class="dvir-tab-panel">
      <!-- JSA Header -->
      <div class="pretrip-header" style="padding: 16px;">
        <div class="pretrip-header-left">
          <h1>Job Safety Analysis</h1>
          <p class="pretrip-subtitle">Daily pre-job safety assessment</p>
        </div>
      </div>

      <!-- Big Action Button -->
      <div class="pretrip-driver-action" style="padding: 16px 16px 8px;">
        <button id="btn-new-jsa" class="btn btn-primary btn-driver-inspection" onclick="window.JSAModule.showNewJSA()">
          <span class="material-icons">checklist</span>
          <span>New JSA</span>
        </button>
        <p class="pretrip-driver-hint">Tap to begin a new Job Safety Analysis</p>
      </div>

      <!-- TASK-361 — My Recent JSAs (driver/SPA-member view of their own submissions).
           Moved here 2026-05-26 from the bottom of the Job Board so drivers find
           their forms in the Inspections > JSA tab where they look for safety
           forms. Hidden entirely when N=0 (rendered by renderMyJsaSection). -->
      <div class="jsa-section jsa-my-recent" id="jsa-sec-mine-wrapper" style="display:none;margin:0 16px 8px;">
        <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-mine')">
          <span class="material-icons jsa-section-chevron">expand_more</span>
          <span id="jsa-sec-mine-label">My Recent JSAs (0)</span>
        </div>
        <div class="jsa-section-body" id="jsa-sec-mine">
          <div id="jsa-my-recent-list"></div>
        </div>
      </div>

      <!-- JSA List (recent only - full archive below) -->
      <div class="pretrip-list" id="jsa-list" style="padding: 0 16px 16px;">
        <div class="empty-state">
          <div class="empty-state-icon">🦺</div>
          <div class="empty-state-title">No JSA reports yet</div>
          <div class="empty-state-text">Complete a Job Safety Analysis before starting work.</div>
        </div>
      </div>

      <!-- JSA Reports Archive — TASK-9581: collapsed by default, last-day window, filters.
           Mounted by the lazy utils/jsaListWindow.js (renderJSAArchive), shared with HR/Compliance Safety. -->
      <div id="jsa-archive-mount"></div>
    </div>

    <!-- JSA Form Modal -->
    <div class="modal hidden" id="modal-jsa">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-jsa-title">New Job Safety Analysis</h3>
          <button class="btn-icon" onclick="window.JSAModule.closeJSAModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-jsa" class="modal-form-scrollable" novalidate onsubmit="window.JSAModule.saveJSA(event)">
          <div class="modal-form-scroll" id="jsa-form-scroll">
            <div id="jsa-mode-toggle-container"></div>
            <div id="jsa-form-body"></div>
          </div>

          <!-- Form Actions -->
          <div class="form-actions" style="padding: 12px 16px; border-top: 1px solid var(--border-light);">
            <button type="button" class="btn btn-secondary" onclick="window.JSAModule.closeJSAModal()">Cancel</button>
            <button type="submit" class="btn btn-primary" id="jsa-submit-btn">
              <span class="material-icons">check_circle</span> Submit JSA
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- JSA Detail Modal -->
    <div class="modal hidden" id="modal-jsa-detail">
      <div class="modal-content modal-large">
        <div class="modal-header" style="display:flex;align-items:center;gap:8px;">
          <h3 style="margin:0;flex:1;">JSA Report Details</h3>
          <!-- TASK-312 follow-up — XTO Edit button promoted to header so the
               footer doesn't carry 6 buttons. Edit is a "modify" action,
               distinct category from the export actions in the footer. -->
          <button type="button" class="btn btn-secondary" id="jsa-header-edit-btn" style="display:none;" onclick="window.JSAModule.editXTOReport()">
            <span class="material-icons" style="font-size:16px;">edit</span><span>Edit</span>
          </button>
          <button class="btn-icon" aria-label="Close" onclick="window.JSAModule.closeDetailModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-form-scrollable">
          <div class="modal-form-scroll" id="jsa-detail-content">
            <!-- Populated when viewing -->
          </div>
          <div class="form-actions" id="jsa-detail-actions" style="padding: 12px 16px; border-top: 1px solid var(--border-light);">
            <button type="button" class="btn btn-secondary" onclick="window.JSAModule.closeDetailModal()">Close</button>
            <button type="button" class="btn btn-primary" id="jsa-download-btn" onclick="window.JSAModule.downloadPdf()">
              <span class="material-icons">picture_as_pdf</span> Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- JSA Signature Overlay -->
    <div class="modal hidden" id="modal-jsa-signature">
      <div class="modal-content" style="max-width:440px;">
        <div class="modal-header">
          <h3 id="jsa-sig-title">Sign: Crew Member</h3>
          <button class="btn-icon" onclick="window.JSAModule.closeSignatureOverlay()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="padding:16px;">
          <div class="signature-pad-container" style="max-width:100%;">
            <!-- TASK-9147: these dimensions are LOAD-BEARING, not a styling choice. This pad is deliberately a FIXED 400x150 buffer with no devicePixelRatio scaling. Do NOT "make it crisp on retina" by copying the hr.js safety-sign-in pattern (canvas.width = getBoundingClientRect().width * 2), however much more correct that looks: it multiplies every signature PNG by ~6x, and these PNGs are stored INLINE as base64 inside jsaReports.crewAcknowledgments[] in a single Firestore document with a hard 1,048,576-byte cap. Measured 2026-09-10, all tenants: worst jsaReport = 64,462 bytes (6.15% of cap), mean signature 12,227 bytes. The DPR-scaled safety sheet that this exact shape already broke was at 91.8% with a 14th signer who could not sign. Re-run the census before changing this: node scripts/measure-jsa-report-sizes.cjs -->
            <canvas id="jsa-signature-canvas" width="400" height="150" style="display:block;width:100%;height:auto;aspect-ratio:400 / 150;border:2px solid #64748B;border-radius:8px;background:#fff;touch-action:none;cursor:crosshair;"></canvas>
          </div>
          <p class="signature-hint" style="text-align:center;font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">Sign above using your finger or mouse</p>
          <div style="display:flex;gap:8px;margin-top:12px;">
            <button type="button" class="btn btn-secondary" style="flex:1;" onclick="window.JSAModule.clearJSASignature()">
              <span class="material-icons" style="font-size:16px;">refresh</span> Clear
            </button>
            <button type="button" class="btn btn-primary" style="flex:1;" onclick="window.JSAModule.confirmSignature()">
              <span class="material-icons" style="font-size:16px;">check</span> Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// CLEANUP
// ============================================
export function cleanupJSA() {
  JsaState.unsubscribers.forEach(unsub => unsub());
  JsaState.unsubscribers = [];
  JsaState.reports = [];
  JsaState.crewSignatures = {};
  JsaState.taskRows = [];
  JsaState.selectedHazards.clear();
  JsaState.selectedPPE.clear();
  JsaState.formMode = 'tits';
  JsaState.templates = [];
  JsaState.firstSnap = { jobs: false, people: false, templates: false }; // TASK-10011
  JsaState.activeTemplate = null;
  JsaState.templateSnapshot = null;
  JsaState.templateChecks = new Set();
  currentDetailSignIns = null;
  JsaState.selectedTopics.clear();
  JsaState.emergencyFacility = null;
  JsaState.facilityResults = [];
  JsaState.userLocation = null;
  _jsaFacilitySearchSeq++; // TASK-9996 — a search still in flight from the last form must not paint this one
  JsaState.lsraChecks = {};
  JsaState.hazardCategories = new Set();
  JsaState.lessonsLearned = '';
  JsaState.xtoSignatures = { issuer: { name: '', signature: null }, receiver: { name: '', signature: null } };
}

// ============================================
// RENDER JSA LIST (History)
// ============================================
function renderJSAList() {
  const container = document.getElementById('jsa-list');
  if (!container) return;

  // Show only today's and yesterday's reports
  // TASK-9923 — company calendar; after ~7 PM Central the UTC date was tomorrow.
  const todayStr = window.RecordDate.today();
  const yesterdayStr = window.RecordDate.addDays(todayStr, -1);

  let reports = JsaState.reports.filter(r => {
    const d = r.date?.toDate?.() || new Date(r.date);
    const ds = window.RecordDate.ymd(d, window.RecordDate.zone());
    return ds === todayStr || ds === yesterdayStr;
  });

  // TASK-9581 COD r12 — a failed jsaReports read is never "No recent JSA reports".
  if (reports.length === 0 && JsaState.reportsFailed) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🦺</div>
        <div class="empty-state-title">JSA reports could not load</div>
        <div class="empty-state-text">Refresh to try again.</div>
      </div>
    `;
    return;
  }
  if (reports.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🦺</div>
        <div class="empty-state-title">No recent JSA reports</div>
        <div class="empty-state-text">No reports from today or yesterday. Complete a JSA before starting work.</div>
      </div>
    `;
    return;
  }

  // TASK-9371 S3 — a driver-PIN session gets Edit only on its OWN JSA (the
  // rules' driver update arm is own-JSA only); members see it on every card.
  const isPinDriver = window.AppState?.driverModePin === true;
  const viewerPersonId = getViewerPersonId();
  container.innerHTML = reports.map(report => {
    const date = report.date?.toDate?.() || new Date(report.date);
    const crewCount = report.formMode === 'template' ? (Number(report.signInCount) || 0) : (report.crewAcknowledgments?.length || 0);
    const hazardCount = jsaHazardCount(report); // TASK-9714
    const truckDisp = report.formMode !== 'xto' ? getReportTruckDisplay(report) : '';
    return `
      <div class="jsa-card" onclick="window.JSAModule.viewJSADetail('${report.id}')">
        <div class="jsa-card-status">
          <span class="material-icons" style="color: ${report.status === 'completed' ? 'var(--action-success)' : 'var(--action-warning)'}">
            ${report.status === 'completed' ? 'check_circle' : 'pending'}
          </span>
        </div>
        <div class="jsa-card-info">
          <div class="jsa-card-header">
            <span class="jsa-card-job">${report.formMode === 'xto' ? escapeHtml(report.unitArea || report.jsaNumber || 'Oilfield JSA') : escapeHtml(report.customerName || report.jobTitle || 'Untitled Job')}</span>
            <span class="pretrip-card-type" style="background:rgba(245,158,11,0.12);color:#d97706;">${report.formMode === 'xto' ? 'Oilfield JSA' : (report.formMode === 'template' ? escapeHtml(report.templateName || 'Template JSA') : 'JSA')}</span>
            <span class="pretrip-card-type">${report.status === 'completed' ? 'Completed' : 'Draft'}</span>
            ${_jsaRevisionBadgesHTML(report)}
          </div>
          <div class="pretrip-card-meta">
            <span>${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            ${report.formMode === 'xto'
              ? `<span>${escapeHtml(report.specificLocation || '')}</span>`
              : `<span>Supervisor: ${escapeHtml(report.supervisorName || 'N/A')}</span>`
            }
          </div>
          ${truckDisp ? `
          <div class="pretrip-card-meta" style="margin-top:2px;">
            <span style="color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:-2px;margin-right:2px;">local_shipping</span>${escapeHtml(truckDisp)}</span>
          </div>` : ''}
          <div class="pretrip-card-meta" style="margin-top:2px;">
            ${report.formMode !== 'xto' ? `
              <span>Crew: ${crewCount} ${crewCount === 1 ? 'member' : 'members'}</span>
              <span>Hazards: ${hazardCount}</span>
              ${report.jobTitle ? `<span>Job: ${escapeHtml(report.jobTitle)}</span>` : ''}
            ` : `<span>JSA: ${escapeHtml(report.jsaNumber || '')}</span>`}
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:2px;">
          <!-- TASK-313 — use purpose-built .btn-icon-xs (4px padding,
               18px icon, no min-height collision) instead of btn+btn-sm
               overridden with padding:4px which produced subtly mismatched
               heights between Print and Edit. Both buttons + chevron now
               share the same vertical baseline. -->
          <button class="btn-icon-xs" aria-label="Print this JSA" onclick="event.stopPropagation();window.JSAModule.printFromCard('${report.id}')" title="Print">
            <span class="material-icons">print</span>
          </button>
          ${(!isPinDriver || (!!viewerPersonId && report.submittedByPersonId === viewerPersonId)) ? `
          <button class="btn-icon-xs" aria-label="Edit this JSA" onclick="event.stopPropagation();window.JSAModule.editJSAReport('${report.id}')" title="Edit">
            <span class="material-icons">edit</span>
          </button>` : ''}
          <span class="material-icons" style="color:var(--text-secondary);font-size:20px;margin-left:2px;">chevron_right</span>
        </div>
      </div>
    `;
  }).join('');
}

// ============================================
// MY RECENT JSAS (TASK-361 — relocated from Job Board)
// Driver-side view of the current viewer's own submissions, capped at 10.
// Filtered client-side from the existing JsaState.reports subscription — no
// new Firestore listener required (Architect's "use what's already subscribed"
// note). Personal identity resolves PIN-driver -> SPA-member.personId in the
// same priority order as `getActiveJobBoardPersonId()` at jobboard.js:1513.
// Hidden entirely when the viewer has zero matches (don't push empty header).
// ============================================
// TASK-9714 — the hazard count a list card shows. A template sheet keeps its
// answers in templateAnswers (report.hazards is always empty there, so cards
// said "Hazards: 0"): count the ticked boxes in its hazard checkbox grid(s) —
// a checkbox-grid section whose id or title says "hazard".
function jsaHazardCount(report) {
  if (!report) return 0;
  if (report.formMode !== 'template') return Array.isArray(report.hazards) ? report.hazards.length : 0;
  const answers = report.templateAnswers || {};
  let n = 0;
  (report.templateSnapshot?.sections || []).forEach(sec => {
    if (!sec || sec.layout !== 'checkbox-grid' || !/hazard/i.test(`${sec.id || ''} ${sec.title || ''}`)) return;
    (sec.fields || []).forEach(f => { if (f && f.type === 'checkbox' && answers[f.id] === true) n++; });
  });
  return n;
}

function getViewerPersonId() {
  return window.AppState?.driverPersonId
    || window.AppState?.memberAccess?.personId
    || null;
}

function renderMyJsaSection() {
  const wrapper = document.getElementById('jsa-sec-mine-wrapper');
  const listEl = document.getElementById('jsa-my-recent-list');
  const label = document.getElementById('jsa-sec-mine-label');
  if (!wrapper || !listEl) return;

  const personId = getViewerPersonId();
  if (!personId) {
    wrapper.style.display = 'none';
    return;
  }

  const mine = (JsaState.reports || [])
    .filter(r => r.submittedByPersonId === personId)
    .slice(0, 10);

  if (mine.length === 0) {
    wrapper.style.display = 'none';
    return;
  }

  wrapper.style.display = '';
  if (label) label.textContent = `My Recent JSAs (${mine.length})`;
  listEl.innerHTML = mine.map(jsa => renderJSAActionCard(jsa, {
    showEmail: true,
    showPdf: true,
    showSignatures: true
  })).join('');
}

// Shared card renderer used by "My Recent JSAs" (driver-side actions) and
// available for other surfaces that need a compact JSA card with action
// buttons. The tenant-wide "Recent JSAs (today + yesterday)" list still uses
// its bespoke renderer at renderJSAList() above — it has a different layout
// (Print/Edit on each card, full meta line including supervisor/crew counts).
function renderJSAActionCard(jsa, options) {
  const opts = options || {};
  const isOilfield = jsa.formMode === 'xto';
  const dateObj = jsa.date?.toDate?.() || (jsa.date ? new Date(jsa.date) : null);
  const dateStr = dateObj ? dateObj.toLocaleDateString() : '';
  const label = isOilfield
    ? (jsa.jsaNumber || `JSA ${String(jsa.id || '').slice(0, 6)}`)
    : (jsa.customerName || jsa.jobTitle || `JSA ${String(jsa.id || '').slice(0, 6)}`);
  const loc = isOilfield
    ? (jsa.specificLocation || jsa.unitArea || '')
    : (jsa.locationArea || '');
  const isTemplate = jsa.formMode === 'template'; // TASK-4839
  const badge = isOilfield ? 'Oilfield' : (isTemplate ? escapeHtml(jsa.templateName || 'Template') : 'Standard');
  const badgeBg = isOilfield ? '#FEF3C7' : '#DBEAFE';
  const badgeFg = isOilfield ? '#92400E' : '#1E40AF';

  const emailBtn = opts.showEmail ? `
    <button class="btn btn-secondary" style="flex:1;padding:8px;font-size:13px;" onclick="event.stopPropagation();window.JSAModule.openMyJsaEmailOverlay('${jsa.id}')">
      <span class="material-icons" style="font-size:16px;vertical-align:middle;">email</span>
      Email
    </button>` : '';
  const pdfBtn = opts.showPdf ? `
    <button class="btn btn-secondary" style="flex:1;padding:8px;font-size:13px;" onclick="event.stopPropagation();window.JSAModule.downloadMyJsaPdf('${jsa.id}')">
      <span class="material-icons" style="font-size:16px;vertical-align:middle;">picture_as_pdf</span>
      PDF
    </button>` : '';
  const sigBtn = opts.showSignatures ? `
    <button class="btn btn-primary" style="width:100%;margin-top:6px;padding:10px;font-size:13px;font-weight:600;" onclick="event.stopPropagation();window.JSAModule.getMyJsaSignatureLink('${jsa.id}')">
      <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">qr_code_2</span>
      Get Signatures
    </button>` : '';

  return `
    <div style="background:#fff;border:1px solid #E2E8F0;border-radius:10px;padding:12px 14px;margin-bottom:10px;box-shadow:0 1px 2px rgba(15,23,42,0.04);">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(label)}</div>
          <div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">
            ${escapeHtml(dateStr)}${loc ? ' · ' + escapeHtml(loc) : ''}
          </div>
        </div>
        <span style="display:flex;gap:4px;flex-wrap:wrap;justify-content:flex-end;">
          <span style="background:${badgeBg};color:${badgeFg};font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;">${badge}</span>
          ${(Number(jsa.revisionCount) || 0) > 0 ? `<span style="background:#FEF3C7;color:#92400E;font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;">Revised ×${Number(jsa.revisionCount)}</span>` : ''}
          ${jsa.signaturesStale === true ? `<span style="background:#FEF2F2;color:#991B1B;font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;">Re-sign needed</span>` : ''}
        </span>
      </div>
      ${(emailBtn || pdfBtn) ? `<div style="display:flex;gap:8px;margin-top:10px;">${emailBtn}${pdfBtn}</div>` : ''}
      ${sigBtn}
    </div>
  `;
}

// ============================================
// MY RECENT JSAS — driver-side action handlers (TASK-361)
// Mirror the jobboard.js handlers (deleted in this task) but with proper
// driverToken threading on emailJsaReport and downloadJsaPdf callables so
// driver-PIN sessions don't hit silent callable rejection (Independent's
// critical mitigation from the COD review).
// ============================================
// TASK-9504 — a driver no longer types an address. The server sends ONLY to
// the company's JSA recipients ("office") or the driver's own roster email
// ("self"); any recipientEmail from a driver session is ignored server-side.
function openMyJsaEmailOverlay(jsaId) {
  const jsa = (JsaState.reports || []).find(j => j.id === jsaId)
    || (currentDetailJsaId === jsaId ? currentDetailReport : null);
  if (!jsa) return;
  const existing = document.getElementById('driver-jsa-email-overlay');
  if (existing) existing.remove();
  const isOilfield = jsa.formMode === 'xto';
  const title = isOilfield ? 'Email Oilfield JSA' : 'Email JSA';
  const safeId = window.escJsAttr ? window.escJsAttr(jsaId) : String(jsaId).replace(/[^a-zA-Z0-9_-]/g, '');
  const overlay = document.createElement('div');
  overlay.id = 'driver-jsa-email-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  // TASK-9504 (COD r1) — "My Recent JSAs" also renders for a signed-in MEMBER
  // with a linked personId. A member keeps the free-text recipient (the server
  // requires recipientEmail for members); only a PIN session gets office / me.
  if (window.AppState?.driverModePin !== true) {
    const prefill = !isOilfield ? (jsa.customerEmail || '') : '';
    overlay.innerHTML = `
    <div style="background:#fff;border-radius:14px;max-width:420px;width:100%;padding:20px;box-shadow:0 10px 40px rgba(0,0,0,0.25);">
      <h3 style="margin:0 0 8px;font-size:18px;color:var(--text-primary);">${title}</h3>
      <p style="margin:0 0 12px;font-size:13px;color:var(--text-secondary);">Enter the recipient's email address. A PDF of the report will be attached.</p>
      <input id="driver-jsa-email-input" type="email" inputmode="email" autocomplete="email" placeholder="recipient@example.com" value="${String(prefill).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')}" style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:8px;font-size:14px;box-sizing:border-box;">
      <div style="display:flex;gap:8px;margin-top:14px;">
        <button class="btn btn-secondary" style="flex:1;" onclick="document.getElementById('driver-jsa-email-overlay')?.remove()">Cancel</button>
        <button class="btn btn-primary" id="driver-jsa-email-send" style="flex:1;" onclick="window.JSAModule.sendMyJsaEmailTo('${safeId}')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">send</span>
          Send
        </button>
      </div>
    </div>`;
    document.body.appendChild(overlay);
    setTimeout(() => document.getElementById('driver-jsa-email-input')?.focus(), 50);
    return;
  }
  overlay.innerHTML = `
    <div style="background:#fff;border-radius:14px;max-width:420px;width:100%;padding:20px;box-shadow:0 10px 40px rgba(0,0,0,0.25);">
      <h3 style="margin:0 0 8px;font-size:18px;color:var(--text-primary);">${title}</h3>
      <p style="margin:0 0 14px;font-size:14px;color:var(--text-secondary);">A PDF of the report will be attached.</p>
      <div style="display:flex;flex-direction:column;gap:8px;">
        <button class="btn btn-primary" id="driver-jsa-email-office" style="min-height:44px;" onclick="window.JSAModule.sendMyJsaEmail('${safeId}', 'office')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">business</span>
          Email to office
        </button>
        <button class="btn btn-secondary" id="driver-jsa-email-self" style="min-height:44px;" onclick="window.JSAModule.sendMyJsaEmail('${safeId}', 'self')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">person</span>
          Email to me
        </button>
        <button class="btn btn-secondary" style="min-height:44px;" onclick="document.getElementById('driver-jsa-email-overlay')?.remove()">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
}

// Member (non-PIN) send from "My Recent JSAs": free-text recipient, unchanged
// server contract (members must name recipientEmail).
async function sendMyJsaEmailTo(jsaId) {
  const input = document.getElementById('driver-jsa-email-input');
  const email = (input?.value || '').trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    window.showToast?.('Enter a valid email address', 'error');
    return;
  }
  const btn = document.getElementById('driver-jsa-email-send');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Sending...'; }
  try {
    const call = httpsCallable(functions, 'emailJsaReport');
    const res = await call({ tenantId: window.AppState?.tenant, jsaId, recipientEmail: email });
    document.getElementById('driver-jsa-email-overlay')?.remove();
    window.showToast?.(res?.data?.mode === 'office' ? 'JSA emailed to the office' : `JSA sent to ${email}`, 'success');
  } catch (err) {
    console.error('[JSA] sendMyJsaEmailTo error:', err);
    window.showToast?.(err?.message ? `Failed to send: ${err.message}` : 'Failed to send. Try again.', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">send</span> Send'; }
  }
}

async function sendMyJsaEmail(jsaId, mode) {
  const recipientMode = mode === 'self' ? 'self' : 'office';
  const btnIds = ['driver-jsa-email-office', 'driver-jsa-email-self'];
  const btn = document.getElementById(recipientMode === 'self' ? 'driver-jsa-email-self' : 'driver-jsa-email-office');
  const btnHtml = btn ? btn.innerHTML : '';
  btnIds.forEach(id => { const b = document.getElementById(id); if (b) b.disabled = true; });
  if (btn) btn.innerHTML = '<span class="btn-spinner"></span> Sending...';
  try {
    const call = httpsCallable(functions, 'emailJsaReport');
    await call({
      tenantId: window.AppState?.tenant,
      jsaId,
      recipientMode,
      driverToken: window.AppState?.driverAccessToken || '',
    });
    document.getElementById('driver-jsa-email-overlay')?.remove();
    window.showToast?.(recipientMode === 'self' ? 'JSA emailed to you' : 'JSA emailed to the office', 'success');
  } catch (err) {
    console.error('[JSA] sendMyJsaEmail error:', err);
    // The server's refusal text is written for the driver (no recipients set
    // up, no email on file, daily cap, session expired) — show it as-is.
    const code = err?.code || '';
    const serverMsg = /failed-precondition|resource-exhausted|unauthenticated|permission-denied/.test(code) ? err?.message : '';
    window.showToast?.(serverMsg || 'Failed to send. Try again.', 'error');
    btnIds.forEach(id => { const b = document.getElementById(id); if (b) b.disabled = false; });
    if (btn) btn.innerHTML = btnHtml;
  }
}

async function downloadMyJsaPdf(jsaId) {
  window.showToast?.('Generating PDF...', 'info');
  try {
    const call = httpsCallable(functions, 'downloadJsaPdf');
    const res = await call({
      tenantId: window.AppState?.tenant,
      jsaId,
      driverToken: window.AppState?.driverAccessToken || '',
    });
    const url = res?.data?.url;
    if (url) {
      window.open(url, '_blank');
    } else {
      window.showToast?.('Could not generate PDF', 'error');
    }
  } catch (err) {
    console.error('[JSA] downloadMyJsaPdf error:', err);
    // TASK-9504 — "Session expired — re-enter PIN" etc. are written for the driver.
    const serverMsg = /unauthenticated|permission-denied/.test(err?.code || '') ? err?.message : '';
    window.showToast?.(serverMsg || 'Failed to generate PDF', 'error');
  }
}

// Driver-side: generate / show a multi-party signing link for one of the
// driver's own JSAs. Ported verbatim from jobboard.js (TASK-361).
// TASK-9714 — `generateJsaSignatureToken` now accepts a driver-PIN session for
// a JSA it filed (tenant from the session, author checked server-side), and
// returns the JSA's live link instead of replacing it.
async function getMyJsaSignatureLink(jsaId) {
  const tenantId = window.AppState?.tenant;
  if (!tenantId || !jsaId) return;
  let overlay = document.getElementById('jb-jsa-sig-overlay');
  if (overlay) overlay.remove();
  overlay = document.createElement('div');
  overlay.id = 'jb-jsa-sig-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  overlay.innerHTML = `
    <div id="jb-jsa-sig-card" style="background:#fff;border-radius:14px;max-width:480px;width:100%;padding:20px;box-shadow:0 10px 40px rgba(0,0,0,0.25);">
      <div style="text-align:center;padding:24px 20px;color:var(--text-secondary);">
        <span class="material-icons" style="font-size:36px;color:var(--brand-blue);">hourglass_empty</span>
        <div style="margin-top:8px;">Generating signing link…</div>
      </div>
    </div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);

  try {
    const fn = httpsCallable(functions, 'generateJsaSignatureToken');
    const res = await fn({ tenantId, jsaId });
    const r = res.data || {};
    if (!r.ok || !r.url) throw new Error('Could not generate link');
    const expiresStr = r.expiresAt ? new Date(r.expiresAt).toLocaleString('en-US', { month: 'short', day: 'numeric' }) : 'in 7 days';
    const card = document.getElementById('jb-jsa-sig-card');
    if (!card) return;
    const escAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    card.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:12px;">
        <h3 style="margin:0;font-size:17px;color:var(--text-primary);">JSA Signing Link</h3>
        <button type="button" onclick="document.getElementById('jb-jsa-sig-overlay').remove()" style="background:transparent;border:0;cursor:pointer;color:var(--text-secondary);">
          <span class="material-icons">close</span>
        </button>
      </div>
      <p style="margin:0 0 12px;font-size:13px;color:var(--text-secondary);">
        Share this link with anyone on site — they sign from their phone. Up to <strong>${r.signatureCap}</strong> signatures, expires <strong>${expiresStr}</strong>.
      </p>
      <div style="display:flex;gap:6px;margin-bottom:10px;">
        <input id="jb-jsa-sig-input" readonly value="${escAttr(r.url)}" style="flex:1;padding:10px 12px;border:1px solid #CBD5E1;border-radius:8px;font-size:12px;background:#F8FAFC;font-family:monospace;">
        <button type="button" id="jb-jsa-sig-copy-btn" style="background:var(--brand-blue);color:#fff;border:0;border-radius:8px;padding:10px 14px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">content_copy</span> Copy
        </button>
      </div>
      <div style="display:flex;gap:8px;">
        <button type="button" onclick="window.open('${window.escJsAttr(r.url)}','_blank','noopener')" class="btn btn-secondary" style="flex:1;padding:10px;font-size:13px;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">open_in_new</span> Open
        </button>
        <button type="button" id="jb-jsa-sig-qr-btn" class="btn btn-secondary" style="flex:1;padding:10px;font-size:13px;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">qr_code_2</span> Show QR
        </button>
      </div>
      <div id="jb-jsa-sig-qr-target" style="margin-top:12px;"></div>
    `;
    document.getElementById('jb-jsa-sig-copy-btn').addEventListener('click', () => {
      const inp = document.getElementById('jb-jsa-sig-input');
      navigator.clipboard.writeText(inp.value).then(() => {
        const btn = document.getElementById('jb-jsa-sig-copy-btn');
        btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">check</span> Copied';
        setTimeout(() => { btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">content_copy</span> Copy'; }, 2000);
      }).catch(() => { inp.select(); });
    });
    document.getElementById('jb-jsa-sig-qr-btn').addEventListener('click', () => {
      if (window.JSAModule?._renderQrInto) {
        window.JSAModule._renderQrInto('jb-jsa-sig-qr-target', r.url);
      } else {
        document.getElementById('jb-jsa-sig-qr-target').innerHTML =
          '<div style="text-align:center;padding:10px;color:var(--text-secondary);font-size:13px;">QR helper not available.</div>';
      }
    });
  } catch (e) {
    console.error('[JSA] sig link error:', e);
    const card = document.getElementById('jb-jsa-sig-card');
    if (card) {
      card.innerHTML = `<div style="text-align:center;padding:20px;color:var(--action-danger);">
        <span class="material-icons" style="font-size:36px;">error_outline</span>
        <div style="margin-top:8px;font-size:14px;">${(e?.message || 'Could not generate link').replace(/[<>&]/g,'')}</div>
        <button type="button" onclick="document.getElementById('jb-jsa-sig-overlay').remove()" class="btn btn-secondary" style="margin-top:12px;">Close</button>
      </div>`;
    }
  }
}

// ============================================
// FORM BODY RENDERING (TITS / XTO)
// ============================================

function getTITSFormHTML() {
  return `
    <!-- Section 1: Job & Header -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-job')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>1. Job & Header</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-job">
        <div class="form-row">
          <div class="form-group">
            <label for="jsa-customer">Customer</label>
            <select id="jsa-customer" onchange="window.JSAModule.onCustomerSelect()">
              <option value="">— None —</option>
            </select>
            <small id="jsa-customer-hint" style="display:none;color:var(--text-secondary);font-size:0.75rem;margin-top:2px;">No customers yet — add one in <a href="#" onclick="event.preventDefault();window.JSAModule.goToDispatchAddJob()" style="color:var(--brand-blue);text-decoration:underline;">Dispatch</a> or <a href="#" onclick="event.preventDefault();window.JSAModule.goToCRM()" style="color:var(--brand-blue);text-decoration:underline;">CRM</a></small>
          </div>
          <div class="form-group">
            <label for="jsa-date">Date *</label>
            <input type="date" id="jsa-date" required>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="jsa-job">Job</label>
            <select id="jsa-job" onchange="window.JSAModule.onJobSelect()">
              <option value="">Select job (optional)</option>
            </select>
          </div>
          <div class="form-group">
            <label for="jsa-supervisor">Supervisor *</label>
            <select id="jsa-supervisor" required>
              <option value="">Select supervisor</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="jsa-person-responsible">Person Responsible</label>
            <input type="text" id="jsa-person-responsible" placeholder="Name of responsible person">
          </div>
          <div class="form-group">
            <label for="jsa-total-hours">Total Hours</label>
            <input type="number" id="jsa-total-hours" value="8" min="1" max="24" step="0.5">
          </div>
        </div>
        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" id="jsa-permit-required">
            <span>Permit Required</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Section 2: Hazards -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-hazards')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>2. Hazard Identification</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-hazards">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">Select all hazards present at this job site:</p>
        <div class="jsa-chip-grid" id="jsa-hazard-grid">
          ${JSA_HAZARDS.map(h => `
            <button type="button" class="jsa-hazard-chip" data-id="${h.id}" onclick="window.JSAModule.toggleHazard('${h.id}')">
              <span class="material-icons">${h.icon}</span>
              <span>${h.label}</span>
            </button>
          `).join('')}
        </div>
        <div class="form-group hidden" id="jsa-hazard-other-group" style="margin-top:8px;">
          <label for="jsa-hazard-other">Other Hazard</label>
          <input type="text" id="jsa-hazard-other" placeholder="Describe other hazard">
        </div>
      </div>
    </div>

    <!-- Section 3: Task / Hazard / Control Table -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-tasks')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>3. Task / Hazard / Control Measures</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-tasks">
        ${JsaState.formMode === 'xto' ? `
          <!-- XTO back-page hazard-type categories (oilfield only) -->
          <div style="margin-bottom:14px;padding:12px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:8px;">
            <p style="font-size:0.85rem;font-weight:600;color:var(--text-primary);margin:0 0 8px;">Hazard Types Present on This Job</p>
            <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 10px;">Toggle every hazard category that applies — even if it appears in only one task step:</p>
            <div class="jsa-chip-grid" id="jsa-xto-hazcat-grid">
              ${XTO_HAZARD_CATEGORIES.map(h => `
                <button type="button" class="jsa-hazard-chip jsa-xto-hazcat-chip" data-id="${h.id}" onclick="window.JSAModule.toggleHazardCategory('${h.id}')">
                  <span class="material-icons">${h.icon}</span>
                  <span>${h.label}</span>
                </button>
              `).join('')}
            </div>
          </div>
          <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">For each <strong>higher-potential-consequence task step</strong>, capture the hazards/consequences, the safeguards, and whether a Hold Point applies:</p>
        ` : `
          <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">Describe each task, its hazards, and control measures:</p>
        `}
        <div id="jsa-task-rows"></div>
        <button type="button" class="btn btn-sm btn-secondary" onclick="window.JSAModule.addTaskRow()" style="margin-top:8px;">
          <span class="material-icons" style="font-size:16px;">add</span> Add Task
        </button>
        ${JsaState.formMode === 'xto' ? `
          <!-- XTO back-page confirm-safeguards banner + Lessons Learned -->
          <div style="margin-top:16px;padding:12px 14px;background:#FEE2E2;border:1px solid #FCA5A5;border-left:4px solid #DC2626;border-radius:6px;color:#7F1D1D;font-size:0.85rem;font-weight:600;line-height:1.5;">
            Confirm these safeguards are in place and effective prior to starting work using this JSA.<br>
            During the task, if safeguards are no longer in place or effective. <span style="text-decoration:underline;">STOP Work</span>.
          </div>
          <div class="form-group" style="margin-top:14px;">
            <label for="jsa-xto-lessons-learned" style="font-weight:600;font-size:0.9rem;">Lessons Learned</label>
            <p style="font-size:0.78rem;color:var(--text-secondary);margin:2px 0 6px;">What worked well? What improvements can be made? Discussion leader: provide feedback to update planning package and inform the JSA owner if needed.</p>
            <textarea id="jsa-xto-lessons-learned" style="font-family:inherit;" rows="3" placeholder="What worked, what could improve, feedback for next time..." onchange="window.JSAModule.updateLessonsLearned(this.value)">${escapeHtml(JsaState.lessonsLearned || '')}</textarea>
          </div>
        ` : ''}
      </div>
    </div>

    <!-- Section 4: PPE Equipment -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-ppe')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>4. PPE Equipment</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-ppe">
        <div class="form-group" style="margin-bottom:12px;">
          <label class="checkbox-label">
            <input type="checkbox" id="jsa-ppe-required" checked onchange="window.JSAModule.togglePPERequired()">
            <span>PPE Required</span>
          </label>
        </div>
        <div id="jsa-ppe-section">
          <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">Select required PPE:</p>
          <div class="jsa-chip-grid" id="jsa-ppe-grid">
            ${JSA_PPE.map(p => `
              <button type="button" class="jsa-ppe-chip" data-id="${p.id}" onclick="window.JSAModule.togglePPE('${p.id}')">
                <span class="material-icons">${p.icon}</span>
                <span>${p.label}</span>
              </button>
            `).join('')}
          </div>
          <div class="form-group hidden" id="jsa-ppe-other-group" style="margin-top:8px;">
            <label for="jsa-ppe-other">Other PPE</label>
            <input type="text" id="jsa-ppe-other" placeholder="Describe other PPE">
          </div>
        </div>
      </div>
    </div>

    <!-- Section 5: Tailgate Safety Meeting -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-tailgate')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>5. Tailgate Safety Meeting</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-tailgate">
        <label style="font-weight:600;font-size:0.9rem;margin-bottom:4px;display:block;">Tailgate Topics</label>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">Select all topics discussed:</p>
        <div class="jsa-chip-grid" id="jsa-topic-grid">
          ${LIFE_SAVING_RULES.map(r => `
            <button type="button" class="jsa-topic-chip" data-id="${slugify(r)}"
                    onclick="window.JSAModule.toggleTopic('${slugify(r)}')">
              <span>${r}</span>
            </button>
          `).join('')}
        </div>
        <div class="form-group" style="margin-top:8px;">
          <label for="jsa-tailgate-items">Tailgate Safety Meeting Topics</label>
          <textarea id="jsa-tailgate-items" style="font-family:inherit;" rows="3" placeholder="Summarize tailgate meeting discussion..."></textarea>
        </div>
      </div>
    </div>

    <!-- Section 6: Work Plan -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-workplan')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>6. Work Plan</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-workplan">
        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" id="jsa-one-call">
            <span>One-Call / 811 Completed</span>
          </label>
        </div>
        <div class="form-group">
          <label for="jsa-one-call-number">One Call #</label>
          <input type="text" id="jsa-one-call-number" placeholder="e.g. 2026021900123">
        </div>
        <div class="form-group">
          <label for="jsa-work-layout">Work Layout Plan</label>
          <textarea id="jsa-work-layout" style="font-family:inherit;" rows="3" placeholder="Describe equipment staging, work area layout..."></textarea>
        </div>
        <div class="form-group">
          <label for="jsa-muster-point">Muster Point</label>
          <input type="text" id="jsa-muster-point" placeholder="e.g. Gate entrance, 200ft from well">
        </div>
      </div>
    </div>

    <!-- Section 6.5: Emergency Medical Facility -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-emergency')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>Emergency Medical Facility</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-emergency">
        <div class="jsa-emergency-location">
          <button type="button" class="btn btn-sm btn-secondary" onclick="window.JSAModule.getMyLocation()">
            <span class="material-icons" style="font-size:16px;">my_location</span> My Location
          </button>
          <span id="jsa-location-status" style="font-size:0.85rem;color:var(--text-secondary);margin-left:8px;"></span>
        </div>
        <div class="form-group" style="margin-top:8px;">
          <label for="jsa-facility-search">Search Nearby Facilities</label>
          <div style="display:flex;gap:8px;">
            <input type="search" id="jsa-facility-search" placeholder="Search hospitals, clinics..." autocomplete="off" style="flex:1;">
            <button type="button" class="btn btn-sm btn-primary" onclick="window.JSAModule.searchFacilities()">
              <span class="material-icons" style="font-size:16px;">search</span>
            </button>
          </div>
        </div>
        <div id="jsa-facility-results" style="margin-top:8px;"></div>
        <div id="jsa-selected-facility" style="margin-top:8px;"></div>
        <div class="form-group" style="margin-top:8px;">
          <label for="jsa-facility-manual">Or enter manually</label>
          <input type="text" id="jsa-facility-manual" placeholder="Hospital name, address, phone">
        </div>
      </div>
    </div>

    <!-- Section 7: Crew Acknowledgment -->
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-crew')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>7. Crew Acknowledgment</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-crew">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">Each crew member must sign to acknowledge this JSA:</p>
        <div class="jsa-crew-list" id="jsa-crew-list"></div>
        <div style="margin-top:8px;display:flex;gap:8px;align-items:center;">
          <input type="text" id="jsa-add-crew-name" placeholder="Add crew member name" style="flex:1;">
          <button type="button" class="btn btn-sm btn-secondary" onclick="window.JSAModule.addManualCrew()">
            <span class="material-icons" style="font-size:16px;">person_add</span> Add
          </button>
        </div>
      </div>
    </div>
  `;
}

// TASK-9923 — a JSA's day is stored as NOON on the COMPANY calendar, the same
// zone the list / JSA number / PDF read it back in. Was device-local noon, which
// a phone 12h+ from the company zone put on the neighbouring company day.
function _jsaDayMs(dateVal) {
  const ms = window.RecordDate.noonMs(dateVal);
  return Number.isFinite(ms) ? ms : new Date(dateVal + 'T12:00:00').getTime();
}

function getXTOFormHTML() {
  const todayISO = window.RecordDate.today(); // TASK-9923
  return `
    <!-- XTO Header -->
    <div class="xto-header" style="padding:0 4px;">
      <div class="form-row">
        <div class="form-group">
          <label>JSA No.</label>
          <input type="text" id="xto-jsa-number" placeholder="Enter JSA No. (optional)">
        </div>
        <div class="form-group">
          <label>Date</label>
          <input type="date" id="xto-date" value="${todayISO}">
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Unit / Area</label>
          <input type="text" id="xto-unit-area" placeholder="Unit or area name">
        </div>
        <div class="form-group">
          <label>Specific Location</label>
          <input type="text" id="xto-specific-location" placeholder="Specific location">
        </div>
      </div>
    </div>

    <!-- JSA Issuer / Receiver Signatures -->
    <div class="xto-signatures">
      <div class="xto-sig-card" id="xto-sig-issuer" onclick="window.JSAModule.openXTOSignature('issuer')">
        <span class="material-icons">draw</span>
        <span style="font-weight:600;">JSA Issuer</span>
        <span id="xto-issuer-status" class="xto-sig-status">Tap to sign</span>
      </div>
      <div class="xto-sig-card" id="xto-sig-receiver" onclick="window.JSAModule.openXTOSignature('receiver')">
        <span class="material-icons">draw</span>
        <span style="font-weight:600;">JSA Receiver</span>
        <span id="xto-receiver-status" class="xto-sig-status">Tap to sign</span>
      </div>
    </div>

    <!-- 12 LSRA Cards (filtered by tenant settings.lsraRules; missing/true = enabled) -->
    <div class="xto-lsra-section">
      <h4 style="margin:0 0 2px;color:var(--text-primary);font-size:1rem;">Life Saving Rule Assessment</h4>
      <div style="font-size:0.75rem;font-style:italic;color:var(--text-secondary);margin:0 0 8px;">Discuss / Verify / Confirm the items below for each applicable rule.</div>
      ${(() => {
        const enabledCards = getEnabledLsraCards();
        if (enabledCards.length === 0) {
          return `<div style="padding:12px;border:1px dashed var(--border);border-radius:var(--radius-sm);font-size:0.85rem;color:var(--text-secondary);text-align:center;">All Life-Saving Rules are disabled in Settings → Inspection &amp; Compliance. Enable at least one to use this form.</div>`;
        }
        return `<div class="xto-lsra-grid">
        ${enabledCards.map(card => `
          <div class="xto-lsra-card" id="lsra-card-${card.key}">
            <div class="xto-lsra-card-header">
              <span class="material-icons">${card.icon}</span>
              <div style="flex:1;min-width:0;">
                <div>${card.title}</div>
                ${card.subtitle ? `<div style="font-weight:400;font-size:0.75rem;color:var(--text-secondary);line-height:1.2;margin-top:2px;">${card.subtitle}</div>` : ''}
              </div>
              <label class="checkbox-label" style="margin-left:auto;font-weight:400;font-size:0.8rem;white-space:nowrap;" onclick="event.stopPropagation();">
                <input type="checkbox" data-lsra="${card.key}" data-field="applicable"
                       onchange="window.JSAModule.toggleLSRA('${card.key}', 'applicable', this.checked)">
                <span>Applicable</span>
              </label>
            </div>
            <div class="xto-lsra-card-body" id="lsra-body-${card.key}">
              ${card.checklist.map((item, i) => `
                <div class="xto-lsra-item">
                  <label class="checkbox-label">
                    <input type="checkbox" data-lsra="${card.key}" data-item="${i}"
                           onchange="window.JSAModule.toggleLSRAItem('${card.key}', ${i}, this.checked)">
                    <span>${item.text}</span>
                  </label>
                  ${item.subitems ? `
                    <div class="xto-lsra-subitems">
                      ${item.subitems.map(sub => `<div class="xto-lsra-subitem">${sub}</div>`).join('')}
                    </div>
                  ` : ''}
                </div>
              `).join('')}
            </div>
          </div>
        `).join('')}
      </div>`;
      })()}
    </div>

    <!-- Job Description -->
    <div class="form-group" style="margin-top:12px;padding:0 4px;">
      <label for="xto-job-description">Job Description</label>
      <textarea id="xto-job-description" style="font-family:inherit;" rows="4" placeholder="Describe the job scope..."></textarea>
    </div>
  `;
}

// ============================================
// TASK-4839 — PER-TENANT JSA TEMPLATES (formMode 'template')
// ============================================
// A tenant with settings.enableJsaTemplates === true (default OFF, absent ===
// disabled, same pattern as isPermitTrackingEnabled in dispatch.js) and >= 1
// active tenants/{tid}/jsaTemplates doc gets one extra mode button per
// template. The form, detail view, browser print (and the server PDF + signing
// portal) all render from the template's sections — labels come from the
// report's FROZEN templateSnapshot, never from the live template, so a later
// template edit cannot alter what a signed sheet displays.
//
// Shape (see scripts/lib/jsa-template-sunbelt-gp-r2.cjs for the seeded one):
//   { name, version, active, seededFrom, sections:[{ id, title,
//     layout:'fields'|'checkbox-grid'|'signin', slots?, fields:[{ id, label,
//     type:text|longtext|number|date|select|checkbox|person|vehicle|places,
//     options?, group?, required?, bind?, signerVisible? }] }] }
//
// 🔴 Sign-in rows are NEVER stored inline on the report. A 48-slot sheet at the
// measured 12,227 B mean pad is ~587 KB before the snapshot; heavier pens cross
// the 1 MiB doc cap and addDoc rejects the whole sheet (COD Independent ❌).
// They go to jsaReports/{id}/signIns/{n}; the parent carries only signInCount +
// signInNames (bumped server-side by onJsaSignInCreate).
//
// No tenantId compares anywhere in this block — the flag is the switch.
function isJsaTemplatesEnabled() {
  return window.AppState?.settings?.enableJsaTemplates === true;
}

function getActiveJsaTemplates() {
  if (!isJsaTemplatesEnabled()) return [];
  return (JsaState.templates || []).filter(t =>
    t && t.active !== false && Array.isArray(t.sections) && t.sections.length > 0);
}

// One renderer for the mode toggle so the three call sites (new / edit Standard /
// edit Oilfield) cannot drift. `templates` is [] on the edit paths: a filed
// report never changes mode, and the template buttons only exist for a NEW sheet.
function getModeToggleHTML(activeMode, activeTemplateId, templates) {
  // TASK-9511 — a long template name ("Sunbelt Rentals GP Job Safety Analysis")
  // squeezed into a third of 390px wrapped to 3 lines. Each template button now
  // takes its own full-width row on one line (ellipsis + title for the rest).
  const tplButtons = (templates || []).map(t => `
        <button type="button" class="jsa-mode-btn${activeMode === 'template' && activeTemplateId === t.id ? ' active' : ''}" data-mode="template" data-template-id="${escapeHtml(t.id)}" title="${escapeHtml(t.name || 'Template')}" style="flex:1 1 100%;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" onclick="window.JSAModule.switchFormMode('template', '${window.escJsAttr(t.id)}', true)">
          ${escapeHtml(t.name || 'Template')}
        </button>`).join('');
  return `
      <div class="jsa-mode-toggle"${tplButtons ? ' style="flex-wrap:wrap;row-gap:4px;"' : ''}>
        <button type="button" class="jsa-mode-btn${activeMode === 'tits' ? ' active' : ''}" data-mode="tits" onclick="window.JSAModule.switchFormMode('tits', null, true)">
          Standard
        </button>
        <button type="button" class="jsa-mode-btn${activeMode === 'xto' ? ' active' : ''}" data-mode="xto" onclick="window.JSAModule.switchFormMode('xto', null, true)">
          Oilfield
        </button>${tplButtons}
      </div>
    `;
}

// Re-render the mode toggle of an OPEN new-JSA form when the templates
// listener delivers (first snapshot or a live edit). The form is shown right
// away with Standard/Oilfield; template buttons are added when they arrive, so
// a slow or failed read never blocks the form. Edit forms (fixed mode / badge)
// and a closed modal are left alone; only the toggle is touched, never the body.
function refreshNewJsaTemplateButtons() {
  if (JsaState.editingJsaId) return;
  const modal = document.getElementById('modal-jsa');
  if (!modal || modal.classList.contains('hidden')) return;
  const toggleContainer = document.getElementById('jsa-mode-toggle-container');
  if (!toggleContainer || !toggleContainer.querySelector('.jsa-mode-toggle')) return;
  const mode = JsaState.formMode || 'tits';
  const activeTplId = mode === 'template' ? (JsaState.activeTemplate?.id || null) : null;
  const templates = getActiveJsaTemplates();
  // A sheet already open on a template that was just deactivated keeps its
  // (active) button — the body is still that frozen snapshot and will save as it.
  if (activeTplId && JsaState.activeTemplate && !templates.some(t => t.id === activeTplId)) {
    templates.push(JsaState.activeTemplate);
  }
  toggleContainer.innerHTML = getModeToggleHTML(mode, activeTplId, templates);
  // TASK-9511 — the templates often land ~200ms after the form opened on Standard.
  maybeDefaultToSoleTemplate();
}

// TASK-9511 — a tenant running exactly ONE active template wants that sheet, not
// Standard. Default a NEW form to it — only while the user has neither picked a
// mode nor typed anything (the template listener can land after the form is up,
// and switching mode resets the body). Never on an edit; never with 0 or 2+.
function soleActiveTemplate(templates) {
  return Array.isArray(templates) && templates.length === 1 ? templates[0] : null;
}
function maybeDefaultToSoleTemplate() {
  if (JsaState.editingJsaId || JsaState.modeChosenByUser || JsaState.newFormTouched) return false;
  if (JsaState.formMode !== 'tits') return false;
  const modal = document.getElementById('modal-jsa');
  if (!modal || modal.classList.contains('hidden')) return false;
  const only = soleActiveTemplate(getActiveJsaTemplates());
  if (!only) return false;
  switchFormMode('template', only.id);
  return true;
}

// The #modal-jsa form is shared by new + edit; the header must say which.
function setJsaModalTitle(editing) {
  const el = document.getElementById('modal-jsa-title');
  if (el) el.textContent = editing ? 'Edit Job Safety Analysis' : 'New Job Safety Analysis';
}

// Editing a template report: the mode is fixed. A static badge replaces the toggle.
function getTemplateModeBadgeHTML(name, version) {
  return `
      <div style="margin:0 16px 16px;padding:8px 12px;border-radius:10px;background:#E2E8F0;font-weight:700;font-size:0.9rem;color:var(--text-primary);">
        ${escapeHtml(name || 'Template')}${version ? ` <span style="font-weight:400;color:var(--text-secondary);">v${escapeHtml(String(version))}</span>` : ''}
      </div>
    `;
}

// TASK-4839 COD r1 (Code Reviewer: stored XSS) — a field id comes from the
// report's templateSnapshot, which any PIN session can write, and it lands in
// id="" / for="" attributes. Encode every char outside [A-Za-z0-9_-] as
// _<hex>_ — a validated id (^[A-Za-z][A-Za-z0-9_]*$) is unchanged, and the
// output can never break out of an attribute or a CSS/querySelector context.
function tfId(fieldId) {
  return 'jsa-tf-' + String(fieldId == null ? '' : fieldId).replace(/[^A-Za-z0-9_-]/g, c => `_${c.codePointAt(0).toString(16)}_`);
}

function getTemplateFacilityPickerHTML(field) {
  // Reuses the ONE emergency-facility picker (Google Places, TASK-era ids
  // jsa-facility-*) — that is why a template may carry at most one `places`
  // field (validateJsaTemplate). The answer is JsaState.emergencyFacility.
  return `
    <div class="form-group" style="grid-column:1 / -1;">
      <label>${escapeHtml(field.label || 'Nearest Facility')}${field.required ? ' *' : ''}</label>
      <div class="jsa-emergency-location">
        <button type="button" class="btn btn-sm btn-secondary" onclick="window.JSAModule.getMyLocation()">
          <span class="material-icons" style="font-size:16px;">my_location</span> My Location
        </button>
        <span id="jsa-location-status" style="font-size:0.85rem;color:var(--text-secondary);margin-left:8px;"></span>
      </div>
      <div style="display:flex;gap:8px;margin-top:8px;">
        <input type="search" id="jsa-facility-search" placeholder="Search hospitals, clinics..." autocomplete="off" style="flex:1;">
        <button type="button" class="btn btn-sm btn-primary" onclick="window.JSAModule.searchFacilities()">
          <span class="material-icons" style="font-size:16px;">search</span>
        </button>
      </div>
      <div id="jsa-facility-results" style="margin-top:8px;"></div>
      <div id="jsa-selected-facility" style="margin-top:8px;"></div>
      <input type="text" id="jsa-facility-manual" placeholder="Or enter manually: hospital name, address, phone" style="margin-top:8px;">
    </div>`;
}

function getTemplateFieldInputHTML(field, ctx) {
  const id = tfId(field.id);
  const req = field.required ? ' required' : '';
  const star = field.required ? ' *' : '';
  const label = `<label for="${id}">${escapeHtml(field.label || field.id)}${star}</label>`;
  const help = field.help ? `<small style="display:block;color:var(--text-secondary);font-size:0.75rem;margin-top:2px;">${escapeHtml(field.help)}</small>` : '';
  const ph = field.placeholder ? ` placeholder="${escapeHtml(field.placeholder)}"` : '';
  switch (field.type) {
    case 'text':
      return `<div class="form-group">${label}<input type="text" id="${id}"${ph}${req}>${help}</div>`;
    case 'longtext':
      return `<div class="form-group" style="grid-column:1 / -1;">${label}<textarea id="${id}" rows="3" style="font-family:inherit;"${ph}${req}></textarea>${help}</div>`;
    case 'number':
      return `<div class="form-group">${label}<input type="number" id="${id}" step="any"${ph}${req}>${help}</div>`;
    case 'date':
      return `<div class="form-group">${label}<input type="date" id="${id}"${req}>${help}</div>`;
    case 'select': {
      const opts = (field.options || []).map(o => {
        const v = (o && typeof o === 'object') ? o.value : o;
        const l = (o && typeof o === 'object') ? (o.label ?? o.value) : o;
        return `<option value="${escapeHtml(v)}">${escapeHtml(l)}</option>`;
      }).join('');
      return `<div class="form-group">${label}<select id="${id}"${req}><option value="">— Select —</option>${opts}</select>${help}</div>`;
    }
    case 'checkbox':
      return `<div class="form-group"><label class="checkbox-label"><input type="checkbox" id="${id}"><span>${escapeHtml(field.label || field.id)}</span></label>${help}</div>`;
    case 'person': {
      const people = (ctx.people || []).filter(p => p && p.status !== 'inactive');
      const opts = people.map(p => `<option value="${escapeHtml(p.id)}" data-name="${escapeHtml(p.name)}">${escapeHtml(p.name)}</option>`).join('');
      return `<div class="form-group">${label}<select id="${id}"${req}><option value="">— Select —</option>${opts}</select>${help}</div>`;
    }
    case 'vehicle': {
      const trucks = ctx.trucks || [];
      const opts = trucks.map(t => {
        const disp = window.getUnitDisplay ? window.getUnitDisplay(t) : (t.unit || t.nickname || t.id);
        return `<option value="${escapeHtml(t.id)}" data-display="${escapeHtml(disp)}">${escapeHtml(disp)}</option>`;
      }).join('');
      return `<div class="form-group">${label}<select id="${id}"${req}><option value="">— Select —</option>${opts}</select>${help}</div>`;
    }
    case 'places':
      return getTemplateFacilityPickerHTML(field);
    default:
      return ''; // unknown type → not rendered (allow-list, never a guess)
  }
}

function getTemplateCheckboxGridHTML(section) {
  // Preserve first-seen group order; ungrouped fields render first, unlabeled.
  const groups = [];
  const byGroup = new Map();
  (section.fields || []).forEach(f => {
    const g = f.group || '';
    if (!byGroup.has(g)) { byGroup.set(g, []); groups.push(g); }
    byGroup.get(g).push(f);
  });
  return groups.map(g => {
    const fields = byGroup.get(g);
    const chips = fields.filter(f => f.type === 'checkbox').map(f => `
            <button type="button" class="jsa-hazard-chip" data-id="${escapeHtml(f.id)}" onclick="window.JSAModule.toggleTemplateCheck('${window.escJsAttr(f.id)}')">
              <span>${escapeHtml(f.label)}</span>
            </button>`).join('');
    const texts = fields.filter(f => f.type === 'text').map(f => `
          <div class="form-group" style="margin-top:8px;">
            <label for="${tfId(f.id)}">${escapeHtml(f.label)}</label>
            <input type="text" id="${tfId(f.id)}">
          </div>`).join('');
    return `
        <div style="margin-bottom:10px;">
          ${g ? `<div style="font-size:0.78rem;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;color:var(--text-secondary);margin:6px 0 4px;">${escapeHtml(g)}</div>` : ''}
          <div class="jsa-chip-grid">${chips}</div>${texts}
        </div>`;
  }).join('');
}

function getTemplateSignInSectionHTML(section, editing) {
  const slots = Number(section.slots) > 0 ? Number(section.slots) : 0;
  if (editing) {
    return `
        <p style="font-size:0.85rem;color:var(--text-secondary);">Sign-ins are kept with the filed report. Open the report to view or add attendee signatures.</p>`;
  }
  return `
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:8px;">Each attendee prints their name and signs${slots ? ` (paper form: ${slots} slots)` : ''}. Select a job to auto-populate the crew.</p>
        <div class="jsa-crew-list" id="jsa-crew-list"></div>
        <div style="margin-top:8px;display:flex;gap:8px;align-items:center;">
          <input type="text" id="jsa-add-crew-name" placeholder="Add attendee name" style="flex:1;">
          <button type="button" class="btn btn-sm btn-secondary" onclick="window.JSAModule.addManualCrew()">
            <span class="material-icons" style="font-size:16px;">person_add</span> Add
          </button>
        </div>`;
}

// Fixed job-binding header shared by every mode. 🔴 The ids are the Standard
// form's (#jsa-customer / #jsa-job / #jsa-date) on purpose: populateCustomerDropdown,
// populateJobDropdown, onCustomerSelect, onJobSelect and resolveJobBinding all
// key off them, so a template sheet links its jobDay exactly like a Standard
// one — the job card flips to "JSA filed" and jsaAccountability clears.
function getTemplateFixedHeaderHTML() {
  return `
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('jsa-sec-job')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>Customer, Job & Date</span>
      </div>
      <div class="jsa-section-body" id="jsa-sec-job">
        <div class="form-row">
          <div class="form-group">
            <label for="jsa-customer">Customer</label>
            <select id="jsa-customer" onchange="window.JSAModule.onCustomerSelect()">
              <option value="">— None —</option>
            </select>
            <small id="jsa-customer-hint" style="display:none;color:var(--text-secondary);font-size:0.75rem;margin-top:2px;">No customers yet — add one in <a href="#" onclick="event.preventDefault();window.JSAModule.goToDispatchAddJob()" style="color:var(--brand-blue);text-decoration:underline;">Dispatch</a> or <a href="#" onclick="event.preventDefault();window.JSAModule.goToCRM()" style="color:var(--brand-blue);text-decoration:underline;">CRM</a></small>
          </div>
          <div class="form-group">
            <label for="jsa-date">Date *</label>
            <input type="date" id="jsa-date" required>
          </div>
        </div>
        <div class="form-group">
          <label for="jsa-job">Job</label>
          <select id="jsa-job" onchange="window.JSAModule.onJobSelect()">
            <option value="">Select job (optional)</option>
          </select>
        </div>
      </div>
    </div>`;
}

function getTemplateFormHTML(snapshot, ctx) {
  const c = ctx || {};
  const sections = (snapshot && Array.isArray(snapshot.sections)) ? snapshot.sections : [];
  const body = sections.map((s, i) => {
    const secId = `jsa-sec-tpl-${s.id}`;
    let inner = '';
    if (s.layout === 'checkbox-grid') {
      inner = getTemplateCheckboxGridHTML(s);
    } else if (s.layout === 'signin') {
      inner = getTemplateSignInSectionHTML(s, !!c.editing);
    } else {
      inner = `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;">${(s.fields || []).map(f => getTemplateFieldInputHTML(f, c)).join('')}</div>`;
    }
    return `
    <div class="jsa-section">
      <div class="jsa-section-header" onclick="window.JSAModule.toggleSection('${window.escJsAttr(secId)}')">
        <span class="material-icons jsa-section-chevron">expand_more</span>
        <span>${i + 2}. ${escapeHtml(s.title || s.id)}</span>
      </div>
      <div class="jsa-section-body" id="${escapeHtml(secId)}">${inner}</div>
    </div>`;
  }).join('');
  return getTemplateFixedHeaderHTML() + body;
}

// The TASK-794 "Start JSA from a job card" prefill, shared by the Standard and
// template forms (the template form has the same #jsa-customer/#jsa-job ids).
function applyPrefillJobToForm() {
  if (!JsaState.prefillJobId) return;
  const job = JsaState.jobs.find(j => j.id === JsaState.prefillJobId);
  if (!job) return;
  JsaState.prefillApplied = true; // TASK-10011
  const custSel = document.getElementById('jsa-customer');
  if (custSel && job.customerId) {
    _jsaSetCustomer(custSel, job.customerId);
    populateJobDropdown(job.customerId);
  }
  const jobSel = document.getElementById('jsa-job');
  if (jobSel) {
    // The dedupe in populateJobDropdown may drop this exact job in favor of a
    // same-title sibling; ensure the option exists before selecting it.
    if (!Array.from(jobSel.options).some(o => o.value === job.id)) {
      const opt = document.createElement('option');
      opt.value = job.id;
      opt.textContent = job.title || 'Untitled Job';
      opt.dataset.title = job.title || 'Untitled Job';
      opt.dataset.customerId = job.customerId || '';
      jobSel.appendChild(opt);
    }
    jobSel.value = job.id;
    onJobSelect();
  }
}

// TASK-10011 — the jobs list landed AFTER a new form opened from a job: put the
// job on the form now — only on a NEW form that is still open, still waiting
// for its prefill, and untouched by the user (never overwrite a choice).
function lateApplyJobPrefill() {
  if (!JsaState.prefillJobId || JsaState.prefillApplied) return;
  if (JsaState.editingJsaId || JsaState.newFormTouched) return;
  const modal = document.getElementById('modal-jsa');
  if (!modal || modal.classList.contains('hidden')) return;
  applyPrefillJobToForm();
}

// TASK-10011 — is the JSA view ready to open a form FROM A JOB? The first
// snapshots of jobs (prefill), people (crew names) and — when the company
// uses templates — jsaTemplates (the template button / sole-template default)
// must have landed. Pure over its inputs for the unit tripwire.
function jsaReadyForJobForm(firstSnap, templatesEnabled) {
  const s = firstSnap || {};
  return s.jobs === true && s.people === true && (!templatesEnabled || s.templates === true);
}

// TASK-10011 — THE one opener for "start a JSA for this job" (the crew's Job
// Board and the office Job Details / quickview both call it; reached through
// window.JSAModule, never a ?v= import — Pitfall #1). Routes to the
// Inspections/JSA view, waits (bounded) until the view is mounted and its first
// snapshots are in, then opens a NEW JSA prefilled from the job + day.
//
// The wait fixes the first-open gap the 2026-09-29 Guy walkthrough found
// (TASK-9386 (1)): the form used to open ~200ms after initJSA subscribed,
// before jobs/templates arrived, so the very first form from a job could miss
// the job prefill and open without the company's template. The late-refresh
// paths (refreshNewJsaTemplateButtons, lateApplyJobPrefill) stay as the
// backstop when the cap is hit on a slow connection — the form is never
// blocked on a read for longer than JOB_FORM_WAIT_MS.
const JOB_FORM_WAIT_MS = 4000;
function startForJob(jobId, jobDayId, opts) {
  const o = opts || {};
  const rt = o.returnTo && typeof o.returnTo.jobId === 'string' && o.returnTo.jobId ? o.returnTo : null;
  if (window.location.hash.split('?')[0] !== '#/pretrip') window.location.hash = '#/pretrip';
  // One pending open at a time: a second tap (or a second job) supersedes the
  // first, so two waits can never both open (and reset) the form.
  const token = {};
  JsaState.pendingJobForm = token;
  const startedAt = Date.now();
  let mountedAt = 0;
  let tries = 0;
  const open = () => {
    if (JsaState.pendingJobForm !== token) return;
    // The user left the JSA view while it loaded — drop the open quietly.
    if (window.location.hash.split('?')[0] !== '#/pretrip') { JsaState.pendingJobForm = null; return; }
    const mounted = !!(window.PretripModule?.switchDvirTab && document.getElementById('modal-jsa') && document.getElementById('jsa-panel'));
    if (mounted) {
      if (!mountedAt) mountedAt = Date.now();
      const ready = jsaReadyForJobForm(JsaState.firstSnap, isJsaTemplatesEnabled());
      if (ready || Date.now() - mountedAt >= JOB_FORM_WAIT_MS) {
        JsaState.pendingJobForm = null;
        try { window.PretripModule.switchDvirTab('jsa'); } catch (_) {}
        showNewJSA(jobId || undefined, jobDayId || undefined, rt ? { returnTo: rt } : undefined);
        return;
      }
    } else if (Date.now() - startedAt > 10000) {
      JsaState.pendingJobForm = null;
      window.showToast?.('The JSA form did not load. Open Inspections → JSA and try again.', 'error');
      return;
    }
    tries++;
    setTimeout(open, tries < 4 ? 100 : 150);
  };
  setTimeout(open, 100);
  return true;
}

function initTemplateForm() {
  const dateInput = document.getElementById('jsa-date');
  if (dateInput) dateInput.value = window.RecordDate.today(); // TASK-9923
  populateCustomerDropdown();
  populateJobDropdown('');
  applyPrefillJobToForm();
  const crewList = document.getElementById('jsa-crew-list');
  if (crewList && !Object.keys(JsaState.crewSignatures).length) {
    crewList.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;">Select a job to auto-populate the crew, or add attendees below.</p>';
  }
}

function toggleTemplateCheck(fieldId) {
  if (JsaState.templateChecks.has(fieldId)) JsaState.templateChecks.delete(fieldId);
  else JsaState.templateChecks.add(fieldId);
  const chip = document.querySelector(`.jsa-hazard-chip[data-id="${CSS.escape(String(fieldId))}"]`);
  if (chip) chip.classList.toggle('selected', JsaState.templateChecks.has(fieldId));
}

// Read every answer off the DOM by DECLARED type. Empty / false / unselected
// answers are omitted so the doc carries only what was filled in. `ctx.getEl`
// is injectable for the unit tripwire.
function readTemplateAnswersFromDom(snapshot, ctx) {
  const c = ctx || {};
  const getEl = c.getEl || ((id) => document.getElementById(id));
  const checks = c.checks || new Set();
  const answers = {};
  (snapshot?.sections || []).forEach(s => {
    (s.fields || []).forEach(f => {
      const el = getEl(tfId(f.id));
      let v;
      switch (f.type) {
        case 'text':
        case 'longtext':
        case 'select':
        case 'date':
          v = (el?.value ?? '').toString().trim();
          break;
        case 'number': {
          const raw = (el?.value ?? '').toString().trim();
          v = raw === '' ? null : Number(raw);
          if (v !== null && !Number.isFinite(v)) v = null;
          break;
        }
        case 'checkbox':
          v = s.layout === 'checkbox-grid' ? checks.has(f.id) : !!el?.checked;
          break;
        case 'person': {
          const id = el?.value || '';
          const name = el?.selectedOptions?.[0]?.dataset?.name || el?.selectedOptions?.[0]?.textContent?.trim() || '';
          v = id ? { id, name } : null;
          break;
        }
        case 'vehicle': {
          const id = el?.value || '';
          const display = el?.selectedOptions?.[0]?.dataset?.display || el?.selectedOptions?.[0]?.textContent?.trim() || '';
          v = id ? { id, display } : null;
          break;
        }
        case 'places': {
          if (c.facility && c.facility.name) {
            v = c.facility;
          } else {
            const manual = (getEl('jsa-facility-manual')?.value ?? '').toString().trim();
            v = manual ? { name: manual, address: '', phone: '', source: 'manual' } : null;
          }
          break;
        }
        default:
          v = undefined;
      }
      if (v === undefined || v === null || v === '' || v === false) return;
      answers[f.id] = v;
    });
  });
  return answers;
}

function requiredTemplateFieldsMissing(snapshot, answers) {
  const missing = [];
  (snapshot?.sections || []).forEach(s => (s.fields || []).forEach(f => {
    if (f.required && (answers[f.id] === undefined || answers[f.id] === null || answers[f.id] === '')) missing.push(f.label || f.id);
  }));
  return missing;
}

// Copy bound answers onto the shared jsaReports columns every consumer already
// reads (notifications: supervisorName / locationArea; cards: truckDisplay;
// detail + PDF: emergencyFacility / jobDescription).
function applyTemplateBinds(sections, answers, data) {
  (sections || []).forEach(s => (s.fields || []).forEach(f => {
    if (!f.bind) return;
    const v = answers[f.id];
    switch (f.bind) {
      case 'supervisor':
        data.supervisorId = (v && v.id) || '';
        data.supervisorName = (v && v.name) || '';
        break;
      case 'locationArea':
        if (typeof v === 'string' && v) data.locationArea = v;
        break;
      case 'truckDisplay':
        if (v && v.display) data.truckDisplay = v.display;
        break;
      case 'emergencyFacility':
        if (v && typeof v === 'object') data.emergencyFacility = v;
        break;
      case 'jobDescription':
        if (typeof v === 'string' && v) data.jobDescription = v;
        break;
      default:
        break;
    }
  }));
  return data;
}

// The fixed header (customer / job / date) read the same way for Standard and
// template sheets. TASK-794: prefer the pre-linked jobDay — multi-day jobs have
// many jobDays for one jobId and the bare find picks the FIRST, which mis-links.
function resolveJobBinding() {
  const customerSelect = document.getElementById('jsa-customer');
  const customerId = customerSelect?.value || '';
  const customerName = customerSelect?.selectedOptions[0]?.dataset?.name || customerSelect?.selectedOptions[0]?.textContent?.trim() || '';

  const rawJobId = document.getElementById('jsa-job')?.value || '';
  const jobId = rawJobId === 'none' ? '' : rawJobId;
  const jobOption = document.getElementById('jsa-job')?.selectedOptions[0];
  const jobTitle = (rawJobId === 'none' || !rawJobId) ? '' : (jobOption?.dataset?.title || jobOption?.textContent?.trim() || '');

  let jobDay = null;
  if (jobId && JsaState.prefillJobDayId) {
    const pref = JsaState.jobDays.find(jd => jd.id === JsaState.prefillJobDayId);
    if (pref && pref.jobId === jobId) jobDay = pref;
    // TASK-10011 COD r1 — the prefill day may not be today (office pre-start):
    // JsaState.jobDays only holds today's, so use the doc onJobSelect read.
    const held = JsaState.prefillJobDay;
    if (!jobDay && held && held.id === JsaState.prefillJobDayId && held.jobId === jobId) jobDay = held;
  }
  // TASK-10011 COD r2 — the form was opened for an EXPLICIT day of this job and
  // that day's doc has not arrived (Save tapped before the read, or the read
  // failed): bind the requested day by id, never fall back to today's day.
  const explicitDayId = (!jobDay && jobId && JsaState.prefillJobDayId && JsaState.prefillJobId === jobId && !JsaState.prefillDayInvalid)
    ? JsaState.prefillJobDayId : '';
  // COD r4 — and if that explicit day turned out invalid, bind NO day rather
  // than quietly filing a future-day JSA against today's day of the job.
  const explicitDayRejected = !jobDay && jobId && JsaState.prefillJobDayId && JsaState.prefillJobId === jobId && JsaState.prefillDayInvalid === true;
  if (!jobDay && !explicitDayId && !explicitDayRejected) jobDay = jobId ? JsaState.jobDays.find(jd => jd.jobId === jobId) : null;
  const jobDayId = jobDay?.id || explicitDayId;
  const truckDisplay = getTruckDisplayFromJobDay(jobDay);
  const dateVal = document.getElementById('jsa-date')?.value || '';
  return { customerId, customerName, jobId, jobTitle, jobDay, jobDayId, truckDisplay, dateVal };
}

// Pure: the jsaReports doc body for a template sheet. `existing` (edit) keeps the
// FROZEN templateSnapshot / templateId / templateVersion / templateName of the
// filed report — an edit never re-snapshots from the live template and never
// converts the report to another mode.
function buildTemplateReportData({ snapshot, template, answers, binding, actor, existing, now }) {
  const ts = now || Timestamp.now();
  const a = actor || {};
  const data = {
    formMode: 'template',
    templateId: existing?.templateId || template?.id || snapshot?.id || '',
    templateVersion: existing?.templateVersion ?? snapshot?.version ?? template?.version ?? 1,
    templateName: existing?.templateName || snapshot?.name || template?.name || 'Template',
    templateSnapshot: existing?.templateSnapshot || {
      name: snapshot?.name || template?.name || 'Template',
      version: snapshot?.version ?? template?.version ?? 1,
      sections: snapshot?.sections || [],
    },
    templateAnswers: answers || {},
    customerId: binding.customerId || '',
    customerName: binding.customerName || '',
    jobId: binding.jobId || '',
    jobDayId: binding.jobDayId || '',
    jobTitle: binding.jobTitle || '',
    locationArea: binding.jobTitle || '',
    truckDisplay: binding.truckDisplay || '',
    date: Timestamp.fromMillis(_jsaDayMs(binding.dateVal)), // TASK-9923 company-zone noon
    supervisorId: '',
    supervisorName: '',
    jobDescription: '',
    emergencyFacility: null,
    completedById: a.uid || '',
    completedByName: a.displayName || '',
    submittedByPersonId: a.personId || '',
    submittedByPersonName: a.personName || '',
    status: 'completed',
    createdAt: ts,
    updatedAt: ts,
    // Always empty in template mode — sign-ins live in the signIns subcollection.
    crewAcknowledgments: [],
  };
  applyTemplateBinds(data.templateSnapshot.sections, data.templateAnswers, data);
  if (!existing) {
    data.signInCount = 0;
    data.signInNames = [];
  }
  return data;
}

// Pure: one signIns doc per SIGNED crew entry. Unsigned rows are not sign-ins.
// The PNG is the ONLY place a signature is written; nothing copies it onto the
// parent doc or into templateAnswers.
function buildTemplateSignInDocs(crewSignatures, actor, now) {
  const ts = now || Timestamp.now();
  const a = actor || {};
  return Object.entries(crewSignatures || {})
    .filter(([, d]) => d && typeof d.signature === 'string' && d.signature.length >= 100)
    .map(([personId, d]) => ({
      name: String(d.name || '').slice(0, 200),
      personId: personId.startsWith('manual_') ? null : personId,
      signaturePngB64: d.signature,
      signedAt: d.acknowledgedAt instanceof Date ? Timestamp.fromDate(d.acknowledgedAt) : ts,
      source: 'crew',
      addedByUid: a.uid || '',
      addedByPersonId: a.personId || '',
    }));
}

// ---- Detail / print (template) ----
let currentDetailSignIns = null; // sign-in rows loaded for the open detail report

function templateSelectLabel(field, value) {
  const o = (field.options || []).find(x => (x && typeof x === 'object' ? x.value : x) === value);
  if (!o) return value;
  return (typeof o === 'object') ? (o.label ?? o.value) : o;
}

// Display string for an answer, by declared type. Returns '' when unanswered.
function templateAnswerDisplay(field, value) {
  if (value === undefined || value === null || value === '' || value === false) return '';
  switch (field.type) {
    case 'checkbox': return value === true ? 'Yes' : '';
    case 'select': return String(templateSelectLabel(field, value));
    case 'person': return typeof value === 'object' ? String(value.name || '') : String(value);
    case 'vehicle': return typeof value === 'object' ? String(value.display || '') : String(value);
    case 'places': {
      if (typeof value !== 'object') return String(value);
      return [value.name, value.address, value.phone].filter(Boolean).join(' · ');
    }
    case 'number': return String(value);
    default: return typeof value === 'object' ? '' : String(value);
  }
}

function getTemplateDetailHTML(report) {
  const date = report.date?.toDate?.() || new Date(report.date);
  const snap = report.templateSnapshot || { sections: [] };
  const answers = report.templateAnswers || {};
  const sectionsHTML = (snap.sections || []).map(s => {
    let body = '';
    if (s.layout === 'checkbox-grid') {
      const groups = [];
      const byGroup = new Map();
      (s.fields || []).forEach(f => {
        const g = f.group || '';
        if (!byGroup.has(g)) { byGroup.set(g, []); groups.push(g); }
        byGroup.get(g).push(f);
      });
      const parts = groups.map(g => {
        const fields = byGroup.get(g);
        const checked = fields.filter(f => f.type === 'checkbox' && answers[f.id] === true);
        const texts = fields.filter(f => f.type === 'text' && answers[f.id]);
        if (!checked.length && !texts.length) return '';
        return `
          <div style="margin-bottom:6px;">
            ${g ? `<div style="font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;color:var(--text-secondary);margin-bottom:2px;">${escapeHtml(g)}</div>` : ''}
            <div class="jsa-detail-chips">${checked.map(f => `<span class="jsa-detail-chip">${escapeHtml(f.label)}</span>`).join('')}</div>
            ${texts.map(f => `<p style="margin-top:4px;"><strong>${escapeHtml(f.label)}:</strong> ${escapeHtml(answers[f.id])}</p>`).join('')}
          </div>`;
      }).filter(Boolean);
      body = parts.length ? parts.join('') : '<p style="color:var(--text-secondary);">None selected</p>';
    } else if (s.layout === 'signin') {
      body = `
        <div id="jsa-signin-list" style="font-size:0.85rem;color:var(--text-secondary);">${(report.signInCount || 0) > 0 ? 'Loading…' : 'No sign-ins yet.'}</div>
        ${window.isReadOnlyLinkSession?.() ? '' : !_jsaTemplateSignInDirect(report) ? _jsaTemplateSelfSignInHTML(report) : `
        <div style="margin-top:10px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <input type="text" id="jsa-signin-add-name" list="jsa-signin-people" placeholder="Attendee name (print)" style="flex:1;min-width:160px;">
          <datalist id="jsa-signin-people">${(JsaState.people || []).filter(p => p && p.status !== 'inactive').map(p => `<option value="${escapeHtml(p.name)}"></option>`).join('')}</datalist>
          <button type="button" class="btn btn-sm btn-primary" onclick="window.JSAModule.openSignInPad()">
            <span class="material-icons" style="font-size:16px;">draw</span> Sign
          </button>
        </div>`}`;
    } else {
      const rows = (s.fields || []).map(f => {
        const disp = templateAnswerDisplay(f, answers[f.id]);
        if (!disp) return '';
        return `<div><strong>${escapeHtml(f.label)}:</strong> ${escapeHtml(disp)}</div>`;
      }).filter(Boolean);
      body = rows.length ? `<div class="jsa-detail-grid">${rows.join('')}</div>` : '<p style="color:var(--text-secondary);">Nothing entered</p>';
    }
    return `
      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">${escapeHtml(s.title || s.id)}${s.layout === 'signin' ? ` <span id="jsa-signin-count" style="font-size:0.8rem;font-weight:400;color:var(--text-secondary);">(${Number(report.signInCount) || 0})</span>` : ''}</h4>
        ${body}
      </div>`;
  }).join('');

  return `
    ${_jsaRevisionBannerHTML(report)}
    <div style="padding: 16px;">
      <div style="display:inline-block;padding:3px 10px;border-radius:12px;background:var(--brand-blue);color:#fff;font-size:0.8rem;font-weight:600;margin-bottom:12px;">${escapeHtml(report.templateName || snap.name || 'Template')}${snap.version ? ` · v${escapeHtml(String(snap.version))}` : ''}</div>
      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Customer, Job & Date</h4>
        <div class="jsa-detail-grid">
          <div><strong>Customer:</strong> ${escapeHtml(report.customerName || 'N/A')}</div>
          <div><strong>Date:</strong> ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          <div><strong>Job:</strong> ${escapeHtml(report.jobTitle || 'N/A')}</div>
          ${report.submittedByPersonName || report.completedByName ? `<div><strong>Filed by:</strong> ${escapeHtml(report.submittedByPersonName || report.completedByName)}</div>` : ''}
        </div>
      </div>
      ${sectionsHTML}
      <div class="jsa-detail-section" id="jsa-external-signers-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">
          External Signers
          ${(report.externalSignersCount || 0) > 0 ? `<span style="font-size:0.8rem;font-weight:400;color:var(--text-secondary);">(${Number(report.externalSignersCount) || 0})</span>` : ''}
        </h4>
        <div id="jsa-external-signers-list" style="font-size:0.85rem;color:var(--text-secondary);">
          ${(report.externalSignersCount || 0) > 0 ? 'Loading…' : 'No external signers yet. Use <strong>Get Signatures</strong> below to share a link with anyone on site.'}
        </div>
      </div>
    </div>
  `;
}

function renderSignInRowsHTML(signIns) {
  if (!signIns || !signIns.length) return 'No sign-ins yet.';
  return `
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${signIns.map(s => {
          const when = s.signedAt?.toDate?.()?.toLocaleString?.('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) || '';
          return `
            <div style="border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:8px 10px;background:#fff;">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
                <strong style="color:var(--text-primary);">${escapeHtml(s.name || '(unnamed)')}</strong>
                <span style="font-size:0.72rem;color:var(--text-secondary);">${escapeHtml(when)}</span>
              </div>
              ${/^data:image\/png;base64,/.test(String(s.signaturePngB64 || '')) ? `<div style="margin-top:6px;border-bottom:1px solid var(--border-light);min-height:36px;display:flex;align-items:flex-end;padding-bottom:2px;">
                <img src="${escapeHtml(s.signaturePngB64)}" alt="Signature" style="max-height:36px;max-width:100%;">
              </div>` : ''}
            </div>`;
        }).join('')}
      </div>`;
}

// The ONE sign-in loader (detail, print, and the TASK-9371 revision snapshot).
// `opts.includeDeleted` keeps soft-deleted rows (flagged deleted:true) — the
// revision snapshot records the sheet exactly as it stood.
async function loadSignInsForDetail(jsaId, opts = {}) {
  const tenantId = opts.tenantId || window.AppState?.tenant;
  if (!tenantId || !jsaId) return [];
  const snap = await getDocs(collection(db, 'tenants', tenantId, 'jsaReports', jsaId, 'signIns'));
  // COD r1 — a soft-deleted row (deleted:true, kept + audited) is not an attendee.
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => opts.includeDeleted || r.deleted !== true);
  rows.sort((a, b) => (a.signedAt?.toDate?.()?.getTime?.() || 0) - (b.signedAt?.toDate?.()?.getTime?.() || 0));
  return rows;
}

async function refreshDetailSignIns(jsaId) {
  const listEl = document.getElementById('jsa-signin-list');
  try {
    const rows = await loadSignInsForDetail(jsaId);
    if (currentDetailJsaId !== jsaId) return; // detail moved on
    currentDetailSignIns = rows;
    if (listEl) listEl.innerHTML = renderSignInRowsHTML(rows);
    _jsaPaintSelfSignInState(rows);
    const countEl = document.getElementById('jsa-signin-count');
    if (countEl) countEl.textContent = `(${rows.length})`;
  } catch (e) {
    console.error('[JSA] loadSignIns error:', e);
    if (listEl) listEl.innerHTML = `<span style="color:var(--action-danger);">Could not load sign-ins: ${escapeHtml(e?.message || 'unknown error')}</span>`;
  }
}

// TASK-10024 (left-open #1) — who may write signIns rows DIRECTLY, mirroring
// firestore.rules jsaSignInReportOk: a safety manager (owner / hr), the login
// that filed the sheet, or a PIN session for the person who filed it. That is
// the shared-tablet tailgate (type any attendee's name, pass the tablet round).
// Everyone else used to get the same name box and a "Could not save sign-in"
// refusal; they now get "Sign in as <me>", which goes through signJsaCrewLine
// (server: own row only, on the job day, once per content version).
function _jsaTemplateSignInDirect(report) {
  if (!report) return false;
  if (_jsaViewerIsSafetyManager()) return true;
  const uid = window.AppState?.user?.uid || '';
  if (uid && report.completedById && report.completedById === uid) return true;
  const me = getViewerPersonId();
  return window.AppState?.driverModePin === true && !!me && report.submittedByPersonId === me;
}

// COD r2: only a SERVER-written self row (source 'crew_self', signedAt = server
// time) proves "already signed in" here. A filer-tablet row's signedAt is the
// client's clock (rules allow +5 min), so it must never hide the button — the
// server decides on createTime and says so if it refuses.
function _jsaSelfSignInDone(rows, report) {
  const me = getViewerPersonId();
  if (!me || !Array.isArray(rows)) return false;
  const since = _jsaMillis(report?.contentRevisedAt) || 0;
  return rows.some(r => r && r.deleted !== true && r.personId === me && r.source === 'crew_self' && (_jsaMillis(r.signedAt) || 0) >= since);
}

function _jsaTemplateSelfSignInHTML(report) {
  const me = getViewerPersonId();
  if (!me) {
    return `<p class="jsa-signin-self-note" style="margin-top:10px;font-size:0.85rem;color:var(--text-secondary);">Only the person who filed this sheet or the office can add sign-ins from this login.</p>`;
  }
  const meName = (JsaState.people || []).find(p => p && p.id === me)?.name || window.AppState?.driverPersonName || 'me';
  return `
        <div id="jsa-signin-self" style="margin-top:10px;">
          <button type="button" class="btn btn-primary jsa-crew-sign-btn jsa-signin-self-btn" style="width:100%;min-height:44px;display:flex;align-items:center;justify-content:center;gap:8px;" onclick="window.JSAModule.openTemplateSelfSignIn()">
            <span class="material-icons" style="font-size:18px;">draw</span> Sign in as ${escapeHtml(meName)}
          </button>
        </div>`;
}

// After the rows load: the viewer already signed this version → say so instead
// of offering a second sign-in the server would refuse.
function _jsaPaintSelfSignInState(rows) {
  const box = document.getElementById('jsa-signin-self');
  if (!box || !_jsaSelfSignInDone(rows, currentDetailReport)) return;
  box.innerHTML = `<p style="font-size:0.9rem;font-weight:600;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin:0;"><span class="material-icons" style="font-size:18px;color:var(--status-success, #16a34a);">check_circle</span> You're signed in on this sheet.</p>`;
}

function openTemplateSelfSignIn() {
  if (window.isReadOnlyLinkSession?.()) {
    window.showToast?.('Read-only preview — log in with your PIN to sign.', 'info');
    return;
  }
  const report = currentDetailReport;
  const me = getViewerPersonId();
  if (!report || !currentDetailJsaId || !me) return;
  const name = (JsaState.people || []).find(p => p && p.id === me)?.name || window.AppState?.driverPersonName || 'Crew member';
  JsaState.crewSignatures.__signin_self = { name, personId: me, jsaId: currentDetailJsaId, seenContentMs: _jsaMillis(report.contentRevisedAt) || 0, signature: null, acknowledgedAt: null };
  initCrewSignature('__signin_self');
}

async function _saveTemplateSelfSignIn(sigData) {
  const pending = JsaState.crewSignatures.__signin_self;
  delete JsaState.crewSignatures.__signin_self;
  if (!pending || !pending.jsaId) return;
  try {
    await signCrewLineOnServer(pending.jsaId, pending.personId, sigData, pending.seenContentMs);
    window.showToast?.(`${pending.name} signed in`, 'success');
    if (currentDetailJsaId === pending.jsaId) refreshDetailSignIns(pending.jsaId);
  } catch (e) {
    console.error('[JSA] self sign-in error:', e);
    window.showToast?.(e.message || 'Could not save the sign-in', 'error');
  }
}

// Detail-view "Sign" — the in-room path for a template sheet AFTER it is filed.
// This is the crew path (a signIns doc per attendee), NOT the external link:
// the link's 10/hr-per-network rate limit would stop a 48-person yard tablet at
// signer 11. The external link stays for third parties.
function openSignInPad() {
  if (window.isReadOnlyLinkSession?.()) {
    window.showToast?.('Read-only preview — log in with your PIN to sign.', 'info');
    return;
  }
  const name = document.getElementById('jsa-signin-add-name')?.value?.trim();
  if (!name) { window.showToast?.('Enter the attendee name first', 'error'); return; }
  if (!currentDetailJsaId) return;
  // Reuse the crew pad: park the pending attendee under a reserved key.
  const person = (JsaState.people || []).find(p => p && p.name === name && p.status !== 'inactive');
  JsaState.crewSignatures.__signin_add = { name, personId: person?.id || null, signature: null, acknowledgedAt: null };
  initCrewSignature('__signin_add');
}

async function saveDetailSignIn(sigData) {
  const pending = JsaState.crewSignatures.__signin_add;
  delete JsaState.crewSignatures.__signin_add;
  const tenantId = window.AppState?.tenant;
  const jsaId = currentDetailJsaId;
  if (!pending || !tenantId || !jsaId) return;
  try {
    await addDoc(collection(db, 'tenants', tenantId, 'jsaReports', jsaId, 'signIns'), {
      name: String(pending.name || '').slice(0, 200),
      personId: pending.personId || null,
      signaturePngB64: sigData,
      signedAt: Timestamp.now(),
      source: 'crew',
      // COD r1: rules bind these to the caller — addedByUid must equal the auth
      // uid (AppState.user IS auth.currentUser for members and PIN sessions) and
      // a PIN session's addedByPersonId must be its own session personId.
      addedByUid: window.AppState?.user?.uid || '',
      addedByPersonId: window.AppState?.driverPersonId || '',
    });
    const nameEl = document.getElementById('jsa-signin-add-name');
    if (nameEl) nameEl.value = '';
    window.showToast?.(`${pending.name} signed in`, 'success');
    refreshDetailSignIns(jsaId);
  } catch (e) {
    console.error('[JSA] sign-in write error:', e);
    window.showToast?.('Could not save sign-in: ' + (e?.message || 'unknown error'), 'error');
  }
}

// TASK-9714 — the print view's signature cells. `r._inkless` is set by
// printTemplateReport (jsaPngHasInk) before this renders; an inkless or missing
// PNG prints "Signature missing" instead of an empty box that reads as signed.
function _jsaPrintSignedAt(v) {
  const d = v?.toDate?.() || (v instanceof Date ? v : null);
  return d && !isNaN(d.getTime()) ? d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : '';
}
function _jsaPrintSigCell(r) {
  const png = String(r?.signaturePngB64 || '');
  if (!r._inkless && /^data:image\/png;base64,/.test(png)) return `<img src="${escapeHtml(png)}" class="sig-img" alt="Signature">`;
  return '<span class="sig-missing">Signature missing</span>';
}
// Blank paper rows after the filled ones — mirrors pdf.js _jsaBlankSignRows.
function _jsaBlankSignRows(slots, filled) {
  const room = Math.max(0, (Number(slots) || 0) - filled);
  return Math.min(room, filled > 0 ? 3 : 6);
}

/**
 * TASK-9714 — does a signature PNG carry ink? Same rule as the server
 * (functions/src/services/signatureInk.js): at least 30 pixels that are visible
 * (alpha >= 16) and not near-white (min channel < 200). Resolves false for a
 * non-PNG or an image that will not load; true if the pixels cannot be read
 * (never hide a signature we could not inspect).
 */
function jsaPngHasInk(dataUrl) {
  return new Promise((resolve) => {
    if (!/^data:image\/png;base64,/i.test(String(dataUrl || ''))) { resolve(false); return; }
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) { resolve(false); return; }
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const d = ctx.getImageData(0, 0, w, h).data;
        let n = 0;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] >= 16 && Math.min(d[i], d[i + 1], d[i + 2]) < 200 && ++n >= 30) { resolve(true); return; }
        }
        resolve(false);
      } catch (_) { resolve(true); }
    };
    img.onerror = () => resolve(false);
    img.src = dataUrl;
  });
}

// TASK-9714 — link / QR signers for the print view, oldest first. Rules let only
// admin/dispatcher/owner/HR read externalSigners, so anyone else gets
// { denied: true } and the print says where the full list is (the PDF).
async function loadExternalSignersForPrint(jsaId) {
  const tenantId = window.AppState?.tenant;
  if (!tenantId || !jsaId) return { rows: [], denied: false };
  try {
    const snap = await getDocs(collection(db, 'tenants', tenantId, 'jsaReports', jsaId, 'externalSigners'));
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => (a.signedAt?.toDate?.()?.getTime?.() || 0) - (b.signedAt?.toDate?.()?.getTime?.() || 0));
    return { rows, denied: false };
  } catch (e) {
    console.warn('[JSA] print: external signers not readable:', e?.code || e?.message);
    return { rows: [], denied: true };
  }
}

function getTemplatePrintHTML(report, signIns, external) {
  const date = report.date?.toDate?.() || new Date(report.date);
  const dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const snap = report.templateSnapshot || { sections: [] };
  const answers = report.templateAnswers || {};
  const rows = Array.isArray(signIns) ? signIns : [];
  const ext = external && Array.isArray(external.rows) ? external : { rows: [], denied: false };
  const extExpected = Number(report.externalSignersCount) || 0;

  const sectionsHTML = (snap.sections || []).map(s => {
    if (s.layout === 'checkbox-grid') {
      const groups = [];
      const byGroup = new Map();
      (s.fields || []).forEach(f => {
        const g = f.group || '';
        if (!byGroup.has(g)) { byGroup.set(g, []); groups.push(g); }
        byGroup.get(g).push(f);
      });
      const inner = groups.map(g => {
        const fields = byGroup.get(g);
        return `<div class="grp">${g ? `<div class="grp-title">${escapeHtml(g)}</div>` : ''}<div class="boxes">${fields.map(f => {
          if (f.type === 'text') return answers[f.id] ? `<div class="box"><strong>${escapeHtml(f.label)}:</strong> ${escapeHtml(answers[f.id])}</div>` : '';
          return `<div class="box"><span class="ck">${answers[f.id] === true ? '☑' : '☐'}</span> ${escapeHtml(f.label)}</div>`;
        }).join('')}</div></div>`;
      }).join('');
      return `<div class="section"><div class="section-title">${escapeHtml(s.title || s.id)}</div>${inner}</div>`;
    }
    if (s.layout === 'signin') {
      // TASK-9714 — Signed date/time column; "Signature missing" for an inkless
      // PNG; a few blank paper rows instead of every slot.
      const slots = Number(s.slots) > 0 ? Math.min(Number(s.slots), 60) : 0;
      const total = rows.length + _jsaBlankSignRows(slots, rows.length);
      const trs = [];
      for (let i = 0; i < total; i++) {
        const r = rows[i];
        trs.push(r
          ? `<tr><td class="nm">${escapeHtml(r.name || '')}</td><td class="sg">${_jsaPrintSigCell(r)}</td><td class="wh">${escapeHtml(_jsaPrintSignedAt(r.signedAt))}</td></tr>`
          : '<tr><td class="nm"></td><td class="sg"></td><td class="wh"></td></tr>');
      }
      return `<div class="section"><div class="section-title">${escapeHtml(s.title || s.id)} (${rows.length})</div>
        <table class="signin"><tr><th>Attendee Name (Print)</th><th>Signature</th><th>Signed</th></tr>${trs.join('')}</table></div>`;
    }
    const items = (s.fields || []).map(f => {
      const disp = templateAnswerDisplay(f, answers[f.id]);
      return disp ? `<div><strong>${escapeHtml(f.label)}:</strong> ${escapeHtml(disp)}</div>` : '';
    }).filter(Boolean).join('');
    return items ? `<div class="section"><div class="section-title">${escapeHtml(s.title || s.id)}</div><div class="header-grid">${items}</div></div>` : '';
  }).join('');

  // TASK-9714 — people who signed through the QR / link (jsaReports/{id}/
  // externalSigners). Display fields only — never email / GPS.
  let externalHTML = '';
  if (ext.denied && extExpected > 0) {
    externalHTML = `<div class="section"><div class="section-title">Signed via link / QR (${extExpected})</div>
      <div class="ext-note">${extExpected} ${extExpected === 1 ? 'person' : 'people'} signed through the link. Your login can't list them here — download the PDF for the full signed record.</div></div>`;
  } else if (ext.rows.length) {
    const trs = ext.rows.map(r => {
      const detail = [r.company, r.role].filter(x => typeof x === 'string' && x.trim()).join(' · ');
      return `<tr><td class="nm">${escapeHtml(r.name || '')}${detail ? `<div class="det">${escapeHtml(detail)}</div>` : ''}</td><td class="sg">${_jsaPrintSigCell(r)}</td><td class="wh">${escapeHtml(_jsaPrintSignedAt(r.signedAt))}</td></tr>`;
    }).join('');
    externalHTML = `<div class="section"><div class="section-title">Signed via link / QR (${ext.rows.length})</div>
      <table class="signin"><tr><th>Name (Print) / Company · Role</th><th>Signature</th><th>Signed</th></tr>${trs}</table></div>`;
  }

  return `<!DOCTYPE html>
<html><head>
<title>${escapeHtml(report.templateName || snap.name || 'JSA')} - ${escapeHtml(report.customerName || report.jobTitle || 'Report')}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:Arial,sans-serif;font-size:11px;color:#000;padding:16px 20px;}
h2{font-size:16px;margin-bottom:2px;text-align:center;text-transform:uppercase;letter-spacing:1px;}
.sub{text-align:center;font-size:9px;color:#666;margin-bottom:10px;}
.header-grid{display:grid;grid-template-columns:1fr 1fr;gap:4px 16px;padding:8px 10px;border:1px solid #ccc;border-radius:4px;background:#fafafa;}
.header-grid div{padding:2px 0;}
.section{margin-bottom:10px;}
.section-title{font-weight:bold;font-size:12px;color:#0077b6;border-bottom:1px solid #0077b6;padding-bottom:2px;margin-bottom:6px;}
.grp{margin-bottom:4px;}
.grp-title{font-weight:bold;font-size:10px;text-transform:uppercase;color:#333;margin:4px 0 2px;}
.boxes{display:grid;grid-template-columns:repeat(3,1fr);gap:2px 10px;}
.box{font-size:10px;}
.ck{font-size:12px;}
table.signin{width:100%;border-collapse:collapse;margin-top:4px;}
table.signin th,table.signin td{border:1px solid #ccc;padding:3px 6px;text-align:left;font-size:10px;}
table.signin th{background:#f0f0f0;}
table.signin td.nm{width:38%;height:28px;}
table.signin td.sg{height:28px;}
table.signin td.wh{width:24%;white-space:nowrap;}
.sig-img{max-height:26px;max-width:100%;}
.sig-missing{color:#b91c1c;font-weight:bold;font-size:10px;}
.det{font-size:9px;color:#555;}
.ext-note{font-size:10px;color:#333;padding:4px 0;}
.footer{text-align:center;font-size:8px;color:#999;margin-top:12px;border-top:1px solid #eee;padding-top:6px;}
.no-print{text-align:center;margin-top:16px;}
@media print{.no-print{display:none!important;}}
@page{margin:10mm;}
</style>
</head><body>
<h2>${escapeHtml(report.templateName || snap.name || 'Job Safety Analysis')}</h2>
<div class="sub">${snap.version ? `Template v${escapeHtml(String(snap.version))} · ` : ''}Filed ${escapeHtml(dateStr)}</div>
<div class="section"><div class="header-grid">
  <div><strong>Customer:</strong> ${escapeHtml(report.customerName || 'N/A')}</div>
  <div><strong>Date:</strong> ${escapeHtml(dateStr)}</div>
  <div><strong>Job:</strong> ${escapeHtml(report.jobTitle || 'N/A')}</div>
  ${report.submittedByPersonName || report.completedByName ? `<div><strong>Filed by:</strong> ${escapeHtml(report.submittedByPersonName || report.completedByName)}</div>` : ''}
</div></div>
${sectionsHTML}
${externalHTML}
<div class="footer">Generated by DispatchDeck on ${new Date().toLocaleDateString()}</div>
<div class="no-print">
  <button onclick="window.print()" style="padding:10px 24px;font-size:14px;cursor:pointer;background:#0077b6;color:#fff;border:none;border-radius:6px;margin-right:8px;">Print / Save as PDF</button>
  <button onclick="window.close()" style="padding:10px 24px;font-size:14px;cursor:pointer;background:#666;color:#fff;border:none;border-radius:6px;">Close</button>
</div>
</body></html>`;
}

async function printTemplateReport() {
  const report = currentDetailReport;
  if (!report || report.formMode !== 'template') return;
  // TASK-9714 — open the window INSIDE the tap (before any await) so a popup
  // blocker does not eat it, then fill it once the signer rows are loaded.
  const w = window.open('', '_blank');
  if (w) w.document.write('<p style="font-family:Arial,sans-serif;padding:20px;">Loading JSA…</p>');
  let rows = currentDetailSignIns;
  if (!Array.isArray(rows)) {
    try { rows = await loadSignInsForDetail(report.id); } catch (_) { rows = []; }
    if (currentDetailReport === report) currentDetailSignIns = rows;
  }
  const external = await loadExternalSignersForPrint(report.id);
  const all = [...rows, ...external.rows];
  const ink = await Promise.all(all.map(r => jsaPngHasInk(r.signaturePngB64)));
  const printRows = rows.map((r, i) => ({ ...r, _inkless: !ink[i] }));
  const extRows = external.rows.map((r, i) => ({ ...r, _inkless: !ink[rows.length + i] }));
  const html = getTemplatePrintHTML(report, printRows, { rows: extRows, denied: external.denied });
  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
  }
}

// Edit a filed template report: restore answers into the form. formMode stays
// 'template' and the report's OWN snapshot is what renders — never the live one.
function restoreTemplateEditState(report) {
  JsaState.formMode = 'template';
  JsaState.activeTemplate = null;
  JsaState.templateSnapshot = report.templateSnapshot || { name: report.templateName, version: report.templateVersion, sections: [] };
  JsaState.templateChecks = new Set();
  JsaState.emergencyFacility = null;
  const answers = report.templateAnswers || {};
  (JsaState.templateSnapshot.sections || []).forEach(s => (s.fields || []).forEach(f => {
    if (s.layout === 'checkbox-grid' && f.type === 'checkbox' && answers[f.id] === true) JsaState.templateChecks.add(f.id);
    if (f.type === 'places' && answers[f.id] && typeof answers[f.id] === 'object' && answers[f.id].source !== 'manual') JsaState.emergencyFacility = answers[f.id];
  }));
}

function prefillTemplateFormFromReport(report) {
  const el = (id) => document.getElementById(id);
  const answers = report.templateAnswers || {};
  if (el('jsa-date')) {
    const d = report.date?.toDate?.() || new Date(report.date);
    el('jsa-date').value = window.RecordDate.ymd(d, window.RecordDate.zone()); // TASK-9923
  }
  if (el('jsa-customer') && report.customerId) {
    _jsaSetCustomer(el('jsa-customer'), report.customerId);
    populateJobDropdown(report.customerId);
  }
  if (el('jsa-job') && report.jobId) el('jsa-job').value = report.jobId;
  (JsaState.templateSnapshot?.sections || []).forEach(s => (s.fields || []).forEach(f => {
    const v = answers[f.id];
    const input = el(tfId(f.id));
    switch (f.type) {
      case 'text': case 'longtext': case 'select': case 'date': case 'number':
        if (input && v !== undefined && v !== null) input.value = String(v);
        break;
      case 'checkbox':
        if (s.layout === 'checkbox-grid') {
          const chip = document.querySelector(`.jsa-hazard-chip[data-id="${CSS.escape(String(f.id))}"]`);
          if (chip) chip.classList.toggle('selected', v === true);
        } else if (input) {
          input.checked = v === true;
        }
        break;
      case 'person': case 'vehicle':
        if (input && v && v.id) input.value = v.id;
        break;
      case 'places':
        if (v && typeof v === 'object' && v.source === 'manual' && el('jsa-facility-manual')) el('jsa-facility-manual').value = v.name || '';
        if (JsaState.emergencyFacility && JsaState.emergencyFacility.name && el('jsa-selected-facility')) {
          el('jsa-selected-facility').innerHTML = `<div class="jsa-facility-selected"><strong>${escapeHtml(JsaState.emergencyFacility.name)}</strong>${JsaState.emergencyFacility.address ? `<div style="font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(JsaState.emergencyFacility.address)}</div>` : ''}</div>`;
        }
        break;
      default:
        break;
    }
  }));
}

function renderFormBody() {
  const container = document.getElementById('jsa-form-body');
  if (!container) return;

  if (JsaState.formMode === 'xto') {
    container.innerHTML = getXTOFormHTML();
  } else if (JsaState.formMode === 'template') {
    container.innerHTML = getTemplateFormHTML(JsaState.templateSnapshot, {
      people: JsaState.people,
      trucks: JsaState.trucks,
      editing: !!JsaState.editingJsaId,
    });
  } else {
    container.innerHTML = getTITSFormHTML();
  }
}

function switchFormMode(mode, templateId, byUser) {
  // TASK-9511 — a click on the toggle is a choice; the sole-template default
  // must never override it (set before the "already on this mode" early return).
  if (byUser === true) JsaState.modeChosenByUser = true;
  // TASK-4839 — 'template' needs the flag on AND a live active template; a stale
  // button (template deactivated mid-session) falls back to doing nothing.
  let template = null;
  if (mode === 'template') {
    template = getActiveJsaTemplates().find(t => t.id === templateId) || null;
    if (!template) { window.showToast?.('That template is no longer available', 'error'); return; }
    if (JsaState.formMode === 'template' && JsaState.activeTemplate?.id === templateId) return;
  } else if (JsaState.formMode === mode) {
    return;
  }
  JsaState.formMode = mode;
  JsaState.activeTemplate = template;
  JsaState.templateSnapshot = template
    ? { id: template.id, name: template.name, version: template.version, sections: JSON.parse(JSON.stringify(template.sections || [])) }
    : null;
  JsaState.templateChecks = new Set();

  // Update toggle buttons
  document.querySelectorAll('.jsa-mode-btn').forEach(btn => {
    const on = mode === 'template'
      ? (btn.dataset.mode === 'template' && btn.dataset.templateId === templateId)
      : btn.dataset.mode === mode;
    btn.classList.toggle('active', on);
  });

  // Reset mode-specific state
  JsaState.selectedTopics.clear();
  JsaState.emergencyFacility = null;
  JsaState.facilityResults = [];
  JsaState.userLocation = null;
  _jsaFacilitySearchSeq++; // TASK-9996 — a search still in flight from the last form must not paint this one
  JsaState.lsraChecks = {};
  JsaState.xtoSignatures = {
    issuer: { name: '', signature: null },
    receiver: { name: '', signature: null }
  };

  // Re-render form body
  renderFormBody();

  // If switching back to TITS, re-populate dropdowns and reset form
  if (mode === 'tits') {
    initTITSForm();
  } else if (mode === 'template') {
    // TASK-4839 — the template sheet shares the fixed job header, so it gets the
    // same dropdown population + TASK-794 job prefill the Standard form gets.
    JsaState.crewSignatures = {};
    initTemplateForm();
  }
}

function initTITSForm() {
  // Set today's date
  const dateInput = document.getElementById('jsa-date');
  if (dateInput) dateInput.value = window.RecordDate.today(); // TASK-9923

  // Populate dropdowns
  populateCustomerDropdown();
  populateJobDropdown('');
  populateSupervisorDropdown();

  // TASK-794: pre-link to the job this JSA was launched from (dispatch card /
  // job-board "Start JSA"). Set the customer first (so populateJobDropdown keeps
  // the job in range), then select the job + auto-populate its crew. The save
  // path honors JsaState.prefillJobDayId so multi-day jobs link to the right day.
  if (JsaState.prefillJobId) {
    const job = JsaState.jobs.find(j => j.id === JsaState.prefillJobId);
    if (job) {
      const custSel = document.getElementById('jsa-customer');
      if (custSel && job.customerId) {
        _jsaSetCustomer(custSel, job.customerId);
        populateJobDropdown(job.customerId);
      }
      const jobSel = document.getElementById('jsa-job');
      if (jobSel) {
        // The dedupe in populateJobDropdown may drop this exact job in favor of a
        // same-title sibling; ensure the option exists before selecting it.
        if (!Array.from(jobSel.options).some(o => o.value === job.id)) {
          const opt = document.createElement('option');
          opt.value = job.id;
          opt.textContent = job.title || 'Untitled Job';
          opt.dataset.title = job.title || 'Untitled Job';
          opt.dataset.customerId = job.customerId || '';
          jobSel.appendChild(opt);
        }
        jobSel.value = job.id;
        onJobSelect();
      }
    }
  }

  // Reset fields
  const el = (id) => document.getElementById(id);
  if (el('jsa-person-responsible')) el('jsa-person-responsible').value = '';
  if (el('jsa-total-hours')) el('jsa-total-hours').value = '8';
  if (el('jsa-permit-required')) el('jsa-permit-required').checked = false;
  if (el('jsa-hazard-other')) el('jsa-hazard-other').value = '';
  if (el('jsa-hazard-other-group')) el('jsa-hazard-other-group').classList.add('hidden');
  if (el('jsa-ppe-required')) el('jsa-ppe-required').checked = true;
  if (el('jsa-ppe-section')) el('jsa-ppe-section').style.display = '';
  if (el('jsa-ppe-other')) el('jsa-ppe-other').value = '';
  if (el('jsa-ppe-other-group')) el('jsa-ppe-other-group').classList.add('hidden');
  if (el('jsa-life-saving-rule')) el('jsa-life-saving-rule').value = '';
  if (el('jsa-tailgate-items')) el('jsa-tailgate-items').value = '';
  if (el('jsa-one-call')) el('jsa-one-call').checked = false;
  if (el('jsa-one-call-number')) el('jsa-one-call-number').value = '';
  if (el('jsa-work-layout')) el('jsa-work-layout').value = '';
  if (el('jsa-muster-point')) el('jsa-muster-point').value = '';
  if (el('jsa-add-crew-name')) el('jsa-add-crew-name').value = '';
  if (el('jsa-facility-manual')) el('jsa-facility-manual').value = '';

  // Reset chip selections
  document.querySelectorAll('.jsa-hazard-chip').forEach(c => c.classList.remove('selected'));
  document.querySelectorAll('.jsa-ppe-chip').forEach(c => c.classList.remove('selected'));
  document.querySelectorAll('.jsa-topic-chip').forEach(c => c.classList.remove('selected'));

  // Pre-select default PPE
  const defaultPPE = ['hard_hat', 'steel_toed_boots', 'glasses', 'gas_monitor'];
  defaultPPE.forEach(id => {
    JsaState.selectedPPE.add(id);
    const chip = document.querySelector(`.jsa-ppe-chip[data-id="${id}"]`);
    if (chip) chip.classList.add('selected');
  });

  // Add one empty task row
  JsaState.taskRows = [{ task: '', potentialHazards: '', controlMeasures: '', holdPoint: false }];
  renderTaskRows();

  // Clear crew list
  const crewList = document.getElementById('jsa-crew-list');
  if (crewList) crewList.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;">Select a job to auto-populate crew members.</p>';
}

async function generateXTOJsaNumber() {
  const el = document.getElementById('xto-jsa-number');
  if (!el) return;
  // TASK-9923 — the number carries the company's day, not the UTC day.
  const todayYmd = window.RecordDate.today();
  const dateStr = todayYmd.replace(/-/g, '');
  // Count today's existing reports
  const todayReports = JsaState.reports.filter(r => {
    const d = r.date?.toDate?.() || new Date(r.date);
    return window.RecordDate.ymd(d, window.RecordDate.zone()) === todayYmd;
  });
  const num = String(todayReports.length + 1).padStart(3, '0');
  el.value = `JSA-${dateStr}-${num}`;
}

// ============================================
// SHOW NEW JSA FORM
// ============================================
function showNewJSA(prefillJobId, prefillJobDayId, opts) {
  // TASK-398: new form generation — invalidate/cancel any in-flight edit prefill.
  bumpJsaFormGen();
  // TASK-9511 — where Cancel goes back to (the job board's "Add another JSA"
  // passes the job it came from). Anything else: Cancel just closes, as before.
  const rt = opts && opts.returnTo;
  JsaState.returnTo = (rt && typeof rt.jobId === 'string' && rt.jobId)
    ? {
      hash: typeof rt.hash === 'string' ? rt.hash : '',
      jobId: rt.jobId,
      jobDayId: typeof rt.jobDayId === 'string' ? rt.jobDayId : '',
      // TASK-10011 — which job screen to reopen: the office Job Details
      // ('dispatch') or the crew's Job Board job (default, unchanged).
      surface: rt.surface === 'dispatch' ? 'dispatch' : 'jobboard',
    }
    : null;
  JsaState.modeChosenByUser = false;
  JsaState.newFormTouched = false;
  JsaState.prefillApplied = false; // TASK-10011
  JsaState.prefillJobDay = null;   // TASK-10011 COD r1 — the prefill day doc (may be a future day)
  JsaState.prefillDayInvalid = false; // TASK-10011 COD r3 — the read showed the day missing / another job's
  // TASK-794: optional pre-link to a specific job (+ jobDay for multi-day jobs)
  // when launched from a dispatch card / job-board "Start JSA" affordance.
  JsaState.prefillJobId = (prefillJobId && prefillJobId !== 'none') ? prefillJobId : null;
  JsaState.prefillJobDayId = prefillJobDayId || null;
  // Reset state
  JsaState.crewSignatures = {};
  JsaState.taskRows = [];
  JsaState.selectedHazards.clear();
  JsaState.selectedPPE.clear();
  JsaState.selectedTopics.clear();
  JsaState.emergencyFacility = null;
  JsaState.facilityResults = [];
  JsaState.userLocation = null;
  _jsaFacilitySearchSeq++; // TASK-9996 — a search still in flight from the last form must not paint this one
  JsaState.lsraChecks = {};
  JsaState.hazardCategories = new Set();
  JsaState.lessonsLearned = '';
  JsaState.xtoSignatures = { issuer: { name: '', signature: null }, receiver: { name: '', signature: null } };
  JsaState.editingJsaId = null;
  JsaState.editingReport = null;
  JsaState.formMode = 'tits';
  JsaState.activeTemplate = null;
  JsaState.templateSnapshot = null;
  JsaState.templateChecks = new Set();

  const modal = document.getElementById('modal-jsa');
  if (!modal) return;

  // Mode toggle: Standard (TITS-style) vs Oilfield (XTO/LSRA-style) — available to all tenants.
  // TASK-4839: plus one button per active tenant template when settings.enableJsaTemplates is on.
  const toggleContainer = document.getElementById('jsa-mode-toggle-container');
  if (toggleContainer) {
    toggleContainer.innerHTML = getModeToggleHTML('tits', null, getActiveJsaTemplates());
  }
  setJsaModalTitle(false);

  // Render form body (TITS by default)
  renderFormBody();

  // Initialize TITS form fields
  initTITSForm();

  // TASK-9511 — typing into the form is a choice too: once the user has entered
  // anything, a late template snapshot must never swap the body out from under
  // them. One capturing listener per form element (it survives body re-renders).
  const form = document.getElementById('form-jsa');
  if (form && !form.dataset.t9511Touch) {
    form.dataset.t9511Touch = '1';
    const markTouched = (e) => {
      if (e && e.isTrusted === false) return;
      if (!JsaState.editingJsaId) JsaState.newFormTouched = true;
    };
    form.addEventListener('input', markTouched, true);
    form.addEventListener('change', markTouched, true);
  }
  // 1996 COD (data loss): hazard/PPE chips are buttons and signatures are a
  // canvas — neither fires input/change. Any press inside the modal counts as
  // touching it, so a late template load can't wipe picks or signatures.
  if (modal && !modal.dataset.t9511Press) {
    modal.dataset.t9511Press = '1';
    const markPressed = (e) => {
      if (e && e.isTrusted === false) return;
      if (!JsaState.editingJsaId) JsaState.newFormTouched = true;
    };
    modal.addEventListener('pointerdown', markPressed, true);
    modal.addEventListener('click', markPressed, true);
    modal.addEventListener('keydown', markPressed, true);
  }

  // Show modal
  modal.classList.remove('hidden');

  // TASK-9511 — exactly one active template → open on it (templates already loaded).
  maybeDefaultToSoleTemplate();
}

// ============================================
// POPULATE DROPDOWNS
// ============================================
// keepId = the customer a report/job ALREADY points at — kept even if it is a
// vendor, so editing an old JSA never silently blanks its customer.
function populateCustomerDropdown(keepId) {
  const select = document.getElementById('jsa-customer');
  if (!select) return;

  const hint = document.getElementById('jsa-customer-hint');
  // P0 2026-10-06 — customers only (QBO vendors share the collection).
  const customers = window.BillableCustomer.billableCustomers(JsaState.customers || [], keepId);

  if (customers.length === 0) {
    select.innerHTML = '<option value="">— None —</option>';
    if (hint) hint.style.display = 'block';
    return;
  }

  if (hint) hint.style.display = 'none';
  // TASK-9548 slice 3 — Shop/Office under "Internal — not billed", then
  // "Customers" A–Z. The builder applies the same vendor rule + keepId.
  select.innerHTML = '<option value="">— None —</option>' +
    window.PickerGroups.customerOptionsHtml(JsaState.customers || [], {
      nameOf: c => c.name || '',
      keepId,
      optionAttrs: c => ` data-name="${escapeHtml(c.name)}"`,
    });
}

// P0 2026-10-06 — set #jsa-customer to an id, re-populating with it kept when the
// filtered list does not carry it (a JSA/job already on a vendor doc).
function _jsaSetCustomer(sel, id) {
  if (!sel) return;
  if (id && ![...sel.options].some(o => o.value === id)) populateCustomerDropdown(id);
  sel.value = id;
}

function populateJobDropdown(customerId) {
  const select = document.getElementById('jsa-job');
  if (!select) return;

  // Filter to customer if selected, otherwise all non-finished jobs
  const todaysJobIds = new Set(JsaState.jobDays.map(jd => jd.jobId));
  const custJobs = JsaState.jobs.filter(j =>
    !customerId || j.customerId === customerId
  );

  // Bucket by relevance (same pattern as Clock module)
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today); todayEnd.setHours(23, 59, 59, 999);

  const todayJobs = [];
  const rangeJobs = [];
  const otherJobs = [];

  for (const j of custJobs) {
    if (todaysJobIds.has(j.id)) {
      todayJobs.push(j);
    } else {
      const start = j.startDate?.toDate?.() || (j.startDate ? new Date(j.startDate) : null);
      const end = j.endDate?.toDate?.() || (j.endDate ? new Date(j.endDate) : start);
      if (start) {
        const s = new Date(start); s.setHours(0, 0, 0, 0);
        const e = new Date(end); e.setHours(23, 59, 59, 999);
        s.setDate(s.getDate() - 1);
        if (today <= e && todayEnd >= s) { rangeJobs.push(j); continue; }
      }
      otherJobs.push(j);
    }
  }

  // Sort other by most recent, cap at 10
  otherJobs.sort((a, b) => {
    const da = a.startDate?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
    const db2 = b.startDate?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
    return db2 - da;
  });

  const relevant = [...todayJobs, ...rangeJobs, ...otherJobs.slice(0, 10)];

  // Deduplicate by title (keep first occurrence — today's version wins)
  const seen = new Set();
  const deduped = relevant.filter(j => {
    const key = (j.title || '').trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // TASK-9548 slice 2 — real <optgroup> headers ("Today" / "In progress" /
  // "Recent") via window.PickerGroups (Pitfall #1: never a ?v= import) replace
  // the fake disabled "── Today ──" / "── Other ──" divider rows, which iOS
  // Safari's picker showed as greyed-out choices. A–Z inside each group, as
  // before. Values are still the job ids; the two placeholders ('' / 'none')
  // keep their values, so onJobSelect / resolveJobBinding / the edit-path
  // `el('jsa-job').value = report.jobId` are untouched.
  const byTitle = (a, b) => (a.title || '').localeCompare(b.title || '');
  const rangeIds = new Set(rangeJobs.map(j => j.id));
  const groups = {
    today: deduped.filter(j => todaysJobIds.has(j.id)).sort(byTitle),
    range: deduped.filter(j => !todaysJobIds.has(j.id) && rangeIds.has(j.id)).sort(byTitle),
    other: deduped.filter(j => !todaysJobIds.has(j.id) && !rangeIds.has(j.id)).sort(byTitle),
  };

  select.innerHTML = window.PickerGroups.jobOptionsHtml(groups, {
    placeholders: [{ value: '', label: 'Select job' }, { value: 'none', label: 'No job associated' }],
    nameOf: j => j.title || 'Untitled Job',
    optionAttrs: j => ` data-title="${escapeHtml(j.title || 'Untitled Job')}" data-customer-id="${escapeHtml(j.customerId || '')}"`,
  });
}

function onCustomerSelect() {
  const customerSelect = document.getElementById('jsa-customer');
  const customerId = customerSelect?.value || '';

  // Re-populate job dropdown (all today's jobs if no customer, filtered if customer selected)
  populateJobDropdown(customerId);

  // Reset crew list since job changed
  JsaState.crewSignatures = {};
  const crewList = document.getElementById('jsa-crew-list');
  if (crewList) crewList.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;">Select a job to auto-populate crew members.</p>';
}

function populateSupervisorDropdown() {
  const select = document.getElementById('jsa-supervisor');
  if (!select) return;

  const activePeople = JsaState.people.filter(p => p.status !== 'inactive');
  select.innerHTML = '<option value="">Select supervisor</option>' +
    activePeople.map(p => `<option value="${p.id}" data-name="${escapeHtml(p.name)}">${escapeHtml(p.name)}</option>`).join('');
}

// ============================================
// JOB SELECT HANDLER
// ============================================
async function onJobSelect() {
  const select = document.getElementById('jsa-job');
  const jobId = select?.value;
  if (!jobId || jobId === 'none') {
    // "No job associated" or empty — clear crew
    JsaState.crewSignatures = {};
    const crewList = document.getElementById('jsa-crew-list');
    if (crewList) crewList.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;">No job selected. Add crew members manually below.</p>';
    return;
  }

  // TASK-10011 — a form opened FROM a job day uses THAT day's crew (the office
  // starts JSAs ahead of time; JsaState.jobDays only holds today's). Else
  // today's day for the job, as before.
  const wantDayId = JsaState.prefillJobId === jobId ? JsaState.prefillJobDayId : null;
  const jobDay = (wantDayId && JsaState.jobDays.find(jd => jd.id === wantDayId && jd.jobId === jobId))
    || JsaState.jobDays.find(jd => jd.jobId === jobId);
  if (jobDay && (!wantDayId || jobDay.id === wantDayId)) {
    if (wantDayId) adoptPrefillJobDay(jobDay);
    populateCrewFromJob(jobDay);
    return;
  }
  if (wantDayId) {
    const gen = JsaState.jsaFormGen;
    const tenantId = window.AppState?.tenant;
    try {
      const snap = tenantId ? await getDoc(doc(db, 'tenants', tenantId, 'jobDays', wantDayId)) : null;
      // Still the same form, same job selected?
      if (gen !== JsaState.jsaFormGen || document.getElementById('jsa-job')?.value !== jobId) return;
      if (snap && snap.exists() && snap.data().jobId === jobId) {
        const day = { id: snap.id, ...snap.data() };
        adoptPrefillJobDay(day);
        populateCrewFromJob(day);
        return;
      }
      // COD r3 — the requested day is gone or belongs to another job: never
      // bind it (resolveJobBinding's explicit-id fallback reads this flag).
      JsaState.prefillDayInvalid = true;
    } catch (e) {
      console.warn('[JSA] prefill day read failed:', e?.code || e?.message || e);
      // COD r3 — same guards as the success path: a newer form, or the user
      // switched job (or to "No job") while the read was pending → hands off.
      if (gen !== JsaState.jsaFormGen || document.getElementById('jsa-job')?.value !== jobId) return;
    }
  }
  if (jobDay) populateCrewFromJob(jobDay);
}

// TASK-10011 COD r1 — a NEW form opened from a specific job day (the office
// pre-starting tomorrow's JSA) must (a) SAVE against that day, not today's or
// none — resolveJobBinding reads JsaState.prefillJobDay — and (b) be dated that
// day on the company calendar — unless the user already picked another date
// (the field holds something other than the form's default, today). Not keyed
// on newFormTouched: tapping a mode button counts as "touched" and re-renders
// the body with today's date, which must still become the job's day.
function adoptPrefillJobDay(day) {
  if (!day || !day.id || JsaState.editingJsaId) return;
  JsaState.prefillJobDay = day;
  const dateStr = jsaDayYmd(day.date);
  const el = document.getElementById('jsa-date');
  if (!el || !dateStr) return;
  const today = window.RecordDate?.today ? window.RecordDate.today() : '';
  if (el.value && el.value !== today && el.value !== dateStr) return;
  el.value = dateStr;
}

// 'YYYY-MM-DD' of a jobDay.date (Timestamp | Date | ms | string) on the company
// calendar ('' when unreadable). jobDay dates are anchored to noon local.
function jsaDayYmd(v) {
  let ms = NaN;
  if (v && typeof v.toMillis === 'function') ms = v.toMillis();
  else if (v && typeof v.toDate === 'function') ms = v.toDate().getTime();
  else if (v instanceof Date) ms = v.getTime();
  else if (typeof v === 'number') ms = v;
  else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  else if (typeof v === 'string') ms = Date.parse(v);
  if (!Number.isFinite(ms)) return '';
  return window.RecordDate?.ymd ? window.RecordDate.ymd(ms, window.RecordDate.zone()) : '';
}

function populateCrewFromJob(jobDay) {
  const crewList = document.getElementById('jsa-crew-list');
  if (!crewList) return;

  // Combine drivers and helpers
  const crewIds = [...(jobDay.drivers || []), ...(jobDay.helpers || [])];

  if (crewIds.length === 0) {
    crewList.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;">No crew assigned to this job. Add members manually below.</p>';
    return;
  }

  // Match IDs to people (dual lookup)
  const crewMembers = crewIds.map(ref => {
    const person = JsaState.people.find(p => p.id === ref || p.name === ref);
    return person ? { id: person.id, name: person.name } : { id: ref, name: ref };
  });

  // Initialize signatures for each crew member
  crewMembers.forEach(m => {
    if (!JsaState.crewSignatures[m.id]) {
      JsaState.crewSignatures[m.id] = { name: m.name, signature: null, acknowledgedAt: null };
    }
  });

  renderCrewList();
}

// ============================================
// CREW SIGNATURE MANAGEMENT
// ============================================
function renderCrewList() {
  const crewList = document.getElementById('jsa-crew-list');
  if (!crewList) return;

  const entries = Object.entries(JsaState.crewSignatures);
  if (entries.length === 0) {
    crewList.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;">No crew members added yet.</p>';
    return;
  }

  crewList.innerHTML = entries.map(([personId, data]) => {
    const hasSig = !!data.signature;
    return `
      <div class="jsa-crew-member ${hasSig ? 'signed' : ''}" style="flex-direction:column;align-items:stretch;gap:4px;padding:10px 12px;">
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div class="jsa-crew-info">
            <span class="material-icons" style="font-size:20px;color:${hasSig ? 'var(--action-success)' : 'var(--text-muted)'}">
              ${hasSig ? 'check_circle' : 'radio_button_unchecked'}
            </span>
            <span class="jsa-crew-name" style="font-weight:600;">${escapeHtml(data.name)}</span>
          </div>
          <div class="jsa-crew-actions">
            ${hasSig
              ? `<button type="button" class="btn btn-sm btn-secondary" onclick="window.JSAModule.initCrewSignature('${personId}')" title="Re-sign">
                  <span class="material-icons" style="font-size:14px;">draw</span> Re-sign
                </button>`
              : `<button type="button" class="btn btn-sm btn-primary" onclick="window.JSAModule.initCrewSignature('${personId}')">
                  <span class="material-icons" style="font-size:14px;">draw</span> Sign
                </button>`
            }
            <button type="button" class="btn-icon" onclick="window.JSAModule.removeCrewMember('${personId}')" title="Remove">
              <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">close</span>
            </button>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:12px;margin-top:2px;">
          <span style="font-size:0.8rem;color:var(--text-secondary);white-space:nowrap;">Print Name:</span>
          <div style="flex:1;border-bottom:1px solid var(--border-light);padding-bottom:2px;font-size:0.85rem;min-height:18px;">
            ${escapeHtml(data.name)}
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:12px;">
          <span style="font-size:0.8rem;color:var(--text-secondary);white-space:nowrap;">Signature:</span>
          <div style="flex:1;border-bottom:1px solid var(--border-light);min-height:40px;display:flex;align-items:flex-end;padding-bottom:2px;">
            ${hasSig ? `<img src="${data.signature}" class="jsa-signature-mini" alt="Signature" style="max-height:36px;">` : ''}
          </div>
        </div>
      </div>
    `;
  }).join('');
}

function addManualCrew() {
  const nameInput = document.getElementById('jsa-add-crew-name');
  const name = nameInput?.value?.trim();
  if (!name) {
    window.showToast?.('Enter a crew member name', 'error');
    return;
  }

  const manualId = 'manual_' + Date.now();
  JsaState.crewSignatures[manualId] = { name, signature: null, acknowledgedAt: null };
  nameInput.value = '';
  renderCrewList();
}

function removeCrewMember(personId) {
  delete JsaState.crewSignatures[personId];
  renderCrewList();
}

// ============================================
// SIGNATURE PAD (for crew)
// ============================================
let jsaSigCanvas = null;
let jsaSigCtx = null;
let jsaSigDrawing = false;
let jsaSigHasDrawn = false;
let jsaSigCurrentPerson = null;

function initCrewSignature(personId) {
  jsaSigCurrentPerson = personId;
  const data = JsaState.crewSignatures[personId];
  if (!data) return;

  document.getElementById('jsa-sig-title').textContent = `Sign: ${data.name}`;

  const modal = document.getElementById('modal-jsa-signature');
  modal.classList.remove('hidden');

  // Initialize canvas after visible
  setTimeout(() => {
    jsaSigCanvas = document.getElementById('jsa-signature-canvas');
    if (!jsaSigCanvas) return;
    jsaSigCtx = jsaSigCanvas.getContext('2d');
    jsaSigCtx.strokeStyle = '#1a237e';
    jsaSigCtx.lineWidth = 2;
    jsaSigCtx.lineCap = 'round';
    jsaSigCtx.lineJoin = 'round';
    jsaSigCtx.fillStyle = '#ffffff';
    jsaSigCtx.fillRect(0, 0, jsaSigCanvas.width, jsaSigCanvas.height);
    jsaSigHasDrawn = false;
    jsaSigDrawing = false;
    bindJsaSignaturePad(); // TASK-9714
  }, 100);
}

// TASK-9714 — ONE pad binding for the crew, Oilfield issuer/receiver and
// re-sign pads (was three copies).
//  * The buffer stays a FIXED 400x150 (TASK-9147 — see the <canvas> comment and
//    tests/jsa-signature-canvas-size.unit.cjs); CSS stretches it to the
//    container, so pointer positions are SCALED into buffer pixels. The old
//    code drew at raw CSS offsets on a canvas that overflowed a 390px phone.
//  * Pointer events + pointer capture: a stroke that leaves the pad keeps
//    drawing until the finger/mouse lifts, instead of stopping at the edge.
//  * A mouse stroke that STARTED on the pad and ends on the dark backdrop makes
//    the browser fire a click on the modal (the common ancestor) — that read as
//    "tap outside to close" and the close guard asked "Discard your changes?".
//    The click that ends a stroke is swallowed (window capture runs before the
//    app.js guard's document capture listener).
let jsaSigStrokeEndedAt = 0;
function _jsaSigPoint(e) {
  const r = jsaSigCanvas.getBoundingClientRect();
  const sx = r.width ? jsaSigCanvas.width / r.width : 1;
  const sy = r.height ? jsaSigCanvas.height / r.height : 1;
  return { x: (e.clientX - r.left) * sx, y: (e.clientY - r.top) * sy };
}
function bindJsaSignaturePad() {
  const c = jsaSigCanvas;
  if (!c || !jsaSigCtx) return;
  c.onmousedown = c.onmousemove = c.onmouseup = c.onmouseout = c.onmouseleave = null;
  c.ontouchstart = c.ontouchmove = c.ontouchend = null;
  c.onpointerdown = (e) => {
    if (e.button !== undefined && e.button > 0) return;
    e.preventDefault();
    try { c.setPointerCapture(e.pointerId); } catch (_) {}
    jsaSigDrawing = true;
    jsaSigHasDrawn = true;
    const p = _jsaSigPoint(e);
    jsaSigCtx.beginPath();
    jsaSigCtx.moveTo(p.x, p.y);
    jsaSigCtx.lineTo(p.x + 0.01, p.y); // a tap leaves a dot
    jsaSigCtx.stroke();
  };
  c.onpointermove = (e) => {
    if (!jsaSigDrawing) return;
    e.preventDefault();
    const p = _jsaSigPoint(e);
    jsaSigCtx.lineTo(p.x, p.y);
    jsaSigCtx.stroke();
  };
  const end = () => {
    if (jsaSigDrawing) jsaSigStrokeEndedAt = Date.now();
    jsaSigDrawing = false;
  };
  c.onpointerup = end;
  c.onpointercancel = end;
  c.onlostpointercapture = end;
  if (!bindJsaSignaturePad._clickGuard) {
    bindJsaSignaturePad._clickGuard = true;
    window.addEventListener('click', (e) => {
      if (!jsaSigStrokeEndedAt || Date.now() - jsaSigStrokeEndedAt > 400) return;
      jsaSigStrokeEndedAt = 0;
      const modal = document.getElementById('modal-jsa-signature');
      if (!modal || modal.classList.contains('hidden')) return;
      const t = e.target;
      if (t === jsaSigCanvas || (t && typeof t.closest === 'function' && t.closest('.modal-content'))) return;
      e.stopPropagation();
      e.preventDefault();
    }, true);
  }
}

function clearJSASignature() {
  if (!jsaSigCanvas || !jsaSigCtx) return;
  jsaSigCtx.fillStyle = '#ffffff';
  jsaSigCtx.fillRect(0, 0, jsaSigCanvas.width, jsaSigCanvas.height);
  jsaSigHasDrawn = false;
}

async function confirmSignature() {
  if (!jsaSigHasDrawn || !jsaSigCurrentPerson) {
    window.showToast?.('Please sign before confirming', 'error');
    return;
  }

  // Check canvas has actual drawing (not just white)
  const imageData = jsaSigCtx.getImageData(0, 0, jsaSigCanvas.width, jsaSigCanvas.height);
  const pixels = imageData.data;
  let hasColor = false;
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i] < 250 || pixels[i + 1] < 250 || pixels[i + 2] < 250) {
      hasColor = true;
      break;
    }
  }
  if (!hasColor) {
    window.showToast?.('Please draw your signature', 'error');
    return;
  }

  const sigData = jsaSigCanvas.toDataURL('image/png');

  // Handle XTO signatures vs crew signatures
  if (jsaSigCurrentPerson === 'xto_issuer') {
    JsaState.xtoSignatures.issuer.signature = sigData;
    const statusEl = document.getElementById('xto-issuer-status');
    if (statusEl) statusEl.textContent = 'Signed';
    const card = document.getElementById('xto-sig-issuer');
    if (card) card.classList.add('signed');
    closeSignatureOverlay();
    window.showToast?.('Issuer signature captured', 'success');
    return;
  }
  if (jsaSigCurrentPerson === 'xto_receiver') {
    JsaState.xtoSignatures.receiver.signature = sigData;
    const statusEl = document.getElementById('xto-receiver-status');
    if (statusEl) statusEl.textContent = 'Signed';
    const card = document.getElementById('xto-sig-receiver');
    if (card) card.classList.add('signed');
    closeSignatureOverlay();
    window.showToast?.('Receiver signature captured', 'success');
    return;
  }

  // Handle XTO signature UPDATE on existing saved report
  if (jsaSigCurrentPerson?.startsWith('xto_update_')) {
    const role = jsaSigCurrentPerson.replace('xto_update_', '');
    const tenantId = window.AppState?.tenant;
    if (!tenantId || !currentDetailJsaId) { closeSignatureOverlay(); return; }
    const field = role === 'issuer' ? 'jsaIssuer' : 'jsaReceiver';
    closeSignatureOverlay();
    try {
      // TASK-9371 S2 — the second jsaReports updater; same revision batch as
      // saveJSA (the row records the OLD signature's hash, never the image).
      const priorName = currentDetailReport?.[field]?.name || '';
      const res = await commitJsaRevision(tenantId, currentDetailJsaId, currentDetailReport, {
        [field]: { name: priorName, signature: sigData },
      });
      // Update local report cache so a second signature in the same view
      // carries the right baseline (revisionCount / updatedAt moved).
      if (currentDetailReport && !res.noop) {
        currentDetailReport[field] = { name: priorName, signature: sigData };
        currentDetailReport.revisionCount = res.revisionNumber;
        currentDetailReport.lastRevisionId = res.revisionId;
        // COD r5: signaturesStale is NOT copied from a client guess — the
        // server flag arrives through the report listener.
        currentDetailReport.updatedAt = res.revisedAt;
      }
      // Refresh detail view
      viewJSADetail(currentDetailJsaId);
      window.showToast?.(`${role === 'issuer' ? 'Issuer' : 'Receiver'} signature saved`, 'success');
    } catch (err) {
      console.error('[JSA] Signature update error:', err);
      window.showToast?.((err instanceof JsaConflictError ? '' : 'Error saving signature: ') + err.message, 'error');
    }
    return;
  }

  // TASK-4839 — detail-view sign-in on a FILED template sheet: one signIns
  // subcollection doc, never a field on the parent.
  if (jsaSigCurrentPerson === '__signin_add') {
    closeSignatureOverlay();
    await saveDetailSignIn(sigData);
    return;
  }
  // TASK-10024 left-open #1 — a non-filer signs THEMSELVES in on a filed
  // template sheet: server-checked (signJsaCrewLine template branch).
  if (jsaSigCurrentPerson === '__signin_self') {
    closeSignatureOverlay();
    await _saveTemplateSelfSignIn(sigData);
    return;
  }

  // TASK-10024 — a crew line on a FILED Standard JSA: server-checked write.
  if (jsaSigCurrentPerson === '__detail_crew') {
    closeSignatureOverlay();
    await _saveDetailCrewSign(sigData);
    return;
  }

  // Crew signature
  JsaState.crewSignatures[jsaSigCurrentPerson].signature = sigData;
  JsaState.crewSignatures[jsaSigCurrentPerson].acknowledgedAt = new Date();

  closeSignatureOverlay();
  renderCrewList();
  window.showToast?.('Signature captured', 'success');
}

function closeSignatureOverlay() {
  document.getElementById('modal-jsa-signature')?.classList.add('hidden');
  jsaSigCurrentPerson = null;
}

// ============================================
// TASK ROW MANAGEMENT
// ============================================
function addTaskRow() {
  JsaState.taskRows.push({ task: '', potentialHazards: '', controlMeasures: '', holdPoint: false });
  renderTaskRows();
}

function removeTaskRow(index) {
  JsaState.taskRows.splice(index, 1);
  if (JsaState.taskRows.length === 0) {
    JsaState.taskRows.push({ task: '', potentialHazards: '', controlMeasures: '', holdPoint: false });
  }
  renderTaskRows();
}

function renderTaskRows() {
  const container = document.getElementById('jsa-task-rows');
  if (!container) return;

  const isOilfield = JsaState.formMode === 'xto';

  container.innerHTML = JsaState.taskRows.map((row, i) => `
    <div class="jsa-task-row">
      <div class="jsa-task-row-header">
        <span style="font-weight:600;font-size:0.85rem;color:var(--text-secondary);">${isOilfield ? `Higher Potential Consequence Task ${i + 1}` : `Task ${i + 1}`}</span>
        ${JsaState.taskRows.length > 1 ? `
          <button type="button" class="btn-icon" onclick="window.JSAModule.removeTaskRow(${i})" title="Remove task">
            <span class="material-icons" style="font-size:16px;color:var(--action-danger);">delete</span>
          </button>
        ` : ''}
      </div>
      <div class="form-group">
        <input type="text" placeholder="${isOilfield ? 'Higher Potential Consequence Task Step (Description)' : 'Task / Step'}" value="${escapeHtml(row.task)}" onchange="window.JSAModule.updateTaskRow(${i}, 'task', this.value)">
      </div>
      <div class="form-group">
        <input type="text" placeholder="${isOilfield ? 'Potential Hazards & Consequences (include error-prone situations and triggers)' : 'Potential Hazards'}" value="${escapeHtml(row.potentialHazards)}" onchange="window.JSAModule.updateTaskRow(${i}, 'potentialHazards', this.value)">
      </div>
      <div class="form-group">
        <input type="text" placeholder="${isOilfield ? 'Safeguards (actions & physical safeguards to eliminate or mitigate)' : 'Control Measures'}" value="${escapeHtml(row.controlMeasures)}" onchange="window.JSAModule.updateTaskRow(${i}, 'controlMeasures', this.value)">
      </div>
      ${isOilfield ? `
      <div class="form-group" style="margin-top:4px;">
        <label class="checkbox-label" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;background:#FFF7ED;cursor:pointer;">
          <input type="checkbox" ${row.holdPoint ? 'checked' : ''} onchange="window.JSAModule.updateTaskRow(${i}, 'holdPoint', this.checked)">
          <span style="font-weight:600;font-size:0.85rem;color:#9A3412;">Hold Point — stop and verify safeguards before proceeding</span>
        </label>
      </div>
      ` : ''}
    </div>
  `).join('');
}

function updateTaskRow(index, field, value) {
  if (JsaState.taskRows[index]) {
    JsaState.taskRows[index][field] = value;
  }
}

// XTO back-page hazard-category toggle (Oilfield only).
function toggleHazardCategory(id) {
  if (JsaState.hazardCategories.has(id)) {
    JsaState.hazardCategories.delete(id);
  } else {
    JsaState.hazardCategories.add(id);
  }
  const chip = document.querySelector(`.jsa-xto-hazcat-chip[data-id="${id}"]`);
  if (chip) chip.classList.toggle('selected', JsaState.hazardCategories.has(id));
}

function updateLessonsLearned(value) {
  JsaState.lessonsLearned = value;
}

// ============================================
// HAZARD & PPE TOGGLE
// ============================================
function toggleHazard(id) {
  if (JsaState.selectedHazards.has(id)) {
    JsaState.selectedHazards.delete(id);
  } else {
    JsaState.selectedHazards.add(id);
  }

  // Update chip visual
  const chip = document.querySelector(`.jsa-hazard-chip[data-id="${id}"]`);
  if (chip) chip.classList.toggle('selected', JsaState.selectedHazards.has(id));

  // Show/hide "Other" input
  const otherGroup = document.getElementById('jsa-hazard-other-group');
  if (otherGroup) {
    otherGroup.classList.toggle('hidden', !JsaState.selectedHazards.has('other'));
  }
}

function togglePPE(id) {
  if (JsaState.selectedPPE.has(id)) {
    JsaState.selectedPPE.delete(id);
  } else {
    JsaState.selectedPPE.add(id);
  }

  const chip = document.querySelector(`.jsa-ppe-chip[data-id="${id}"]`);
  if (chip) chip.classList.toggle('selected', JsaState.selectedPPE.has(id));

  const otherGroup = document.getElementById('jsa-ppe-other-group');
  if (otherGroup) {
    otherGroup.classList.toggle('hidden', !JsaState.selectedPPE.has('other'));
  }
}

function togglePPERequired() {
  const checked = document.getElementById('jsa-ppe-required')?.checked;
  const section = document.getElementById('jsa-ppe-section');
  if (section) section.style.display = checked ? '' : 'none';
}

// ============================================
// TOPIC CHIPS (Founder TITS mode)
// ============================================
function toggleTopic(id) {
  if (JsaState.selectedTopics.has(id)) {
    JsaState.selectedTopics.delete(id);
  } else {
    JsaState.selectedTopics.add(id);
  }
  const chip = document.querySelector(`.jsa-topic-chip[data-id="${id}"]`);
  if (chip) chip.classList.toggle('selected', JsaState.selectedTopics.has(id));
}

// ============================================
// EMERGENCY MEDICAL FACILITY (Founder TITS mode)
// ============================================
function getMyLocation() {
  const status = document.getElementById('jsa-location-status');
  if (!status) return;
  status.textContent = 'Getting location...';
  if (!navigator.geolocation) {
    status.textContent = 'Geolocation not supported';
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      JsaState.userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      status.textContent = `Location found (${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)})`;
      searchFacilities();
    },
    () => {
      // TASK-9996 (COD) — a failed re-try must not leave the LAST fix (e.g. the
      // yard) biasing and sorting the next typed search.
      JsaState.userLocation = null;
      _jsaFacilitySearchSeq++; // retire a search that already read the old fix
      JsaState.facilityResults = [];
      const res = document.getElementById('jsa-facility-results');
      if (res) res.innerHTML = '';
      status.textContent = 'Location unavailable — search by name instead';
    }
  );
}

// TASK-9996 — Places API (New) facility search. Three bugs fixed here (Chris
// 2026-10-21): searchNearby was sent types it rejects (400 "Unsupported types:
// emergency_room, urgent_care_facility" — neither is in Table A; urgent care has
// no type at all), GPS + typed text put textQuery on searchNearby (400 Unknown
// name "textQuery"), and NO failure was checked, so every 400/403 rendered "No
// facilities found nearby" — a crew told no hospital exists. Now:
//   GPS, no text  -> searchNearby, includedTypes ['hospital'] only
//   text (+/- GPS)-> searchText; with GPS, a locationBias circle on the crew
//   any failure   -> "Search isn't working" + point at the manual field
//   empty result  -> "No facilities found nearby" (true only when Google said so)
const JSA_FACILITY_FIELD_MASK = 'places.displayName,places.formattedAddress,places.nationalPhoneNumber,places.location';
const JSA_FACILITY_RADIUS_M = 50000; // searchNearby's max radius
let _jsaFacilitySearchSeq = 0; // TASK-9996 (COD) — only the newest search may paint
const JSA_FACILITY_ERROR_HTML = '<p class="jsa-facility-search-error" style="font-size:0.85rem;color:var(--action-danger);font-weight:600;">Search isn&#39;t working — enter the hospital manually below.</p>';

function buildFacilitySearchRequest(searchText, userLocation) {
  const loc = userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng) ? userLocation : null;
  const circle = loc ? { center: { latitude: loc.lat, longitude: loc.lng }, radius: JSA_FACILITY_RADIUS_M } : null;
  if (searchText) {
    const body = { textQuery: searchText + ' hospital clinic emergency', maxResultCount: 5 };
    if (circle) body.locationBias = { circle };
    return { url: 'https://places.googleapis.com/v1/places:searchText', body };
  }
  if (circle) {
    return {
      url: 'https://places.googleapis.com/v1/places:searchNearby',
      body: { includedTypes: ['hospital'], locationRestriction: { circle }, maxResultCount: 5 }
    };
  }
  return null;
}

async function searchFacilities() {
  const searchText = document.getElementById('jsa-facility-search')?.value?.trim() || '';
  const resultsContainer = document.getElementById('jsa-facility-results');
  if (!resultsContainer) return;

  const loc = JsaState.userLocation;
  const seq = ++_jsaFacilitySearchSeq; // bumped first so even the prompt below retires an in-flight search
  const req = buildFacilitySearchRequest(searchText, loc);
  if (!req) {
    resultsContainer.innerHTML = '<p style="font-size:0.85rem;color:var(--text-secondary);">Get your location or enter a search term to find facilities.</p>';
    return;
  }

  resultsContainer.innerHTML = '<p style="font-size:0.85rem;color:var(--text-secondary);">Searching...</p>';
  JsaState.facilityResults = [];

  let results;
  try {
    const resp = await fetch(req.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
        'X-Goog-FieldMask': JSA_FACILITY_FIELD_MASK
      },
      body: JSON.stringify(req.body)
    });
    let data = null;
    try { data = await resp.json(); } catch (_) { data = null; }
    if (!resp.ok || !data || data.error) {
      const msg = data?.error?.message || ('HTTP ' + resp.status);
      throw new Error('Places ' + (req.url.endsWith('searchNearby') ? 'searchNearby' : 'searchText') + ' failed: ' + msg);
    }
    const places = Array.isArray(data.places) ? data.places : [];
    results = places.map(p => ({
      name: p.displayName?.text || 'Unknown',
      address: p.formattedAddress || '',
      phone: p.nationalPhoneNumber || '',
      lat: p.location?.latitude,
      lng: p.location?.longitude,
      distance: loc ? calcDistance(loc.lat, loc.lng, p.location?.latitude, p.location?.longitude) : null
    }));
    // Google ranks by prominence, so a big hospital 28 mi out can list above the
    // ER 1.4 mi away. With GPS, show the nearest first (unknown distance last).
    if (loc) results.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  } catch (err) {
    console.error('[JSA] Facility search error:', err);
    if (seq !== _jsaFacilitySearchSeq) return; // a newer search owns the panel
    resultsContainer.innerHTML = JSA_FACILITY_ERROR_HTML;
    return;
  }

  if (seq !== _jsaFacilitySearchSeq) return; // a newer search owns the panel
  JsaState.facilityResults = results;

  if (results.length === 0) {
    resultsContainer.innerHTML = '<p style="font-size:0.85rem;color:var(--text-secondary);">No facilities found nearby.</p>';
    return;
  }

  resultsContainer.innerHTML = results.map((r, i) => `
      <div class="jsa-facility-card" onclick="window.JSAModule.selectFacility(${i})">
        <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${escapeHtml(r.name)}</div>
        <div style="font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(r.address)}</div>
        <div style="display:flex;gap:12px;margin-top:2px;">
          ${r.phone ? `<span style="font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(r.phone)}</span>` : ''}
          ${r.distance != null ? `<span style="font-size:0.8rem;color:var(--brand-blue);font-weight:600;">${r.distance.toFixed(1)} mi</span>` : ''}
        </div>
      </div>
    `).join('');
}

function selectFacility(index) {
  const facility = JsaState.facilityResults[index];
  if (!facility) return;
  JsaState.emergencyFacility = { ...facility, source: 'places' };

  // Show selected facility
  const container = document.getElementById('jsa-selected-facility');
  if (container) {
    container.innerHTML = `
      <div class="jsa-facility-selected">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
          <span class="material-icons" style="font-size:18px;color:var(--action-success);">check_circle</span>
          <span style="font-weight:600;color:var(--text-primary);">Selected Facility</span>
        </div>
        <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(facility.name)}</div>
        <div style="font-size:0.85rem;color:var(--text-secondary);">${escapeHtml(facility.address)}</div>
        ${facility.phone ? `<div style="font-size:0.85rem;color:var(--text-secondary);">Phone: ${escapeHtml(facility.phone)}</div>` : ''}
        ${facility.distance != null ? `<div style="font-size:0.85rem;color:var(--brand-blue);">Distance: ${facility.distance.toFixed(1)} miles</div>` : ''}
        <button type="button" class="btn btn-sm btn-secondary" onclick="window.JSAModule.clearFacility()" style="margin-top:6px;">
          <span class="material-icons" style="font-size:14px;">close</span> Clear
        </button>
      </div>
    `;
  }

  // Clear results
  const resultsContainer = document.getElementById('jsa-facility-results');
  if (resultsContainer) resultsContainer.innerHTML = '';
}

function clearFacility() {
  JsaState.emergencyFacility = null;
  const container = document.getElementById('jsa-selected-facility');
  if (container) container.innerHTML = '';
}

function calcDistance(lat1, lng1, lat2, lng2) {
  if (!lat1 || !lng1 || !lat2 || !lng2) return null;
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ============================================
// XTO LSRA STATE MANAGEMENT
// ============================================
function toggleLSRA(cardId, field, checked) {
  if (!JsaState.lsraChecks[cardId]) JsaState.lsraChecks[cardId] = { applicable: false, items: [] };
  JsaState.lsraChecks[cardId].applicable = checked;
  // Show/hide card body based on applicable
  const body = document.getElementById(`lsra-body-${cardId}`);
  if (body) body.style.display = checked ? 'block' : 'none';
}

function toggleLSRAItem(cardId, itemIndex, checked) {
  if (!JsaState.lsraChecks[cardId]) JsaState.lsraChecks[cardId] = { applicable: true, items: [] };
  if (checked) {
    if (!JsaState.lsraChecks[cardId].items.includes(itemIndex)) {
      JsaState.lsraChecks[cardId].items.push(itemIndex);
    }
  } else {
    JsaState.lsraChecks[cardId].items = JsaState.lsraChecks[cardId].items.filter(i => i !== itemIndex);
  }
}

// ============================================
// XTO SIGNATURE HANDLING
// ============================================
function openXTOSignature(role) {
  jsaSigCurrentPerson = `xto_${role}`;
  document.getElementById('jsa-sig-title').textContent = `Sign: JSA ${role === 'issuer' ? 'Issuer' : 'Receiver'}`;

  const modal = document.getElementById('modal-jsa-signature');
  modal.classList.remove('hidden');

  // Initialize canvas after visible
  setTimeout(() => {
    jsaSigCanvas = document.getElementById('jsa-signature-canvas');
    if (!jsaSigCanvas) return;
    jsaSigCtx = jsaSigCanvas.getContext('2d');
    jsaSigCtx.strokeStyle = '#1a237e';
    jsaSigCtx.lineWidth = 2;
    jsaSigCtx.lineCap = 'round';
    jsaSigCtx.lineJoin = 'round';
    jsaSigCtx.fillStyle = '#ffffff';
    jsaSigCtx.fillRect(0, 0, jsaSigCanvas.width, jsaSigCanvas.height);
    jsaSigHasDrawn = false;
    jsaSigDrawing = false;
    bindJsaSignaturePad(); // TASK-9714
  }, 100);
}

// ============================================
// SECTION TOGGLE (Collapsible)
// ============================================
function toggleSection(sectionId) {
  const body = document.getElementById(sectionId);
  if (!body) return;

  const header = body.previousElementSibling;
  const chevron = header?.querySelector('.jsa-section-chevron');

  if (body.classList.contains('collapsed')) {
    body.classList.remove('collapsed');
    if (chevron) chevron.textContent = 'expand_more';
  } else {
    body.classList.add('collapsed');
    if (chevron) chevron.textContent = 'chevron_right';
  }
}

// ============================================
// JSA REVISION TRAIL (TASK-9371 S2)
// ============================================
// Every client EDIT of a filed JSA — saveJSA's edit branch and the Oilfield
// issuer/receiver signature update — goes through commitJsaRevision. It writes,
// in ONE batch: (a) a revision row tenants/{tid}/jsaReports/{id}/revisions/{rev}
// holding the FULL snapshot of the doc as it was BEFORE the edit (litigation
// needs "exactly what was signed", not a diff replay) with every signature
// image replaced by its SHA-256, plus changedFields / who / when / source; and
// (b) the parent patch with revisionCount+1, lastRevisionId, lastRevisedAt/By*
// and the signaturesStale flag. firestore.rules requires the counter to move by
// exactly one and getAfter()-links the parent to the revision row, so no edit
// path can skip the trail and a STALE write (someone else revised in between)
// is refused server-side — that refusal is surfaced as JsaConflictError.
//
// Identity fields (job binding, submitter, filer, format, createdAt) are never
// part of the parent patch: a filed JSA's identity is fixed (S0).

// TASK-4839 merge: a template sheet's frozen form (snapshot / id / version / name)
// is identity too — an edit never re-snapshots or converts the report.
const JSA_IDENTITY_FIELDS = ['jobId', 'jobDayId', 'submittedByPersonId', 'submittedByPersonName', 'completedById', 'completedByName', 'formMode', 'createdAt', 'status', 'templateId', 'templateVersion', 'templateName', 'templateSnapshot'];
// Fields whose change on a doc that already carries a signature makes those
// signatures attest to something else — the "Re-sign required" trigger.
// templateAnswers (TASK-4839): the whole answer map is what a template sheet's
// sign-ins attested to, so any answer change on a signed sheet → re-sign.
const JSA_SIGNATURE_BEARING_FIELDS = ['hazards', 'tasks', 'ppeRequired', 'ppeEquipment', 'ppeOther', 'lifeSavingRulesTopics', 'hazardCategories', 'lsraAssessments', 'templateAnswers'];
// Bookkeeping fields the diff must ignore (they change on every edit).
const JSA_REVISION_BOOKKEEPING = new Set(['updatedAt', 'createdAt', 'revisionCount', 'lastRevisionId', 'lastRevisedAt', 'lastRevisedByName', 'lastRevisedByUid', 'signaturesStale', 'externalSignersCount', 'externalSignersLastSignedAt', 'externalSignersUrl', 'externalSignersTokenExpiresAt', 'signInCount', 'signInNames', 'signInLastAt']);

class JsaConflictError extends Error {
  constructor(msg) { super(msg || 'JSA changed since it was opened'); this.name = 'JsaConflictError'; }
}

function _jsaMillis(v) {
  if (v == null) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  return 0;
}

// Stable, Timestamp-aware serialisation for field equality (Firestore maps are
// order-independent, so keys are sorted).
function _jsaCanon(v) {
  if (v == null) return 'null';
  if (typeof v.toMillis === 'function' || (typeof v === 'object' && typeof v.toDate === 'function')) return `ts:${_jsaMillis(v)}`;
  if (v instanceof Date) return `ts:${v.getTime()}`;
  if (Array.isArray(v)) return `[${v.map(_jsaCanon).join(',')}]`;
  if (typeof v === 'object') return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${_jsaCanon(v[k])}`).join(',')}}`;
  return JSON.stringify(v);
}

function _jsaIsEmptyDefault(v) {
  return v === '' || v === null || v === false || v === undefined
    || (Array.isArray(v) && v.length === 0)
    || (typeof v === 'object' && !Array.isArray(v) && typeof v.toMillis !== 'function' && Object.keys(v).length === 0);
}

// Field names in `patch` whose value differs from `current`. Identity and
// bookkeeping fields never count. A field ABSENT on an older doc that the form
// now emits as its empty default ('' / [] / false / {}) is not a change either —
// otherwise every first edit of a legacy doc would record a phantom revision.
function _jsaChangedFields(current, patch) {
  const out = [];
  Object.keys(patch || {}).forEach(k => {
    if (JSA_REVISION_BOOKKEEPING.has(k) || JSA_IDENTITY_FIELDS.includes(k)) return;
    const before = current ? current[k] : undefined;
    const after = patch[k];
    if (before === undefined && _jsaIsEmptyDefault(after)) return;
    if (_jsaCanon(before) !== _jsaCanon(after)) out.push(k);
  });
  return out;
}

// { crew, external } — signatures present on a report (either format).
// TASK-4839 merge: a template sheet's crew signatures live in the signIns
// subcollection, not on the parent. Pass the loaded rows as `signIns` (the
// authoritative count); without them the server-maintained signInCount is used.
function _jsaCountSignatures(report, signIns) {
  const r = report || {};
  let crew;
  if (r.formMode === 'xto') {
    crew = (r.jsaIssuer?.signature ? 1 : 0) + (r.jsaReceiver?.signature ? 1 : 0);
  } else if (r.formMode === 'template') {
    crew = Array.isArray(signIns)
      ? signIns.filter(s => s && s.deleted !== true && s.signaturePngB64).length
      : (Number(r.signInCount) || 0);
  } else {
    crew = Array.isArray(r.crewAcknowledgments) ? r.crewAcknowledgments.filter(a => a && a.signature).length : 0;
  }
  return { crew, external: Number(r.externalSignersCount) || 0 };
}

async function _jsaSha256Hex(str) {
  const s = String(str ?? '');
  try {
    if (globalThis.crypto?.subtle?.digest) {
      const buf = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
      return 'sha256:' + Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (_) { /* fall through */ }
  // Non-secure context (plain http) has no SubtleCrypto — record a labelled
  // FNV-1a so the row still detects a swapped image; never store the image.
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return `fnv1a:${h.toString(16).padStart(8, '0')}:len${s.length}`;
}

// Full pre-edit snapshot with every signature image replaced by its hash. The
// row must stay small (TASK-9147: inline PNG data URLs are the doc's bulk).
async function _jsaSnapshotWithHashedSignatures(current, signIns) {
  const snap = { ...(current || {}) };
  delete snap.id;
  // TASK-4839 merge: template sign-ins (subcollection rows) captured as they
  // stood at the edit — who, when, and the hash of the PNG, never the PNG.
  if (Array.isArray(signIns)) {
    snap.signIns = await Promise.all(signIns.map(async (s) => {
      const { signaturePngB64, ...rest } = s || {};
      return { ...rest, signatureHash: signaturePngB64 ? await _jsaSha256Hex(signaturePngB64) : null, signatureBytes: signaturePngB64 ? String(signaturePngB64).length : 0 };
    }));
  }
  if (Array.isArray(snap.crewAcknowledgments)) {
    snap.crewAcknowledgments = await Promise.all(snap.crewAcknowledgments.map(async (a) => {
      if (!a || typeof a !== 'object') return a;
      const { signature, ...rest } = a;
      return { ...rest, signatureHash: signature ? await _jsaSha256Hex(signature) : null, signatureBytes: signature ? String(signature).length : 0 };
    }));
  }
  for (const k of ['jsaIssuer', 'jsaReceiver']) {
    if (snap[k] && typeof snap[k] === 'object') {
      const { signature, ...rest } = snap[k];
      snap[k] = { ...rest, signatureHash: signature ? await _jsaSha256Hex(signature) : null, signatureBytes: signature ? String(signature).length : 0 };
    }
  }
  return snap;
}

function _jsaRevisionActor() {
  const isPin = window.AppState?.driverModePin === true;
  return {
    uid: window.AppState?.user?.uid || '',
    personId: getViewerPersonId(),
    name: window.AppState?.driverPersonName
      || window.AppState?.memberAccess?.displayName
      || window.AppState?.user?.displayName
      || window.AppState?.user?.email
      || '',
    source: isPin ? 'driver_pin' : 'member',
  };
}

/**
 * Commit an edit of jsaReports/{jsaId} with its revision row.
 * @param baseline the report as it was when the editor opened it (conflict check)
 * @param patch    the fields the editor wants to write (identity fields ignored)
 * @returns {Promise<{noop:true}|{revisionId:string,changedFields:string[],signatureContentChanged:boolean,revisionNumber:number}>}
 * @throws JsaConflictError when the doc moved since `baseline` or the rules refuse the write
 */
// TASK-10024 — split an Edit JSA crew list into (a) what the client patch may
// carry and (b) the lines to sign through signJsaCrewLine afterwards.
//   patchCrew: the report's OWN line objects for every line that stays, so an
//     unchanged line is byte-identical (the rules' driver arm requires that).
//     Members (office) may add / remove lines here; an added line goes in
//     blank. A PIN session may not change the list at all: patchCrew is the
//     original array and droppedMembership says something was not saved.
//   toSign: lines whose signature is new or different from the saved one.
// Pure (no DOM, no Firestore) — tests/task10024-jsa-crew-sign.unit.cjs.
function _jsaSplitCrewForEdit(editing, crewSignatures, opts) {
  const isPin = opts && 'isPin' in opts ? opts.isPin === true : window.AppState?.driverModePin === true;
  const orig = Array.isArray(editing?.crewAcknowledgments) ? editing.crewAcknowledgments : [];
  const byId = new Map(orig.filter(a => a && a.personId).map(a => [a.personId, a]));
  const entries = Object.entries(crewSignatures || {}).filter(([pid]) => !pid.startsWith('__'));
  const toSign = entries
    .filter(([pid, d]) => d && d.signature && d.signature !== (byId.get(pid)?.signature || null))
    .map(([pid, d]) => ({ personId: pid, name: d.name || '', signature: d.signature }));
  const ids = entries.map(([pid]) => pid);
  const sameMembership = ids.length === byId.size && ids.every(pid => byId.has(pid));
  if (isPin) {
    return {
      patchCrew: orig,
      // A PIN signer may add THEIR OWN line (the server re-checks the job day);
      // anyone else added or removed cannot be saved from a PIN session.
      toSign: toSign.filter(t => byId.has(t.personId) || t.personId === (opts?.viewerPersonId ?? getViewerPersonId())),
      droppedMembership: !sameMembership && !(ids.length === byId.size + 1
        && [...byId.keys()].every(pid => ids.includes(pid))
        && ids.some(pid => !byId.has(pid) && pid === (opts?.viewerPersonId ?? getViewerPersonId()))),
    };
  }
  const patchCrew = entries.map(([pid, d]) => byId.get(pid)
    || { personId: pid, name: d.name || '', signature: null, acknowledgedAt: null });
  return { patchCrew, toSign, droppedMembership: false };
}

// Sign each planned line, in order. Never throws; reports per-line failures.
async function _jsaSignCrewAfterEdit(jsaId, plan) {
  const out = { signed: 0, failed: [] };
  for (const t of plan.toSign) {
    try {
      await signCrewLineOnServer(jsaId, t.personId, t.signature);
      out.signed++;
    } catch (e) {
      out.failed.push({ personId: t.personId, name: t.name || 'Signature', message: e.message || 'not saved' });
    }
  }
  return out;
}

const JSA_MSG_CHANGED_SINCE_OPENED = 'Someone else saved this JSA after you opened it. Close it, open it again, and redo your change.';
const JSA_MSG_NOT_ALLOWED = "You can't change this part of the JSA from this login. Signatures still work: tap Sign on your line.";

async function commitJsaRevision(tenantId, jsaId, baseline, patch) {
  const ref = doc(db, 'tenants', tenantId, 'jsaReports', jsaId);
  const fresh = await getDoc(ref);
  if (!fresh.exists()) throw new JsaConflictError('This JSA no longer exists.');
  const current = fresh.data();
  const baseCount = Number(baseline?.revisionCount) || 0;
  const curCount = Number(current.revisionCount) || 0;
  if (curCount !== baseCount || _jsaMillis(current.updatedAt) !== _jsaMillis(baseline?.updatedAt)) {
    throw new JsaConflictError(JSA_MSG_CHANGED_SINCE_OPENED);
  }

  const changedFields = _jsaChangedFields(current, patch);
  if (changedFields.length === 0) return { noop: true };

  const actor = _jsaRevisionActor();
  let signIns = null;
  let signInsReadError = null;
  if (current.formMode === 'template') {
    // A failed read records `signInsReadError` in the snapshot rather than blocking the edit.
    try { signIns = await loadSignInsForDetail(jsaId, { tenantId, includeDeleted: true }); } catch (e) { signInsReadError = e?.message || 'read failed'; }
  }
  const sigs = _jsaCountSignatures(current, signIns);
  const snapshot = await _jsaSnapshotWithHashedSignatures(current, signIns);
  if (signInsReadError) snapshot.signInsReadError = signInsReadError;
  const revisedAt = Timestamp.now();
  const revRef = doc(collection(ref, 'revisions'));
  const revisionNumber = curCount + 1;

  // TASK-9371 COD r5 — NO client prediction of the re-sign flag. The parent's
  // signaturesStale is server-owned (rules refuse any client change) and
  // derived by onJsaReportSignaturesStale / onJsaSignInWrite from server-only
  // evidence; a client guess from crew-ack timestamps could toast "cleared" while
  // the server says stale. The client records only a FACT about this edit:
  // did it change signature-bearing content on a sheet that carried signatures.
  const signatureContentChanged = changedFields.some(f => JSA_SIGNATURE_BEARING_FIELDS.includes(f))
    && (sigs.crew + sigs.external) > 0;

  const parentPatch = {};
  changedFields.forEach(k => { parentPatch[k] = patch[k]; });
  Object.assign(parentPatch, {
    updatedAt: revisedAt,
    revisionCount: revisionNumber,
    lastRevisionId: revRef.id,
    lastRevisedAt: revisedAt,
    lastRevisedByName: actor.name,
    lastRevisedByUid: actor.uid,
  });

  const batch = writeBatch(db);
  batch.set(revRef, {
    revisionNumber,
    revisedAt,
    revisedByUid: actor.uid,
    revisedByPersonId: actor.personId || null,
    revisedByName: actor.name,
    source: actor.source,
    formMode: current.formMode === 'xto' ? 'xto' : (current.formMode === 'template' ? 'template' : 'tits'),
    changedFields,
    signaturesAtEdit: sigs,
    signatureContentChanged,
    snapshot,
  });
  batch.update(ref, parentPatch);
  try {
    await batch.commit();
  } catch (e) {
    if (e?.code === 'permission-denied') {
      // TASK-10024 — two different problems, two different fixes for the user.
      // Re-read: if the JSA moved, it is a conflict; if not, this login may not
      // make this edit (the rules said no).
      let moved = false;
      try {
        const again = (await getDoc(ref)).data() || {};
        moved = (Number(again.revisionCount) || 0) !== curCount || _jsaMillis(again.updatedAt) !== _jsaMillis(current.updatedAt);
      } catch (_) { /* treat as not moved */ }
      throw new JsaConflictError(moved
        ? JSA_MSG_CHANGED_SINCE_OPENED
        : JSA_MSG_NOT_ALLOWED);
    }
    throw e;
  }
  window.logAudit?.('jsa', 'JSA_REVISED', {
    jsaId,
    revisionId: revRef.id,
    revisionNumber,
    changedFields,
    source: actor.source,
    signaturesAtEdit: sigs,
    signatureContentChanged,
    summary: changedFields.join(', '),
  });
  return { revisionId: revRef.id, changedFields, signatureContentChanged, revisionNumber, revisedAt };
}

// Detail-view banner + card badges read the parent fields only.
function _jsaRevisionBadgesHTML(report) {
  const n = Number(report?.revisionCount) || 0;
  return [
    n > 0 ? `<span class="pretrip-card-type" style="background:#FEF3C7;color:#92400E;">Revised ×${n}</span>` : '',
    report?.signaturesStale === true ? `<span class="pretrip-card-type" style="background:#FEF2F2;color:#991B1B;">Re-sign needed</span>` : '',
  ].join('');
}

function _jsaRevisionBannerHTML(report) {
  const n = Number(report?.revisionCount) || 0;
  if (!n && report?.signaturesStale !== true) return '';
  const when = report?.lastRevisedAt ? new Date(_jsaMillis(report.lastRevisedAt)).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const by = report?.lastRevisedByName ? escapeHtml(report.lastRevisedByName) : '';
  const revisedLine = n
    ? `<div style="font-weight:600;color:#92400E;">Revised ×${n}${by ? ` — last by ${by}` : ''}${when ? ` at ${escapeHtml(when)}` : ''}</div>`
    : '';
  const staleLine = report?.signaturesStale === true
    ? `<div style="font-weight:600;color:#991B1B;margin-top:${n ? 4 : 0}px;">Re-sign required — the signatures on file predate the latest revision.</div>`
    : '';
  const bg = report?.signaturesStale === true ? '#FEF2F2' : '#FFFBEB';
  const border = report?.signaturesStale === true ? '#FCA5A5' : '#FCD34D';
  return `<div class="jsa-revision-banner" style="margin:12px 16px 0;padding:10px 12px;border:1px solid ${border};border-radius:8px;background:${bg};font-size:0.85rem;">${revisedLine}${staleLine}</div>`;
}

// ============================================
// SAVE JSA
// ============================================
async function saveJSA(event) {
  event.preventDefault();
  // H3 — a read-only access-link session is view-only; JSA submit requires PIN.
  if (window.isReadOnlyLinkSession?.()) {
    window.showToast?.('Read-only preview — log in with your PIN to submit a JSA.', 'info');
    return;
  }
  if (!window.validateRequiredFields(event.target)) return;

  const tenantId = window.AppState?.tenant;
  if (!tenantId) {
    window.showToast?.('No tenant found', 'error');
    return;
  }

  let jsaData;
  let templateSignIns = []; // TASK-4839: signIns subcollection rows written alongside a NEW template sheet

  // TASK-9371 S0 — a JSA's IDENTITY is fixed when it is filed. On EDIT every
  // identity field (job binding, submitter, filer, format) is copied from the
  // cached report, never re-derived: `JsaState.jobDays` is a TODAY-ONLY
  // subscription (initJSA), so recomputing `jobDayId` for yesterday's card
  // wrote '' and dropped the JSA off the job; `submittedByPersonId` came from
  // the LIVE session, so a member edit wiped the driver's ownership (both COD
  // ❌ lanes, verified in tests/task9371-s0-edit-keeps-identity.unit.cjs).
  const editing = JsaState.editingJsaId ? (JsaState.editingReport || null) : null;
  if (JsaState.editingJsaId && !editing) {
    window.showToast?.('This JSA could not be reloaded — close and open it again before saving.', 'error');
    return;
  }
  // TASK-4839 merge: 'template' is a third format; a filed template sheet stays one.
  const editingMode = editing
    ? (editing.formMode === 'xto' ? 'xto' : (editing.formMode === 'template' ? 'template' : 'tits'))
    : null;
  if (editing && editingMode !== JsaState.formMode) {
    const label = editingMode === 'xto' ? 'Oilfield' : (editingMode === 'template' ? (editing.templateName || 'the company form') : 'Standard');
    window.showToast?.(`A filed JSA keeps its format — switch back to ${label} to save this edit.`, 'error');
    return;
  }
  // Identity fields shared by both formats. On create they come from the live
  // session; on edit they are the report's own values, verbatim.
  const identity = editing ? {
    completedById: editing.completedById ?? '',
    completedByName: editing.completedByName ?? '',
    submittedByPersonId: editing.submittedByPersonId ?? '',
    submittedByPersonName: editing.submittedByPersonName ?? '',
  } : {
    completedById: window.AppState?.user?.uid || '',
    completedByName: window.AppState?.user?.displayName || window.AppState?.user?.email || '',
    submittedByPersonId: window.AppState?.driverPersonId || '',
    submittedByPersonName: window.AppState?.driverPersonName || '',
  };

  if (JsaState.formMode === 'xto') {
    // ---- XTO MODE SAVE ----
    const dateVal = document.getElementById('xto-date')?.value;
    if (!dateVal) {
      window.showToast?.('Date is required', 'error');
      return;
    }

    // TASK-9371 S0 — the Oilfield form has no job dropdown, so its job binding
    // is the prefill from "Start JSA" / "Add another JSA" (jobboard/dispatch);
    // without this an Oilfield JSA started from a job landed unlinked. On edit
    // the binding is the report's own.
    const xtoJobId = editing ? (editing.jobId || '') : (JsaState.prefillJobId || '');
    const xtoJobDayId = editing ? (editing.jobDayId || '') : (JsaState.prefillDayInvalid ? '' : (JsaState.prefillJobDayId || '')); // TASK-10011 COD r4 — never a day the read showed missing / another job's

    // XTO back-page additions (added 2026-05-21): per-step task table with
    // hold points, hazard-type categories, and lessons-learned text.
    const xtoTasks = JsaState.taskRows.filter(r => r.task?.trim() || r.potentialHazards?.trim() || r.controlMeasures?.trim() || r.holdPoint);

    jsaData = {
      formMode: 'xto',
      jobId: xtoJobId,
      jobDayId: xtoJobDayId,
      jsaNumber: document.getElementById('xto-jsa-number')?.value || '',
      date: Timestamp.fromMillis(_jsaDayMs(dateVal)), // TASK-9923 company-zone noon
      unitArea: document.getElementById('xto-unit-area')?.value?.trim() || '',
      specificLocation: document.getElementById('xto-specific-location')?.value?.trim() || '',
      jsaIssuer: JsaState.xtoSignatures.issuer,
      jsaReceiver: JsaState.xtoSignatures.receiver,
      lsraAssessments: JsaState.lsraChecks,
      hazardCategories: Array.from(JsaState.hazardCategories),
      tasks: xtoTasks,
      lessonsLearned: (JsaState.lessonsLearned || '').trim(),
      jobDescription: document.getElementById('xto-job-description')?.value?.trim() || '',
      ...identity,
      status: 'completed',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
  } else if (JsaState.formMode === 'template') {
    // ---- TEMPLATE MODE SAVE (TASK-4839) ----
    // Same fixed job header as Standard (resolveJobBinding), answers read by
    // DECLARED field type, bound answers copied onto the shared columns. On a
    // NEW sheet every signed crew row becomes a signIns subcollection doc in the
    // same batch as the parent; on an edit the filed report's snapshot/mode are
    // kept and sign-ins are untouched.
    const snapshot = JsaState.templateSnapshot;
    if (!snapshot || !Array.isArray(snapshot.sections)) {
      window.showToast?.('Template not loaded — pick a form type again', 'error');
      return;
    }
    const binding = resolveJobBinding();
    if (!binding.dateVal) {
      window.showToast?.('Date is required', 'error');
      return;
    }
    const answers = readTemplateAnswersFromDom(snapshot, {
      checks: JsaState.templateChecks,
      facility: JsaState.emergencyFacility,
    });
    const missing = requiredTemplateFieldsMissing(snapshot, answers);
    if (missing.length) {
      window.showToast?.(`${missing[0]} is required`, 'error');
      return;
    }
    const actor = {
      uid: window.AppState?.user?.uid || '',
      displayName: window.AppState?.user?.displayName || window.AppState?.user?.email || '',
      personId: window.AppState?.driverPersonId || '',
      personName: window.AppState?.driverPersonName || '',
    };
    jsaData = buildTemplateReportData({
      snapshot,
      template: JsaState.activeTemplate,
      answers,
      binding,
      actor,
      existing: JsaState.editingJsaId ? JsaState.editingReport : null,
    });
    if (!JsaState.editingJsaId) {
      templateSignIns = buildTemplateSignInDocs(JsaState.crewSignatures, actor);
    }
    if (editing) {
      // TASK-9371 S0 on template sheets — the job binding and the filer/submitter
      // are the report's own (buildTemplateReportData reads them from the LIVE
      // header + session, which is the same today-only / wrong-actor trap).
      // The frozen template fields come from `existing` inside the builder.
      Object.assign(jsaData, identity, {
        jobId: editing.jobId || '',
        jobDayId: editing.jobDayId || '',
        jobTitle: editing.jobTitle ?? '',
        truckDisplay: editing.truckDisplay ?? jsaData.truckDisplay ?? '',
      });
      // Sign-in bookkeeping and the (always-empty) inline acks are not the
      // editor's to write — the signIns subcollection owns them.
      delete jsaData.signInCount;
      delete jsaData.signInNames;
      delete jsaData.crewAcknowledgments;
    }
  } else {
    // ---- TITS MODE SAVE ----
    // TASK-4839: the header read moved into resolveJobBinding (shared with the
    // template sheet); the fields and their TASK-794 jobDay preference are unchanged.
    const live = resolveJobBinding();
    const { customerId, customerName } = live;
    // TASK-9371 S0 — EDIT: the job binding is the report's own. The dropdown
    // cannot re-bind a filed JSA (the rules freeze jobId/jobDayId for
    // driver-PIN edits; members get the same invariant here so the JSA never
    // silently moves between days/jobs).
    const jobId = editing ? (editing.jobId || '') : live.jobId;
    const jobDayId = editing ? (editing.jobDayId || '') : live.jobDayId;
    const jobTitle = editing ? (editing.jobTitle ?? '') : live.jobTitle;
    const truckDisplay = editing ? (editing.truckDisplay ?? '') : live.truckDisplay;

    const supervisorSelect = document.getElementById('jsa-supervisor');
    const supervisorId = supervisorSelect?.value || '';
    const supervisorName = supervisorSelect?.selectedOptions[0]?.dataset?.name || supervisorSelect?.selectedOptions[0]?.textContent?.trim() || '';

    const dateVal = document.getElementById('jsa-date')?.value;
    if (!dateVal) {
      window.showToast?.('Date is required', 'error');
      return;
    }
    if (!supervisorId) {
      window.showToast?.('Supervisor is required', 'error');
      return;
    }

    const crewAcknowledgments = Object.entries(JsaState.crewSignatures).map(([personId, data]) => ({
      personId,
      name: data.name,
      signature: data.signature || null,
      acknowledgedAt: data.acknowledgedAt ? Timestamp.fromDate(data.acknowledgedAt) : null
    }));

    const tasks = JsaState.taskRows.filter(r => r.task.trim() || r.potentialHazards.trim() || r.controlMeasures.trim());

    jsaData = {
      formMode: 'tits',
      customerId,
      customerName,
      jobId,
      jobDayId: jobDayId || '',
      jobTitle,
      truckDisplay: truckDisplay || '',
      locationArea: jobTitle,
      date: Timestamp.fromMillis(_jsaDayMs(dateVal)), // TASK-9923 company-zone noon
      supervisorId,
      supervisorName,
      ...identity,
      personResponsible: document.getElementById('jsa-person-responsible')?.value?.trim() || '',
      permitRequired: document.getElementById('jsa-permit-required')?.checked || false,
      hazards: Array.from(JsaState.selectedHazards),
      hazardOther: document.getElementById('jsa-hazard-other')?.value?.trim() || '',
      tasks,
      ppeRequired: document.getElementById('jsa-ppe-required')?.checked || false,
      ppeEquipment: Array.from(JsaState.selectedPPE),
      ppeOther: document.getElementById('jsa-ppe-other')?.value?.trim() || '',
      tailgateItemsDiscussed: document.getElementById('jsa-tailgate-items')?.value?.trim() || '',
      crewAcknowledgments,
      oneCallCompleted: document.getElementById('jsa-one-call')?.checked || false,
      oneCallNumber: document.getElementById('jsa-one-call-number')?.value?.trim() || '',
      workLayoutPlan: document.getElementById('jsa-work-layout')?.value?.trim() || '',
      musterPoint: document.getElementById('jsa-muster-point')?.value?.trim() || '',
      status: 'completed',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    jsaData.lifeSavingRulesTopics = Array.from(JsaState.selectedTopics);
    jsaData.emergencyFacility = JsaState.emergencyFacility || {
      name: document.getElementById('jsa-facility-manual')?.value?.trim() || '',
      address: '', phone: '', source: 'manual'
    };
    jsaData.totalHours = parseFloat(document.getElementById('jsa-total-hours')?.value) || 8;
  }

  // Disable submit button
  const btn = document.getElementById('jsa-submit-btn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  try {
    if (JsaState.editingJsaId) {
      // Editing existing report — TASK-9371 S2: one batch = parent patch +
      // revision snapshot (who / when / what changed, hashed signatures).
      delete jsaData.createdAt; // Don't overwrite original creation time
      // TASK-10024 — crew signatures never ride the client patch: each newly
      // signed / re-signed line goes through signJsaCrewLine AFTER the content
      // save (the rules refuse any PIN write that moves crewAcknowledgments,
      // which is why Chris's Save was refused on 2026-10-22).
      const crewPlan = jsaData.formMode === 'tits' ? _jsaSplitCrewForEdit(editing, JsaState.crewSignatures) : null;
      if (crewPlan) jsaData.crewAcknowledgments = crewPlan.patchCrew;
      const res = await commitJsaRevision(tenantId, JsaState.editingJsaId, editing, jsaData);
      const crewOut = crewPlan ? await _jsaSignCrewAfterEdit(JsaState.editingJsaId, crewPlan) : null;
      if (crewOut && (crewOut.failed.length || crewOut.signed || crewPlan.droppedMembership)) {
        // One plain message for the whole save (content + signatures).
        const parts = [];
        if (!res.noop) parts.push('JSA updated');
        if (crewOut.signed) parts.push(`${crewOut.signed} signature${crewOut.signed === 1 ? '' : 's'} saved`);
        crewOut.failed.forEach(f => parts.push(`${f.name}: ${f.message}`));
        if (crewPlan.droppedMembership) parts.push('Adding or removing crew on a filed JSA needs the office');
        const bad = crewOut.failed.length > 0 || crewPlan.droppedMembership;
        window.showToast?.(parts.join('. ') + '.', bad ? 'error' : 'success');
        if (crewOut.failed.length) {
          // The content part is saved. Never retry from this (now stale) form —
          // a re-save could carry old inputs over someone else's newer edit
          // (COD r2). Close it and open the report on the line to sign; Sign
          // there is one server-checked line at a time.
          const jsaId = JsaState.editingJsaId;
          JsaState.editingJsaId = null;
          JsaState.editingReport = null;
          closeJSAModal({ saved: true });
          await _jsaRefreshReport(jsaId);
          viewJSADetail(jsaId, { focusSign: true });
          return;
        }
      } else if (res.noop) {
        window.showToast?.('No changes to save', 'info');
      } else if (res.signatureContentChanged) {
        // COD r5: never claim the re-sign outcome — the server decides it and the
        // card / detail badge shows its answer a moment later.
        window.showToast?.('JSA updated — hazards / PPE / tasks changed on a signed sheet; checking whether the crew must re-sign…', 'info');
      } else {
        window.showToast?.('JSA updated successfully', 'success');
      }
    } else if (jsaData.formMode === 'template') {
      // TASK-4839 — parent + every signed crew row in ONE atomic batch. The PNGs
      // live only in jsaReports/{id}/signIns/*; the parent stays a few KB.
      const parentRef = doc(collection(db, 'tenants', tenantId, 'jsaReports'));
      const batch = writeBatch(db);
      batch.set(parentRef, jsaData);
      templateSignIns.forEach(row => batch.set(doc(collection(parentRef, 'signIns')), row));
      await batch.commit();
      window.showToast?.(templateSignIns.length ? `JSA submitted — ${templateSignIns.length} signed in` : 'JSA submitted successfully', 'success');
    } else {
      await addDoc(collection(db, 'tenants', tenantId, 'jsaReports'), jsaData);
      window.showToast?.('JSA submitted successfully', 'success');
    }
    JsaState.editingJsaId = null;
    JsaState.editingReport = null;
    // TASK-9714 (supersedes TASK-9511's "a save stays put") — after Submit the
    // driver lands back on the job they opened the form from.
    closeJSAModal({ saved: true, returnToJob: true });
  } catch (error) {
    console.error('[JSA] Error saving:', error);
    if (error instanceof JsaConflictError) {
      window.showToast?.(error.message, 'error');
    } else {
      window.showToast?.('Error saving JSA: ' + error.message, 'error');
    }
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">check_circle</span> Submit JSA';
    }
  }
}

// ============================================
// VIEW JSA DETAIL
// ============================================
let currentDetailJsaId = null;
let currentDetailReport = null;

// TASK-9371 COD r6 — the report listener replaced JsaState.reports but the OPEN
// detail kept the object viewJSADetail copied once, so after an Oilfield
// re-sign (confirmSignature -> viewJSADetail runs BEFORE the server trigger
// clears the flag) "Re-sign required" stayed until the modal was reopened.
// On every listener delivery, if the report open in detail changed, adopt the
// fresh snapshot and swap ONLY the revision banner in place — the rest of the
// detail (sign-in inputs, lists being loaded) is not re-rendered. While a
// signature pad or an edit of this report is open, only the server-owned
// display fields are merged, so the pad's conflict baseline (revisionCount /
// updatedAt) is never moved under it.
const JSA_DETAIL_LIVE_FIELDS = ['signaturesStale', 'signaturesStaleAt', 'signaturesReSignedAt', 'contentRevisedAt', 'lastSignedAt', 'signInCount', 'signInNames', 'externalSignersCount'];
function syncOpenJsaDetail(reports, env) {
  const d = (env && env.document) || document;
  if (!currentDetailJsaId || !currentDetailReport) return false;
  const modal = d.getElementById('modal-jsa-detail');
  if (!modal || modal.classList.contains('hidden')) return false;
  const fresh = (reports || []).find(r => r && r.id === currentDetailJsaId);
  if (!fresh || fresh === currentDetailReport) return false;
  const beforeHTML = _jsaRevisionBannerHTML(currentDetailReport);
  const busy = jsaSigCurrentPerson != null || JsaState.editingJsaId === currentDetailJsaId;
  if (busy) {
    JSA_DETAIL_LIVE_FIELDS.forEach(k => {
      if (k in fresh) currentDetailReport[k] = fresh[k];
      else delete currentDetailReport[k];
    });
  } else {
    currentDetailReport = fresh;
  }
  const html = _jsaRevisionBannerHTML(currentDetailReport);
  if (html === beforeHTML) return false;
  const container = d.getElementById('jsa-detail-content');
  if (!container) return false;
  const old = container.querySelector('.jsa-revision-banner');
  if (old) {
    if (html) old.outerHTML = html;
    else old.remove();
  } else if (html) {
    container.insertAdjacentHTML('afterbegin', html);
  }
  return true;
}

// ============================================
// TASK-10024 — SIGN A CREW LINE ON A FILED STANDARD JSA
// ============================================
// Chris 2026-10-22 (live 2112, PIN session): "Open to sign" landed on this
// read-only report with no way to sign, and signing in Edit JSA was refused on
// Save. firestore.rules never lets a PIN session write crewAcknowledgments
// (it cannot prove only the caller's OWN line changed), so every signature on a
// FILED Standard JSA now goes through the signJsaCrewLine callable, which makes
// that per-line check (functions/src/services/jsaCrewSign.js). These client
// checks only decide which buttons to SHOW; the server decides what lands.

// Office safety manager as the client can see it: the owner (no members doc ->
// memberAccess null) or an hr-module member who is not view/upload-only.
function _jsaViewerIsSafetyManager() {
  if (window.AppState?.driverModePin === true) return false;
  if (window.isReadOnlyLinkSession?.()) return false;
  const ma = window.AppState?.memberAccess;
  if (!ma) return !!window.AppState?.user;
  return Array.isArray(ma.modules) && ma.modules.includes('hr') && ma.viewUploadOnly !== true && ma.readOnly !== true;
}

// TASK-10024 COD r1 — a line someone else signed for the worker says so.
function _jsaSignedByNote(ack) {
  const by = ack && ack.signature && ack.signedBy;
  if (!by || !by.name || by.personId === ack.personId) return '';
  return `<div class="jsa-signed-by" style="font-size:0.78rem;color:var(--text-secondary);">Signed by ${escapeHtml(by.name)} for ${escapeHtml(ack.name || 'this crew member')}</div>`;
}

// 'own' | 'manager' | '' — mirrors decideCrewSign for an EXISTING line. No crew-lead
// proxy (Chris 2026-10-22): a PIN session gets a Sign button on its OWN line only;
// owner / hr office logins may sign any line ("Sign for them").
function _jsaCrewSignRole(report, ack) {
  if (!report || !ack || report.formMode === 'xto' || report.formMode === 'template') return '';
  if (window.isReadOnlyLinkSession?.()) return '';
  const me = getViewerPersonId();
  if (me && ack.personId === me) return 'own';
  if (_jsaViewerIsSafetyManager()) return 'manager';
  return '';
}

// The viewer works this job day but has no line on the JSA yet -> "Add my
// signature" (the server re-checks the job day before appending).
function _jsaAddMyLineHTML(report) {
  if (!report || report.formMode === 'xto' || report.formMode === 'template') return '';
  if (window.isReadOnlyLinkSession?.()) return '';
  const me = getViewerPersonId();
  if (!me || !report.jobDayId) return '';
  if ((report.crewAcknowledgments || []).some(a => a && a.personId === me)) return '';
  const jd = (JsaState.jobDays || []).find(d => d && d.id === report.jobDayId);
  if (!jd) return '';
  const meName = (JsaState.people || []).find(p => p && p.id === me)?.name || '';
  const onDay = [...(jd.drivers || []), ...(jd.helpers || [])].some(r => r === me || (meName && r === meName));
  if (!onDay) return '';
  return `
        <button type="button" class="btn btn-primary jsa-crew-sign-btn jsa-add-my-line" style="width:100%;min-height:44px;display:flex;align-items:center;justify-content:center;gap:8px;" onclick="window.JSAModule.openDetailCrewSign('${window.escJsAttr(me)}')">
          <span class="material-icons" style="font-size:18px;">draw</span> Add my signature
        </button>`;
}

// Bring the viewer to the line they came to sign: their own, else the first
// line they may sign. Highlight briefly; never auto-open the pad.
function _jsaFocusSignLine() {
  const container = document.getElementById('jsa-detail-content');
  if (!container) return;
  const me = getViewerPersonId();
  const esc = (v) => (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&');
  const target = (me && container.querySelector(`.jsa-detail-crew-member[data-person-id="${esc(me)}"]`))
    || container.querySelector('.jsa-add-my-line')
    || container.querySelector('.jsa-crew-sign-btn')?.closest('.jsa-detail-crew-member')
    || container.querySelector('.jsa-detail-crew')
    // template sheet: the sign-in controls (self button, or the filer's name box)
    || container.querySelector('#jsa-signin-self')
    || container.querySelector('#jsa-signin-add-name')?.parentElement;
  if (!target) return;
  try { target.scrollIntoView({ block: 'center' }); } catch (_) { /* old webviews */ }
  target.classList.add('jsa-sign-focus');
  target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.35)';
  setTimeout(() => { target.style.boxShadow = ''; target.classList.remove('jsa-sign-focus'); }, 2500);
}

function openDetailCrewSign(personId) {
  if (window.isReadOnlyLinkSession?.()) {
    window.showToast?.('Read-only preview — log in with your PIN to sign.', 'info');
    return;
  }
  const report = currentDetailReport;
  if (!report || !currentDetailJsaId || !personId) return;
  const ack = (report.crewAcknowledgments || []).find(a => a && a.personId === personId);
  const name = ack?.name
    || (JsaState.people || []).find(p => p && p.id === personId)?.name
    || window.AppState?.driverPersonName || 'Crew member';
  JsaState.crewSignatures.__detail_crew = { name, personId, jsaId: currentDetailJsaId, seenContentMs: _jsaMillis(report.contentRevisedAt) || 0, signature: null, acknowledgedAt: null };
  initCrewSignature('__detail_crew');
}

// One callable per line. Resolves with the server's answer; throws a readable Error.
// seenContentRevisedAtMs: the report's contentRevisedAt as the signer read it
// (detail view). Omitted on the Edit JSA path, where the signer signs the
// content they just saved.
async function signCrewLineOnServer(jsaId, personId, signaturePngB64, seenContentRevisedAtMs) {
  const fn = httpsCallable(functions, 'signJsaCrewLine');
  try {
    const payload = { tenantId: window.AppState?.tenant || '', jsaId, personId, signaturePngB64 };
    if (typeof seenContentRevisedAtMs === 'number') payload.seenContentRevisedAtMs = seenContentRevisedAtMs;
    const res = await fn(payload);
    return res?.data || {};
  } catch (e) {
    const msg = String(e?.message || '').replace(/^(FirebaseError:\s*)/, '');
    throw new Error(msg || 'Could not save the signature');
  }
}

// Pull the report fresh into JsaState.reports so a re-render shows the write
// before the listener delivers it.
async function _jsaRefreshReport(jsaId) {
  const tenantId = window.AppState?.tenant;
  if (!tenantId || !jsaId) return;
  try {
    const fresh = await getDoc(doc(db, 'tenants', tenantId, 'jsaReports', jsaId));
    if (!fresh.exists()) return;
    const r = { id: fresh.id, ...fresh.data() };
    const i = JsaState.reports.findIndex(x => x && x.id === r.id);
    if (i >= 0) JsaState.reports[i] = r; else JsaState.reports.unshift(r);
  } catch (_) { /* the listener catches up */ }
}

async function _saveDetailCrewSign(sigData) {
  const pending = JsaState.crewSignatures.__detail_crew;
  delete JsaState.crewSignatures.__detail_crew;
  if (!pending || !pending.jsaId) return;
  try {
    await signCrewLineOnServer(pending.jsaId, pending.personId, sigData, pending.seenContentMs);
    window.showToast?.(`${pending.name} signed`, 'success');
    await _jsaRefreshReport(pending.jsaId);
    if (currentDetailJsaId === pending.jsaId) viewJSADetail(pending.jsaId);
  } catch (e) {
    console.error('[JSA] crew sign error:', e);
    window.showToast?.(e.message || 'Could not save the signature', 'error');
  }
}

function viewJSADetail(jsaId, opts) {
  const report = JsaState.reports.find(r => r.id === jsaId);
  if (!report) return false; // TASK-794: signal "not hydrated yet" so callers can retry

  currentDetailJsaId = jsaId;
  currentDetailReport = report;
  const container = document.getElementById('jsa-detail-content');
  if (!container) return false;

  currentDetailSignIns = null;
  if (report.formMode === 'xto') {
    container.innerHTML = getXTODetailHTML(report);
  } else if (report.formMode === 'template') {
    container.innerHTML = getTemplateDetailHTML(report); // TASK-4839
  } else {
    container.innerHTML = getTITSDetailHTML(report);
  }

  // TASK-312 — Footer cleanup per DESIGN.md §Buttons:
  // - One clear primary CTA (Print), everything else secondary
  // - Hierarchy via spatial grouping: dismiss left, actions right
  // - 44px min touch target (drop btn-sm)
  // - aria-labels for icon-only at small widths
  // - Mobile wraps the action group as a block while Close stays separate
  const actionsBar = document.getElementById('jsa-detail-actions');
  // TASK-9714 — a PIN driver may get the signing link only for a JSA they
  // filed (the server enforces the same); members see it on every JSA.
  const canGetSigs = window.AppState?.driverModePin !== true
    || (!!getViewerPersonId() && report.submittedByPersonId === getViewerPersonId());
  const sigBtn = canGetSigs ? `
    <button type="button" class="btn btn-secondary" aria-label="Get signing link for this JSA" onclick="window.JSAModule.showJsaSignatureLink('${window.escJsAttr(jsaId)}')">
      <span class="material-icons" style="font-size:16px;">qr_code_2</span><span>Get Signatures</span>
    </button>` : '';
  const pdfBtn = `
    <button type="button" class="btn btn-secondary" aria-label="Download JSA report as PDF" onclick="window.JSAModule.downloadJsaPdf()">
      <span class="material-icons" style="font-size:16px;">download</span><span>PDF</span>
    </button>`;
  const emailBtn = `
    <button type="button" class="btn btn-secondary" aria-label="Email JSA report" onclick="window.JSAModule.showJsaEmailModal()">
      <span class="material-icons" style="font-size:16px;">email</span><span>Email</span>
    </button>`;
  const closeBtn = `
    <button type="button" class="btn btn-secondary" onclick="window.JSAModule.closeDetailModal()">Close</button>`;
  if (actionsBar) {
    // TASK-312 v3 — inline styles removed; styles.css #jsa-detail-actions
    // rules now own the layout (desktop horizontal, mobile stacked with
    // Print on top + Close at bottom). Clears any stale inline cssText
    // from earlier renders so the CSS rules apply cleanly.
    actionsBar.style.cssText = '';
    const headerEditBtn = document.getElementById('jsa-header-edit-btn');
    if (headerEditBtn) {
      headerEditBtn.style.display = report.formMode === 'xto' ? '' : 'none';
    }
    const printBtn = `
      <button type="button" class="btn btn-primary" onclick="window.JSAModule.${report.formMode === 'xto' ? 'printXTOReport' : (report.formMode === 'template' ? 'printTemplateReport' : 'printTITSReport')}()">
        <span class="material-icons" style="font-size:16px;">print</span><span>Print</span>
      </button>`;
    // TASK-9511 — at 390 the five buttons stacked full-width (~35% of the
    // screen). Phone: Close + Print (primary) + a "More" menu holding PDF /
    // Email / Get Signatures. Desktop keeps the full row. Pure CSS switch
    // (ensureJsaDetailFooterStyle), so a rotate/resize needs no re-render.
    ensureJsaDetailFooterStyle();
    const moreItem = (icon, label, call) => `
          <button type="button" class="jsa-more-item" role="menuitem" onclick="window.JSAModule.closeJsaDetailMore();${call}">
            <span class="material-icons" style="font-size:18px;">${icon}</span><span>${label}</span>
          </button>`;
    actionsBar.innerHTML = `
      ${closeBtn}
      <div class="jsa-action-group">
        <span class="jsa-out-wide">${pdfBtn}${emailBtn}${sigBtn}</span>
        <span class="jsa-more-wrap">
          <button type="button" class="btn btn-secondary jsa-more-btn" aria-haspopup="menu" aria-expanded="false" aria-label="More actions" onclick="event.stopPropagation();window.JSAModule.toggleJsaDetailMore()">
            <span class="material-icons" style="font-size:18px;">more_vert</span><span>More</span>
          </button>
          <div class="jsa-more-menu hidden" id="jsa-detail-more-menu" role="menu">
            ${moreItem('download', 'Download PDF', 'window.JSAModule.downloadJsaPdf()')}
            ${moreItem('email', 'Email', 'window.JSAModule.showJsaEmailModal()')}
            ${canGetSigs ? moreItem('qr_code_2', 'Get Signatures', `window.JSAModule.showJsaSignatureLink('${window.escJsAttr(jsaId)}')`) : ''}
          </div>
        </span>
        ${printBtn}
      </div>
    `;
  }

  document.getElementById('modal-jsa-detail')?.classList.remove('hidden');

  // Fetch external signers (subcollection) on demand. Only fires when
  // the parent doc has externalSignersCount > 0.
  if ((report.externalSignersCount || 0) > 0 && report.formMode !== 'xto') {
    loadExternalSignersForDetail(jsaId);
  }
  // TASK-4839 — template sheets keep crew sign-ins in a subcollection; load them
  // on demand (the parent only carries signInCount + names).
  if (report.formMode === 'template' && (report.signInCount || 0) > 0) {
    refreshDetailSignIns(jsaId);
  }
  // TASK-10024 — "Open to sign" lands on the line to sign, not the top of the report.
  if (opts && opts.focusSign) setTimeout(_jsaFocusSignLine, 60);
  return true; // TASK-794: opened successfully
}

// TASK-9511 — the detail footer's phone layout. Injected once (jsa.js owns this
// surface; styles.css's #jsa-detail-actions rules stay as the desktop base).
// Selectors are prefixed with #modal-jsa-detail so they outrank the base rules.
function ensureJsaDetailFooterStyle() {
  if (document.getElementById('jsa-9511-footer-style')) return;
  const st = document.createElement('style');
  st.id = 'jsa-9511-footer-style';
  st.textContent = `
#modal-jsa-detail #jsa-detail-actions .jsa-out-wide { display: contents; }
#modal-jsa-detail #jsa-detail-actions .jsa-more-wrap { display: none; position: relative; }
#modal-jsa-detail #jsa-detail-actions .jsa-more-menu { position: absolute; bottom: 100%; right: 0; margin-bottom: 6px; min-width: 210px; background: var(--bg-card, #fff); border: 1px solid var(--border-light); border-radius: 10px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18); padding: 4px; z-index: 20; }
#modal-jsa-detail #jsa-detail-actions .jsa-more-menu.hidden { display: none; }
#modal-jsa-detail #jsa-detail-actions .jsa-more-item { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 44px; padding: 8px 12px; border: none; background: transparent; color: var(--text-primary); font-size: 0.95rem; font-weight: 600; text-align: left; border-radius: 8px; cursor: pointer; }
#modal-jsa-detail #jsa-detail-actions .jsa-more-item:hover { background: var(--bg-elevated, #F1F5F9); }
@media (max-width: 560px) {
  #modal-jsa-detail #jsa-detail-actions { flex-direction: row; flex-wrap: nowrap; align-items: center; gap: 8px; }
  #modal-jsa-detail #jsa-detail-actions > * { width: auto; }
  #modal-jsa-detail #jsa-detail-actions .jsa-action-group { flex-direction: row; flex-wrap: nowrap; flex: 1; width: auto; gap: 8px; justify-content: flex-end; }
  #modal-jsa-detail #jsa-detail-actions .jsa-out-wide { display: none; }
  #modal-jsa-detail #jsa-detail-actions .jsa-more-wrap { display: inline-block; }
  #modal-jsa-detail #jsa-detail-actions .jsa-action-group .btn-primary { order: 0; flex: 1; min-height: 44px; }
  #modal-jsa-detail #jsa-detail-actions .btn { min-height: 44px; }
}`;
  document.head.appendChild(st);
}

function closeJsaDetailMore() {
  const m = document.getElementById('jsa-detail-more-menu');
  if (m) m.classList.add('hidden');
  document.querySelector('#jsa-detail-actions .jsa-more-btn')?.setAttribute('aria-expanded', 'false');
}

function toggleJsaDetailMore() {
  const m = document.getElementById('jsa-detail-more-menu');
  if (!m) return;
  const opening = m.classList.contains('hidden');
  m.classList.toggle('hidden', !opening);
  document.querySelector('#jsa-detail-actions .jsa-more-btn')?.setAttribute('aria-expanded', opening ? 'true' : 'false');
  if (opening) {
    // Any tap outside the menu closes it (one-shot listener, capture phase).
    setTimeout(() => {
      document.addEventListener('click', function onDoc(e) {
        // The More button itself toggles; anything else outside the menu closes it.
        const inside = e.target && e.target.closest && e.target.closest('#jsa-detail-more-menu, #jsa-detail-actions .jsa-more-btn');
        if (!inside) closeJsaDetailMore();
        document.removeEventListener('click', onDoc, true);
      }, true);
    }, 0);
  }
}

async function loadExternalSignersForDetail(jsaId) {
  const tenantId = window.AppState?.tenant;
  const listEl = document.getElementById('jsa-external-signers-list');
  if (!tenantId || !jsaId || !listEl) return;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenantId, 'jsaReports', jsaId, 'externalSigners'));
    const signers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    if (signers.length === 0) {
      listEl.innerHTML = 'No external signers yet.';
      return;
    }
    signers.sort((a, b) => {
      const ta = a.signedAt?.toDate?.()?.getTime?.() || 0;
      const tb = b.signedAt?.toDate?.()?.getTime?.() || 0;
      return tb - ta;
    });
    listEl.innerHTML = `
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${signers.map(s => {
          const when = s.signedAt?.toDate?.()?.toLocaleString?.('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) || '';
          const hasGps = s.lat != null && s.lng != null;
          const role = s.role ? ` · ${escapeHtml(s.role)}` : '';
          const company = s.company ? ` · ${escapeHtml(s.company)}` : '';
          return `
            <div style="border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:8px 10px;background:#fff;">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
                <div style="min-width:0;flex:1;">
                  <strong style="color:var(--text-primary);">${escapeHtml(s.name || '(unnamed)')}</strong>
                  <span style="font-size:0.78rem;color:var(--text-secondary);">${role}${company}</span>
                </div>
                <div style="font-size:0.72rem;color:var(--text-secondary);display:flex;align-items:center;gap:6px;">
                  ${hasGps ? '<span title="Site-verified GPS" style="color:#059669;">📍</span>' : ''}
                  ${escapeHtml(when)}
                </div>
              </div>
              ${s.signaturePngB64 ? `<div style="margin-top:6px;border-bottom:1px solid var(--border-light);min-height:36px;display:flex;align-items:flex-end;padding-bottom:2px;">
                <img src="${escapeHtml(s.signaturePngB64)}" alt="Signature" style="max-height:36px;max-width:100%;">
              </div>` : ''}
              ${s.email ? `<div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">${escapeHtml(s.email)}</div>` : ''}
            </div>`;
        }).join('')}
      </div>`;
  } catch (e) {
    console.error('[JSA] loadExternalSigners error:', e);
    listEl.innerHTML = `<span style="color:var(--action-danger);">Could not load signers: ${escapeHtml(e?.message || 'unknown error')}</span>`;
  }
}

function getTITSDetailHTML(report) {
  const date = report.date?.toDate?.() || new Date(report.date);

  const hazardLabels = (report.hazards || []).map(id => {
    const h = JSA_HAZARDS.find(x => x.id === id);
    return h ? h.label : id;
  });

  const ppeLabels = (report.ppeEquipment || []).map(id => {
    const p = JSA_PPE.find(x => x.id === id);
    return p ? p.label : id;
  });

  // Backwards compat: lifeSavingRulesTopics (array, new) vs lifeSavingRulesTopic (string, legacy)
  let topicsHTML = '';
  if (report.lifeSavingRulesTopics && Array.isArray(report.lifeSavingRulesTopics) && report.lifeSavingRulesTopics.length > 0) {
    // New format: array of slugified topic IDs — map back to labels
    const topicLabels = report.lifeSavingRulesTopics.map(slug => {
      const rule = LIFE_SAVING_RULES.find(r => slugify(r) === slug);
      return rule || slug;
    });
    topicsHTML = `
      <div class="jsa-detail-chips" style="margin-bottom:4px;">
        ${topicLabels.map(l => `<span class="jsa-detail-chip">${escapeHtml(l)}</span>`).join('')}
      </div>
    `;
  } else if (report.lifeSavingRulesTopic) {
    topicsHTML = `<p><strong>Life Saving Rule:</strong> ${escapeHtml(report.lifeSavingRulesTopic)}</p>`;
  } else {
    topicsHTML = '<p style="color:var(--text-secondary);">No topics selected</p>';
  }

  // Emergency facility
  let emergencyHTML = '';
  if (report.emergencyFacility && report.emergencyFacility.name) {
    const f = report.emergencyFacility;
    emergencyHTML = `
      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Emergency Medical Facility</h4>
        <p><strong>Name:</strong> ${escapeHtml(f.name)}</p>
        ${f.address ? `<p><strong>Address:</strong> ${escapeHtml(f.address)}</p>` : ''}
        ${f.phone ? `<p><strong>Phone:</strong> ${escapeHtml(f.phone)}</p>` : ''}
        ${f.distance != null ? `<p><strong>Distance:</strong> ${f.distance.toFixed(1)} miles</p>` : ''}
      </div>
    `;
  }

  return `
    ${_jsaRevisionBannerHTML(report)}
    <div style="padding: 16px;">
      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Job & Header</h4>
        <div class="jsa-detail-grid">
          <div><strong>Customer:</strong> ${escapeHtml(report.customerName || report.jobTitle || 'N/A')}</div>
          <div><strong>Date:</strong> ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          <div><strong>Job:</strong> ${escapeHtml(report.jobTitle || report.locationArea || 'N/A')}</div>
          <div><strong>Supervisor:</strong> ${escapeHtml(report.supervisorName || 'N/A')}</div>
          <div><strong>Person Responsible:</strong> ${escapeHtml(report.personResponsible || 'N/A')}</div>
          ${report.totalHours ? `<div><strong>Total Hours:</strong> ${report.totalHours}</div>` : ''}
          <div><strong>Permit Required:</strong> ${report.permitRequired ? 'Yes' : 'No'}</div>
        </div>
      </div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Hazards Identified</h4>
        ${hazardLabels.length > 0 ? `
          <div class="jsa-detail-chips">
            ${hazardLabels.map(l => `<span class="jsa-detail-chip">${escapeHtml(l)}</span>`).join('')}
          </div>
        ` : '<p style="color:var(--text-secondary);">No hazards identified</p>'}
        ${report.hazardOther ? `<p style="margin-top:4px;"><strong>Other:</strong> ${escapeHtml(report.hazardOther)}</p>` : ''}
      </div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Tasks / Hazards / Controls</h4>
        ${(report.tasks || []).length > 0 ? `
          <table class="jsa-detail-table">
            <thead><tr><th>Task</th><th>Potential Hazards</th><th>Control Measures</th></tr></thead>
            <tbody>
              ${report.tasks.map(t => `
                <tr>
                  <td>${escapeHtml(t.task || '')}</td>
                  <td>${escapeHtml(t.potentialHazards || '')}</td>
                  <td>${escapeHtml(t.controlMeasures || '')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : '<p style="color:var(--text-secondary);">No tasks documented</p>'}
      </div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">PPE Equipment</h4>
        <p><strong>PPE Required:</strong> ${report.ppeRequired ? 'Yes' : 'No'}</p>
        ${ppeLabels.length > 0 ? `
          <div class="jsa-detail-chips" style="margin-top:4px;">
            ${ppeLabels.map(l => `<span class="jsa-detail-chip">${escapeHtml(l)}</span>`).join('')}
          </div>
        ` : ''}
        ${report.ppeOther ? `<p style="margin-top:4px;"><strong>Other:</strong> ${escapeHtml(report.ppeOther)}</p>` : ''}
      </div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Tailgate Meeting</h4>
        ${topicsHTML}
        <p><strong>Discussion:</strong> ${escapeHtml(report.tailgateItemsDiscussed || 'N/A')}</p>
      </div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Work Plan</h4>
        <p><strong>One-Call / 811:</strong> ${report.oneCallCompleted ? 'Completed' : 'No'}${report.oneCallNumber ? ` &mdash; #${escapeHtml(report.oneCallNumber)}` : ''}</p>
        <p><strong>Work Layout:</strong> ${escapeHtml(report.workLayoutPlan || 'N/A')}</p>
        <p><strong>Muster Point:</strong> ${escapeHtml(report.musterPoint || 'N/A')}</p>
      </div>

      ${emergencyHTML}

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Crew Acknowledgments (${(report.crewAcknowledgments || []).length})</h4>
        ${(report.crewAcknowledgments || []).length > 0 ? `
          <div class="jsa-detail-crew">
            ${report.crewAcknowledgments.map(ack => {
              // TASK-10024 — the sign control the crew never had on a filed JSA.
              const role = _jsaCrewSignRole(report, ack);
              const mine = role === 'own';
              const signBtn = role ? `
                  <button type="button" class="btn ${ack.signature ? 'btn-secondary' : 'btn-primary'} jsa-crew-sign-btn" data-person-id="${escapeHtml(String(ack.personId || ''))}" style="margin-left:auto;min-height:44px;display:inline-flex;align-items:center;gap:6px;white-space:nowrap;" onclick="window.JSAModule.openDetailCrewSign('${window.escJsAttr(String(ack.personId || ''))}')">
                    <span class="material-icons" style="font-size:18px;">draw</span>${ack.signature ? 'Re-sign' : (mine ? 'Sign' : 'Sign for them')}
                  </button>` : '';
              return `
              <div class="jsa-detail-crew-member${mine ? ' jsa-crew-mine' : ''}" data-person-id="${escapeHtml(String(ack.personId || ''))}" style="flex-direction:column;align-items:stretch;gap:4px;padding:8px 12px;border:${mine ? '2px solid var(--brand-blue)' : '1px solid var(--border-light)'};border-radius:var(--radius-sm);margin-bottom:8px;">
                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                  <span class="material-icons" style="font-size:16px;color:${ack.signature ? 'var(--action-success)' : 'var(--text-secondary)'};">
                    ${ack.signature ? 'check_circle' : 'radio_button_unchecked'}
                  </span>
                  <strong>${escapeHtml(ack.name)}</strong>${mine ? ' <span style="font-size:0.75rem;font-weight:600;color:var(--brand-blue);">(you)</span>' : ''}
                  ${signBtn}
                </div>
                <div style="display:flex;align-items:center;gap:12px;margin-top:2px;">
                  <span style="font-size:0.8rem;color:var(--text-secondary);white-space:nowrap;">Print Name:</span>
                  <div style="flex:1;border-bottom:1px solid var(--border-light);padding-bottom:2px;font-size:0.85rem;">${escapeHtml(ack.name)}</div>
                </div>
                <div style="display:flex;align-items:center;gap:12px;">
                  <span style="font-size:0.8rem;color:var(--text-secondary);white-space:nowrap;">Signature:</span>
                  <div style="flex:1;border-bottom:1px solid var(--border-light);min-height:40px;display:flex;align-items:flex-end;padding-bottom:2px;">
                    ${ack.signature ? `<img src="${escapeHtml(ack.signature)}" class="jsa-signature-mini" alt="Signature" style="max-height:36px;">` : '<span style="color:var(--text-secondary);font-size:0.8rem;">Not signed</span>'}
                  </div>
                </div>
                ${_jsaSignedByNote(ack)}
              </div>
            `;
            }).join('')}
          </div>
        ` : '<p style="color:var(--text-secondary);">No crew acknowledgments</p>'}
        ${_jsaAddMyLineHTML(report)}
      </div>

      <div class="jsa-detail-section" id="jsa-external-signers-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">
          External Signers
          ${(report.externalSignersCount || 0) > 0 ? `<span style="font-size:0.8rem;font-weight:400;color:var(--text-secondary);">(${report.externalSignersCount})</span>` : ''}
        </h4>
        <div id="jsa-external-signers-list" style="font-size:0.85rem;color:var(--text-secondary);">
          ${(report.externalSignersCount || 0) > 0 ? 'Loading…' : 'No external signers yet. Use <strong>Get Signatures</strong> below to share a link with anyone on site.'}
        </div>
      </div>
    </div>
  `;
}

function getXTODetailHTML(report) {
  const date = report.date?.toDate?.() || new Date(report.date);
  const lsra = report.lsraAssessments || {};

  return `
    ${_jsaRevisionBannerHTML(report)}
    <div style="padding: 16px;">
      <div style="display:inline-block;padding:3px 10px;border-radius:12px;background:var(--brand-blue);color:#fff;font-size:0.8rem;font-weight:600;margin-bottom:12px;">Oilfield</div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Header</h4>
        <div class="jsa-detail-grid">
          <div><strong>JSA No:</strong> ${escapeHtml(report.jsaNumber || 'N/A')}</div>
          <div><strong>Date:</strong> ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          <div><strong>Unit / Area:</strong> ${escapeHtml(report.unitArea || 'N/A')}</div>
          <div><strong>Location:</strong> ${escapeHtml(report.specificLocation || 'N/A')}</div>
        </div>
      </div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Signatures</h4>
        <div style="display:flex;gap:16px;flex-wrap:wrap;">
          <div style="flex:1;min-width:150px;">
            <p style="font-weight:600;margin-bottom:4px;">JSA Issuer</p>
            ${report.jsaIssuer?.signature
              ? `<img src="${escapeHtml(report.jsaIssuer.signature)}" class="jsa-signature-mini" alt="Issuer Signature" style="max-height:50px;">`
              : '<p style="color:var(--text-secondary);font-size:0.85rem;">Not signed</p>'
            }
          </div>
          <div style="flex:1;min-width:150px;">
            <p style="font-weight:600;margin-bottom:4px;">JSA Receiver</p>
            ${report.jsaReceiver?.signature
              ? `<img src="${escapeHtml(report.jsaReceiver.signature)}" class="jsa-signature-mini" alt="Receiver Signature" style="max-height:50px;">`
              : '<p style="color:var(--text-secondary);font-size:0.85rem;">Not signed</p>'
            }
          </div>
        </div>
      </div>

      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:2px;">Life Saving Rule Assessment</h4>
        <div style="font-size:0.75rem;font-style:italic;color:var(--text-secondary);margin-bottom:8px;">Discuss / Verify / Confirm the items below for each applicable rule.</div>
        ${XTO_LSRA_CARDS.map(card => {
          const check = lsra[card.key];
          if (!check || !check.applicable) return '';
          const checkedIndexes = check.items || [];
          return `
            <div style="margin-bottom:8px;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
              <div style="display:flex;align-items:center;gap:6px;font-weight:600;color:var(--text-primary);">
                <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">${card.icon}</span>
                <div style="flex:1;">
                  ${card.title}
                  ${card.subtitle ? `<div style="font-weight:400;font-size:0.75rem;color:var(--text-secondary);">${card.subtitle}</div>` : ''}
                </div>
                <span class="material-icons" style="font-size:16px;color:var(--action-success);">check_circle</span>
              </div>
              ${checkedIndexes.length > 0 ? `
                <div style="margin-top:4px;padding-left:24px;">
                  ${checkedIndexes.map(i => {
                    const item = card.checklist[i];
                    if (!item) return '';
                    return `
                      <div style="font-size:0.85rem;color:var(--text-secondary);display:flex;align-items:flex-start;gap:4px;margin-bottom:2px;">
                        <span class="material-icons" style="font-size:14px;color:var(--action-success);margin-top:2px;">check</span>
                        <div>
                          ${escapeHtml(item.text)}
                          ${item.subitems ? `<div style="padding-left:8px;margin-top:2px;">${item.subitems.map(s => `<div style="font-size:0.8rem;color:var(--text-secondary);">• ${escapeHtml(s)}</div>`).join('')}</div>` : ''}
                        </div>
                      </div>
                    `;
                  }).join('')}
                </div>
              ` : ''}
            </div>
          `;
        }).join('')}
        ${Object.values(lsra).filter(c => c.applicable).length === 0 ? '<p style="color:var(--text-secondary);">No LSRA items marked as applicable</p>' : ''}
      </div>

      ${report.jobDescription ? `
      <div class="jsa-detail-section">
        <h4 style="color:var(--brand-blue);margin-bottom:8px;">Job Description</h4>
        <p>${escapeHtml(report.jobDescription)}</p>
      </div>
      ` : ''}
    </div>
  `;
}

// ============================================
// TITS PRINT
// ============================================
async function printTITSReport() {
  const report = currentDetailReport;
  if (!report || report.formMode === 'xto') return;
  // TASK-9714 (COD) — same signer record as the template print: crew rows get
  // their time + "Signature missing" for an inkless PNG, and the QR / link
  // signers are listed. Window opens inside the tap, before any await.
  const w = window.open('', '_blank');
  if (w) w.document.write('<p style="font-family:Arial,sans-serif;padding:20px;">Loading JSA…</p>');
  const crewAcks = Array.isArray(report.crewAcknowledgments) ? report.crewAcknowledgments : [];
  const external = await loadExternalSignersForPrint(report.id);
  const inkFlags = await Promise.all([...crewAcks.map(a => jsaPngHasInk(a.signature)), ...external.rows.map(r => jsaPngHasInk(r.signaturePngB64))]);

  const date = report.date?.toDate?.() || new Date(report.date);
  const dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });

  const hazardLabels = (report.hazards || []).map(id => {
    const h = JSA_HAZARDS.find(x => x.id === id);
    return h ? h.label : id;
  });

  const ppeLabels = (report.ppeEquipment || []).map(id => {
    const p = JSA_PPE.find(x => x.id === id);
    return p ? p.label : id;
  });

  let topicLabels = [];
  if (report.lifeSavingRulesTopics && Array.isArray(report.lifeSavingRulesTopics) && report.lifeSavingRulesTopics.length > 0) {
    topicLabels = report.lifeSavingRulesTopics.map(slug => {
      const rule = LIFE_SAVING_RULES.find(r => slugify(r) === slug);
      return rule || slug;
    });
  } else if (report.lifeSavingRulesTopic) {
    topicLabels = [report.lifeSavingRulesTopic];
  }

  const tasksHTML = (report.tasks || []).length > 0 ? `
    <table>
      <tr><th>Task</th><th>Potential Hazards</th><th>Control Measures</th></tr>
      ${report.tasks.map(t => `<tr><td>${escapeHtml(t.task || '')}</td><td>${escapeHtml(t.potentialHazards || '')}</td><td>${escapeHtml(t.controlMeasures || '')}</td></tr>`).join('')}
    </table>` : '';

  const crewHTML = crewAcks.map((ack, i) => {
    const signed = !!ack.signature && inkFlags[i];
    const when = _jsaPrintSignedAt(ack.acknowledgedAt);
    return `
    <div class="crew-row">
      <span class="sig-icon">${signed ? '\u2611' : '\u2610'}</span>
      <div class="crew-info">
        <div class="crew-name">${escapeHtml(ack.name)}${when ? ` <span style="font-weight:normal;color:#555;font-size:9px;">· ${escapeHtml(when)}</span>` : ''}${ack.signedBy && ack.signedBy.name && ack.signedBy.personId !== ack.personId && ack.signature ? ` <span style="font-weight:normal;color:#555;font-size:9px;">(signed by ${escapeHtml(ack.signedBy.name)})</span>` : ''}</div>
        ${signed ? `<img src="${escapeHtml(ack.signature)}" class="sig-img">` : (ack.signature ? '<span style="color:#b91c1c;font-weight:bold;font-size:10px;">Signature missing</span>' : '<div class="sig-line"></div>')}
      </div>
    </div>`;
  }).join('');
  const extRows = external.rows.map((r, i) => ({ ...r, _inkless: !inkFlags[crewAcks.length + i] }));
  const extExpected = Number(report.externalSignersCount) || 0;
  let externalHTML = '';
  if (external.denied && extExpected > 0) {
    externalHTML = `<div class="section"><div class="section-title">Signed via link / QR (${extExpected})</div>
      <div style="font-size:10px;">${extExpected} ${extExpected === 1 ? 'person' : 'people'} signed through the link. Your login can't list them here — download the PDF for the full signed record.</div></div>`;
  } else if (extRows.length) {
    externalHTML = `<div class="section"><div class="section-title">Signed via link / QR (${extRows.length})</div>${extRows.map(r => {
      const detail = [r.company, r.role].filter(x => typeof x === 'string' && x.trim()).join(' · ');
      const when = _jsaPrintSignedAt(r.signedAt);
      return `<div class="crew-row"><span class="sig-icon">${r._inkless ? '\u2610' : '\u2611'}</span><div class="crew-info">
        <div class="crew-name">${escapeHtml(r.name || '')}${detail ? ` <span style="font-weight:normal;color:#555;font-size:9px;">${escapeHtml(detail)}</span>` : ''}${when ? ` <span style="font-weight:normal;color:#555;font-size:9px;">· ${escapeHtml(when)}</span>` : ''}</div>
        ${r._inkless ? '<span style="color:#b91c1c;font-weight:bold;font-size:10px;">Signature missing</span>' : `<img src="${escapeHtml(r.signaturePngB64)}" class="sig-img">`}
      </div></div>`;
    }).join('')}</div>`;
  }

  const emergencyFac = report.emergencyFacility;

  const html = `<!DOCTYPE html>
<html><head>
<title>JSA - ${escapeHtml(report.customerName || report.jobTitle || 'Report')}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:Arial,sans-serif;font-size:11px;color:#000;padding:16px 20px;}
h2{font-size:16px;margin-bottom:12px;text-align:center;text-transform:uppercase;letter-spacing:1px;}
.header-grid{display:grid;grid-template-columns:1fr 1fr;gap:4px 16px;margin-bottom:12px;padding:8px 10px;border:1px solid #ccc;border-radius:4px;background:#fafafa;}
.header-grid div{padding:2px 0;}
.header-grid strong{color:#333;}
.section{margin-bottom:10px;}
.section-title{font-weight:bold;font-size:12px;color:#0077b6;border-bottom:1px solid #0077b6;padding-bottom:2px;margin-bottom:6px;}
.chip-list{display:flex;flex-wrap:wrap;gap:4px;}
.chip{display:inline-block;padding:2px 8px;border-radius:10px;background:#e8f4f8;font-size:10px;border:1px solid #b0d8e8;}
table{width:100%;border-collapse:collapse;margin:6px 0;}
th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;font-size:10px;}
th{background:#f0f0f0;font-weight:bold;}
.crew-row{display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid #eee;}
.sig-icon{font-size:14px;width:18px;text-align:center;}
.crew-info{flex:1;display:flex;align-items:center;gap:12px;}
.crew-name{font-weight:bold;min-width:120px;font-size:11px;}
.sig-img{max-height:28px;}
.sig-line{flex:1;border-bottom:1px solid #ccc;min-width:80px;height:20px;}
.footer{text-align:center;font-size:8px;color:#999;margin-top:12px;border-top:1px solid #eee;padding-top:6px;}
.no-print{text-align:center;margin-top:16px;}
@media print{.no-print{display:none!important;}}
@page{margin:10mm;}
</style>
</head><body>
<h2>Job Safety Analysis (JSA)</h2>

<div class="header-grid">
  <div><strong>Customer:</strong> ${escapeHtml(report.customerName || report.jobTitle || 'N/A')}</div>
  <div><strong>Date:</strong> ${dateStr}</div>
  <div><strong>Job:</strong> ${escapeHtml(report.jobTitle || report.locationArea || 'N/A')}</div>
  <div><strong>Supervisor:</strong> ${escapeHtml(report.supervisorName || 'N/A')}</div>
  <div><strong>Person Responsible:</strong> ${escapeHtml(report.personResponsible || 'N/A')}</div>
  ${report.totalHours ? `<div><strong>Total Hours:</strong> ${report.totalHours}</div>` : ''}
  <div><strong>Permit Required:</strong> ${report.permitRequired ? 'Yes' : 'No'}</div>
</div>

${hazardLabels.length > 0 ? `
<div class="section">
  <div class="section-title">Hazards Identified</div>
  <div class="chip-list">${hazardLabels.map(l => `<span class="chip">${escapeHtml(l)}</span>`).join('')}</div>
  ${report.hazardOther ? `<div style="margin-top:4px;font-size:10px;"><strong>Other:</strong> ${escapeHtml(report.hazardOther)}</div>` : ''}
</div>` : ''}

${tasksHTML ? `
<div class="section">
  <div class="section-title">Tasks / Hazards / Controls</div>
  ${tasksHTML}
</div>` : ''}

${ppeLabels.length > 0 ? `
<div class="section">
  <div class="section-title">PPE Equipment</div>
  <div class="chip-list">${ppeLabels.map(l => `<span class="chip">${escapeHtml(l)}</span>`).join('')}</div>
  ${report.ppeOther ? `<div style="margin-top:4px;font-size:10px;"><strong>Other:</strong> ${escapeHtml(report.ppeOther)}</div>` : ''}
</div>` : ''}

${topicLabels.length > 0 ? `
<div class="section">
  <div class="section-title">Tailgate Meeting</div>
  <div class="chip-list">${topicLabels.map(l => `<span class="chip">${escapeHtml(l)}</span>`).join('')}</div>
  ${report.tailgateItemsDiscussed ? `<div style="margin-top:4px;font-size:10px;"><strong>Discussion:</strong> ${escapeHtml(report.tailgateItemsDiscussed)}</div>` : ''}
</div>` : ''}

<div class="section">
  <div class="section-title">Work Plan</div>
  <div style="font-size:10px;">
    <div><strong>One-Call / 811:</strong> ${report.oneCallCompleted ? 'Completed' : 'No'}${report.oneCallNumber ? ` \u2014 #${escapeHtml(report.oneCallNumber)}` : ''}</div>
    ${report.workLayoutPlan ? `<div><strong>Work Layout:</strong> ${escapeHtml(report.workLayoutPlan)}</div>` : ''}
    ${report.musterPoint ? `<div><strong>Muster Point:</strong> ${escapeHtml(report.musterPoint)}</div>` : ''}
  </div>
</div>

${emergencyFac && emergencyFac.name ? `
<div class="section">
  <div class="section-title">Emergency Medical Facility</div>
  <div style="font-size:10px;">
    <div><strong>Name:</strong> ${escapeHtml(emergencyFac.name)}</div>
    ${emergencyFac.address ? `<div><strong>Address:</strong> ${escapeHtml(emergencyFac.address)}</div>` : ''}
    ${emergencyFac.phone ? `<div><strong>Phone:</strong> ${escapeHtml(emergencyFac.phone)}</div>` : ''}
    ${emergencyFac.distance != null ? `<div><strong>Distance:</strong> ${emergencyFac.distance.toFixed(1)} miles</div>` : ''}
  </div>
</div>` : ''}

<div class="section">
  <div class="section-title">Crew Acknowledgments (${(report.crewAcknowledgments || []).length})</div>
  ${crewHTML || '<div style="font-size:10px;color:#999;">No crew acknowledgments</div>'}
</div>
${externalHTML}

<div class="footer">Generated by DispatchDeck on ${new Date().toLocaleDateString()}</div>

<div class="no-print">
  <button onclick="window.print()" style="padding:10px 24px;font-size:14px;cursor:pointer;background:#0077b6;color:#fff;border:none;border-radius:6px;margin-right:8px;">
    Print / Save as PDF
  </button>
  <button onclick="window.close()" style="padding:10px 24px;font-size:14px;cursor:pointer;background:#666;color:#fff;border:none;border-radius:6px;">
    Close
  </button>
</div>
</body></html>`;

  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
  }
}

// ============================================
// XTO PRINT (Founder Only)
// ============================================
function printXTOReport() {
  const report = currentDetailReport;
  if (!report || report.formMode !== 'xto') return;

  const date = report.date?.toDate?.() || new Date(report.date);
  const dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const lsra = report.lsraAssessments || {};

  const lsraCardsHTML = XTO_LSRA_CARDS.map(card => {
    const check = lsra[card.key];
    const isApplicable = check?.applicable;
    const checkedItems = check?.items || [];
    return `
      <div class="lsra-card ${isApplicable ? 'applicable' : ''}">
        <div class="lsra-card-header">
          <div class="lsra-icon"><span class="material-icons">${card.icon}</span></div>
          <div style="flex:1;">
            <div class="lsra-title">${card.title}</div>
            <div class="lsra-subtitle">${card.subtitle}</div>
          </div>
          <div class="lsra-checkbox">${isApplicable ? '☑' : '☐'}</div>
        </div>
        <div class="lsra-checklist">
          ${card.checklist.map((item, i) => `
            <div class="lsra-item ${checkedItems.includes(i) ? 'checked' : ''}">
              ${i + 1}.${checkedItems.includes(i) ? ' ✓' : ''} ${escapeHtml(item.text)}
              ${item.subitems ? `<ul class="lsra-subs">${item.subitems.map(s => `<li>${escapeHtml(s)}</li>`).join('')}</ul>` : ''}
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }).join('');

  const issuerSig = report.jsaIssuer?.signature
    ? `<img src="${escapeHtml(report.jsaIssuer.signature)}" style="max-height:36px;">`
    : '<span style="color:#999;">(unsigned)</span>';
  const receiverSig = report.jsaReceiver?.signature
    ? `<img src="${escapeHtml(report.jsaReceiver.signature)}" style="max-height:36px;">`
    : '<span style="color:#999;">(unsigned)</span>';

  const html = `<!DOCTYPE html>
<html><head>
<title>Oilfield JSA - ${escapeHtml(report.jsaNumber || 'Report')}</title>
<link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:Arial,sans-serif;font-size:9px;color:#000;padding:6px;}
.hdr-table{width:100%;border-collapse:collapse;margin-bottom:4px;}
.hdr-table td{border:1px solid #000;padding:2px 4px;vertical-align:top;}
.hdr-table .title{text-align:center;font-weight:bold;font-size:12px;}
.hdr-table .lbl{font-weight:bold;font-size:8px;color:#333;}
.job-desc{border:1px solid #000;padding:3px 5px;margin-bottom:3px;min-height:24px;}
.job-desc .lbl{font-weight:bold;font-size:8px;margin-bottom:2px;}
.red-banner{background:#c00;color:#fff;font-weight:bold;text-align:center;padding:3px 6px;margin-bottom:4px;font-size:7.5px;line-height:1.2;}
.lsra-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:3px;}
.lsra-card{border:1.5px solid #ccc;border-radius:2px;padding:3px 4px;page-break-inside:avoid;}
.lsra-card.applicable{border-color:#0077b6;}
.lsra-card-header{display:flex;align-items:flex-start;gap:3px;margin-bottom:2px;}
.lsra-icon{width:20px;height:20px;background:#0077b6;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;}
.lsra-icon .material-icons{font-size:12px;}
.lsra-title{font-weight:bold;color:#0077b6;font-size:8px;line-height:1.1;}
.lsra-subtitle{color:#0077b6;font-style:italic;font-size:6.5px;line-height:1.1;margin-top:0;}
.lsra-checkbox{margin-left:auto;font-size:12px;flex-shrink:0;line-height:1;}
.lsra-checklist{font-size:6.5px;line-height:1.2;}
.lsra-item{margin-bottom:0;}
.lsra-item.checked{font-weight:bold;}
.lsra-subs{padding-left:10px;margin:0;}
.lsra-subs li{list-style:disc;margin-bottom:0;font-size:6px;}
.sig-section{margin-top:4px;display:flex;gap:12px;border-top:1px solid #ccc;padding-top:3px;}
.sig-box{flex:1;text-align:center;}
.sig-box .lbl{font-weight:bold;font-size:8px;margin-bottom:1px;}
.footer{text-align:center;font-size:7px;color:#999;margin-top:4px;}
.no-print{text-align:center;margin-top:10px;}
@media print{.no-print{display:none!important;} body{padding:4px;}}
@page{size:landscape;margin:6mm;}
</style>
</head><body>
<table class="hdr-table">
  <tr>
    <td style="width:14%;"><span class="lbl">JSA No:</span><br>${escapeHtml(report.jsaNumber || '')}</td>
    <td class="title" colspan="3">JOB SAFETY ANALYSIS (JSA) FORM</td>
    <td style="width:10%;"><span class="lbl">Date:</span><br>${dateStr}</td>
  </tr>
  <tr>
    <td><span class="lbl">Unit/Area:</span><br>${escapeHtml(report.unitArea || '')}</td>
    <td><span class="lbl">Specific Location:</span><br>${escapeHtml(report.specificLocation || '')}</td>
    <td><span class="lbl">JSA Issuer:</span><br>${issuerSig}</td>
    <td><span class="lbl">JSA Receiver:</span><br>${receiverSig}</td>
    <td><span class="lbl">Date:</span><br>${dateStr}</td>
  </tr>
</table>

<div class="job-desc">
  <div class="lbl">Job Description for Tasks with Higher Potential Consequence (i.e., life altering injury, significant process safety event):</div>
  <div style="margin-top:3px;">${escapeHtml(report.jobDescription || '')}</div>
</div>

<div class="red-banner">
  Check LSRA(s) that apply and review Start Work Check(s). Complete Page 2 with any additional task steps with higher potential consequence or if there is no applicable LSRA.
</div>

<div style="font-style:italic;color:#555;font-size:7px;margin:2px 0 3px;">Discuss / Verify / Confirm the items below for each applicable rule.</div>

<div class="lsra-grid">${lsraCardsHTML}</div>

<div class="footer">Generated by DispatchDeck on ${new Date().toLocaleDateString()}</div>

<div class="no-print">
  <button onclick="window.print()" style="padding:10px 24px;font-size:14px;cursor:pointer;background:#0077b6;color:#fff;border:none;border-radius:6px;margin-right:8px;">
    Print / Save as PDF
  </button>
  <button onclick="window.close()" style="padding:10px 24px;font-size:14px;cursor:pointer;background:#666;color:#fff;border:none;border-radius:6px;">
    Close
  </button>
</div>
</body></html>`;

  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
  }
}

// ============================================
// XTO SIGNATURE UPDATE (Founder Only)
// ============================================
async function updateXTOSignature(role) {
  if (!currentDetailJsaId || !currentDetailReport) return;

  // Reuse signature modal — use 'xto_update_' prefix to distinguish from create-mode
  jsaSigCurrentPerson = `xto_update_${role}`;
  document.getElementById('jsa-sig-title').textContent = `Sign: JSA ${role === 'issuer' ? 'Issuer' : 'Receiver'}`;

  const modal = document.getElementById('modal-jsa-signature');
  modal.classList.remove('hidden');

  setTimeout(() => {
    jsaSigCanvas = document.getElementById('jsa-signature-canvas');
    if (!jsaSigCanvas) return;
    jsaSigCtx = jsaSigCanvas.getContext('2d');
    jsaSigCtx.strokeStyle = '#1a237e';
    jsaSigCtx.lineWidth = 2;
    jsaSigCtx.lineCap = 'round';
    jsaSigCtx.lineJoin = 'round';
    jsaSigCtx.fillStyle = '#ffffff';
    jsaSigCtx.fillRect(0, 0, jsaSigCanvas.width, jsaSigCanvas.height);
    jsaSigHasDrawn = false;
    jsaSigDrawing = false;
    bindJsaSignaturePad(); // TASK-9714
  }, 100);
}

// ============================================
// JSA EMAIL — works for both Standard (TITS) and Oilfield (XTO) formats
// ============================================
function showJsaEmailModal() {
  if (!currentDetailJsaId || !currentDetailReport) return;

  // TASK-9504 — a driver-PIN session can't pick an address (the server ignores
  // it); give it the office / me choice instead of a free-text field.
  if (window.AppState?.driverModePin === true) {
    openMyJsaEmailOverlay(currentDetailJsaId);
    return;
  }

  const existing = document.getElementById('jsa-email-overlay');
  if (existing) existing.remove();

  // Prefill recipient with customer email if on file (Standard JSAs only)
  const prefillEmail = currentDetailReport.formMode !== 'xto'
    ? (currentDetailReport.customerEmail || '')
    : '';
  const formatLabel = currentDetailReport.formMode === 'xto' ? 'Oilfield JSA' : 'JSA';

  const overlay = document.createElement('div');
  overlay.id = 'jsa-email-overlay';
  overlay.className = 'modal';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <div class="modal-header">
        <h3>Email ${formatLabel}</h3>
        <button class="btn-icon" onclick="document.getElementById('jsa-email-overlay').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="padding:16px;">
        <div class="form-group">
          <label for="jsa-email-input">Recipient Email</label>
          <input type="email" id="jsa-email-input" placeholder="name@company.com" value="${prefillEmail.replace(/"/g, '&quot;')}">
        </div>
        <div style="display:flex;gap:8px;margin-top:12px;">
          <button class="btn btn-secondary" style="flex:1;" onclick="document.getElementById('jsa-email-overlay').classList.add('hidden')">Cancel</button>
          <button class="btn btn-primary" style="flex:1;" id="jsa-email-send-btn" onclick="window.JSAModule.emailJsaReport()">
            <span class="material-icons" style="font-size:16px;">send</span> Send
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
}

async function emailJsaReport() {
  const emailInput = document.getElementById('jsa-email-input');
  const email = emailInput?.value?.trim();
  if (!email || !email.includes('@')) {
    window.showToast?.('Enter a valid email address', 'error');
    return;
  }

  const tenantId = window.AppState?.tenant;
  if (!tenantId || !currentDetailJsaId) return;

  const btn = document.getElementById('jsa-email-send-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Sending...'; }

  try {
    const emailJsaFn = httpsCallable(functions, 'emailJsaReport');
    // TASK-361 — thread driverToken so driver-PIN sessions can hit this
    // callable when they open the detail modal from "My Recent JSAs". No-op
    // for SPA/admin sessions (resolveJsaActor checks context.auth first).
    const res = await emailJsaFn({
      tenantId,
      jsaId: currentDetailJsaId,
      recipientEmail: email,
      driverToken: window.AppState?.driverAccessToken || '',
    });
    // TASK-9504 — a driver session's send is routed by the server (office),
    // not to the typed address; say where it actually went.
    const sentMode = res?.data?.mode;
    window.showToast?.(sentMode === 'office' ? 'Report emailed to the office' : `Report emailed to ${email}`, 'success');
    document.getElementById('jsa-email-overlay')?.classList.add('hidden');
  } catch (error) {
    console.error('[JSA] Email error:', error);
    window.showToast?.('Error sending email: ' + error.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">send</span> Send'; }
  }
}

// ============================================
// PDF DOWNLOAD
// ============================================
async function downloadPdf() {
  if (!currentDetailJsaId) return;

  const tenantId = window.AppState?.tenant;
  if (!tenantId) return;

  const btn = document.getElementById('jsa-download-btn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Generating...';
  }

  try {
    const downloadJsaPdfFn = httpsCallable(functions, 'downloadJsaPdf');
    // TASK-361 — thread driverToken (no-op for auth-context sessions)
    const result = await downloadJsaPdfFn({
      tenantId,
      jsaId: currentDetailJsaId,
      driverToken: window.AppState?.driverAccessToken || '',
    });

    if (result.data?.url) {
      window.open(result.data.url, '_blank');
    } else {
      window.showToast?.('PDF not available yet', 'error');
    }
  } catch (error) {
    console.error('[JSA] PDF download error:', error);
    window.showToast?.('Error generating PDF: ' + error.message, 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">picture_as_pdf</span> Download PDF';
    }
  }
}

// ============================================
// DATE FILTER
// ============================================
function filterByDate() {
  renderJSAList();
}

function clearDateFilter() {
  const startInput = document.getElementById('jsa-filter-start');
  const endInput = document.getElementById('jsa-filter-end');
  if (startInput) startInput.value = '';
  if (endInput) endInput.value = '';
  renderJSAList();
}

// ============================================
// JSA ARCHIVE (mirrors HR / Compliance → Safety → JSA Reports)
// ============================================
// TASK-9581 — same treatment as hr.js renderJSAReportsList, from the same LAZY
// utils/jsaListWindow.js: collapsed by default (header = toggle, count shown),
// default window = the last day, presets + customer / crew / job filters, rows
// a page at a time, Print says the range. The rows come from JsaState.reports —
// this page's existing jsaReports listener (My Recent, the open-detail sync and
// the date-filtered list read it too) — so the archive adds no Firestore read.
let _jsaArchLoadP = null;
let _jsaArchCtl = null;
function _jsaArchLoad() {
  if (!_jsaArchLoadP) {
    _jsaArchLoadP = __ddChunkWrap(import('../utils/jsaListWindow.js?v=9581002')).then(() => {
      _jsaArchCtl = window.JsaListWindow.createList({
        p: 'jsa-archive', mountId: 'jsa-archive-mount', openKey: 'dd.jsaReports.open.archive',
        sectionStyle: 'margin: 0 16px 16px;',
        h: {
          toggle: 'window.JSAModule.toggleJSAArchive()', range: 'window.JSAModule.renderJSAArchive()',
          filter: 'window.JSAModule.renderJSAArchive()', clear: 'window.JSAModule.clearJSAArchiveFilters()',
          print: 'window.JSAModule.printJSAArchive()', more: 'window.JSAModule.showMoreJSAArchive()',
          detail: 'window.JSAModule.viewJSADetail',
        },
        reports: () => JsaState.reports,
        loaded: () => !!(JsaState.reportsLoaded || JsaState.reportsFailed),
        total: () => (JsaState.reportsLoaded ? JsaState.reports.length : null),
        windowFailed: () => !!JsaState.reportsFailed, // COD r9/r10 — ONE whole-archive read covers the upcoming week too
        rowMeta: (r) => {
          const hazardCount = jsaHazardCount(r); // TASK-9714
          return { crew: r.formMode === 'template' ? (Number(r.signInCount) || 0) : (r.crewAcknowledgments?.length || 0), hazards: hazardCount };
        },
      });
      return _jsaArchCtl;
    }).catch((e) => { _jsaArchLoadP = null; console.error('[JSA] archive module failed to load:', e?.message); throw e; });
  }
  return _jsaArchLoadP;
}

function renderJSAArchive() {
  if (!document.getElementById('jsa-archive-mount')) return;
  _jsaArchLoad().then(ctl => ctl.render()).catch(() => {});
}
function toggleJSAArchive() { _jsaArchLoad().then(ctl => ctl.toggle()).catch(() => {}); }
function clearJSAArchiveFilters() { _jsaArchLoad().then(ctl => ctl.clear()).catch(() => {}); }
function showMoreJSAArchive() { _jsaArchLoad().then(ctl => ctl.more()).catch(() => {}); }
// Sync when loaded (it always is once the section shows) so window.open stays in the tap.
function printJSAArchive() { if (_jsaArchCtl) _jsaArchCtl.print(); else _jsaArchLoad().then(ctl => ctl.print()).catch(() => {}); }

// ============================================
// MODAL HELPERS
// ============================================
function closeJSAModal(opts) {
  // TASK-398: closing the form invalidates any pending edit prefill.
  bumpJsaFormGen();
  document.getElementById('modal-jsa')?.classList.add('hidden');
  // TASK-9511 — Cancel / X on a form opened from a job ("Add another JSA") goes
  // back to that job instead of stranding the user on Inspections. A successful
  // save passes {saved:true} and keeps today's behavior. One-shot either way.
  const rt = JsaState.returnTo;
  JsaState.returnTo = null;
  if (rt && (!(opts && opts.saved === true) || opts.returnToJob === true)) returnToOpeningJob(rt);
}

// TASK-9511 — route back, then reopen the job's detail modal once the job board
// has the job in memory (it re-subscribes on route change). Bounded retries;
// JobBoardModule.reopenJobDetails returns false (no toast) until the job is found.
function returnToOpeningJob(rt) {
  if (!rt || !rt.jobId) return;
  if (rt.hash && window.location.hash !== rt.hash) window.location.hash = rt.hash;
  const landedOn = window.location.hash;
  let tries = 0;
  const attempt = () => {
    // COD r1 — stop if the user moved on while the board loaded (another route,
    // or a new JSA form opened): never pop the old job over a different view.
    if (window.location.hash !== landedOn) return;
    const jsaModal = document.getElementById('modal-jsa');
    if (jsaModal && !jsaModal.classList.contains('hidden')) return;
    // TASK-10011 — a JSA started from the office goes back to the office Job
    // Details; everything else to the Job Board job, as before.
    const host = rt.surface === 'dispatch' ? window.DispatchModule : window.JobBoardModule;
    if (host && typeof host.reopenJobDetails === 'function' && host.reopenJobDetails(rt.jobId, rt.jobDayId || '') === true) return;
    if (tries++ < 20) setTimeout(attempt, 150);
  };
  setTimeout(attempt, 150);
}

function closeDetailModal() {
  document.getElementById('modal-jsa-detail')?.classList.add('hidden');
  document.getElementById('xto-email-overlay')?.remove();
  currentDetailJsaId = null;
  currentDetailReport = null;
}

// ============================================
// EDIT XTO REPORT (Founder Only)
// ============================================
function editXTOReport() {
  if (!currentDetailJsaId || !currentDetailReport) return;
  // TASK-398: new form generation — invalidate any in-flight prefill from a
  // prior edit that targets the shared #modal-jsa form.
  bumpJsaFormGen();
  const report = currentDetailReport;
  if (report.formMode !== 'xto') return;

  // Close detail modal
  document.getElementById('modal-jsa-detail')?.classList.add('hidden');

  // Set editing state
  JsaState.editingJsaId = currentDetailJsaId;
  JsaState.editingReport = report;
  JsaState.formMode = 'xto';

  // Pre-load LSRA checks from saved data
  JsaState.lsraChecks = {};
  if (report.lsraAssessments) {
    Object.entries(report.lsraAssessments).forEach(([key, val]) => {
      JsaState.lsraChecks[key] = {
        applicable: val.applicable || false,
        items: [...(val.items || [])]
      };
    });
  }

  // Pre-load signatures from saved data
  JsaState.xtoSignatures = {
    issuer: { name: report.jsaIssuer?.name || '', signature: report.jsaIssuer?.signature || null },
    receiver: { name: report.jsaReceiver?.name || '', signature: report.jsaReceiver?.signature || null }
  };

  // XTO back-page additions (2026-05-21): hazard-type categories, per-step
  // tasks with hold points, and lessons-learned text. Tolerate missing fields
  // for older reports created before this section existed.
  JsaState.hazardCategories = new Set(Array.isArray(report.hazardCategories) ? report.hazardCategories : []);
  JsaState.lessonsLearned = report.lessonsLearned || '';
  JsaState.taskRows = (Array.isArray(report.tasks) && report.tasks.length > 0)
    ? report.tasks.map(t => ({
        task: t.task || '',
        potentialHazards: t.potentialHazards || '',
        controlMeasures: t.controlMeasures || '',
        holdPoint: !!t.holdPoint
      }))
    : [{ task: '', potentialHazards: '', controlMeasures: '', holdPoint: false }];

  // Open JSA modal
  const modal = document.getElementById('modal-jsa');
  if (!modal) return;

  // Set mode toggle to Oilfield (TASK-4839: shared renderer; no template buttons on an edit)
  const toggleContainer = document.getElementById('jsa-mode-toggle-container');
  if (toggleContainer) {
    toggleContainer.innerHTML = getModeToggleHTML('xto', null, []);
  }
  setJsaModalTitle(true);

  // Render XTO form
  renderFormBody();

  // TASK-398: capture the form generation (bumped at the top of editXTOReport).
  // The XTO edit prefills the SAME shared #modal-jsa form; rapid editXTOReport(A)
  // then editXTOReport(B)/editJSAReport(B) would otherwise let A's delayed write
  // stamp A's report fields onto B's form → saveJSA persists the cross-record mix.
  const xtoGen = JsaState.jsaFormGen;
  const xtoTimer = setTimeout(() => {
    JsaState._jsaPrefillTimers = JsaState._jsaPrefillTimers.filter(t => t !== xtoTimer);
    if (JsaState.jsaFormGen !== xtoGen) return; // form moved on — abort all writes
    const jsaNumEl = document.getElementById('xto-jsa-number');
    if (jsaNumEl) jsaNumEl.value = report.jsaNumber || '';

    const dateEl = document.getElementById('xto-date');
    if (dateEl) {
      const d = report.date?.toDate?.() || new Date(report.date);
      dateEl.value = window.RecordDate.ymd(d, window.RecordDate.zone()); // TASK-9923
    }

    const unitEl = document.getElementById('xto-unit-area');
    if (unitEl) unitEl.value = report.unitArea || '';

    const locEl = document.getElementById('xto-specific-location');
    if (locEl) locEl.value = report.specificLocation || '';

    const descEl = document.getElementById('xto-job-description');
    if (descEl) descEl.value = report.jobDescription || '';

    // Restore XTO back-page hazard-category chip selection
    JsaState.hazardCategories.forEach(id => {
      const chip = document.querySelector(`.jsa-xto-hazcat-chip[data-id="${id}"]`);
      if (chip) chip.classList.add('selected');
    });

    // Restore XTO back-page task rows + Lessons Learned
    renderTaskRows();
    const lessonsEl = document.getElementById('jsa-xto-lessons-learned');
    if (lessonsEl) lessonsEl.value = JsaState.lessonsLearned || '';

    // Restore LSRA checkbox states
    Object.entries(JsaState.lsraChecks).forEach(([key, val]) => {
      if (val.applicable) {
        const checkbox = document.querySelector(`input[data-lsra="${key}"][data-field="applicable"]`);
        if (checkbox) {
          checkbox.checked = true;
          const body = document.getElementById(`lsra-body-${key}`);
          if (body) body.style.display = 'block';
        }
        // Restore checked items
        (val.items || []).forEach(idx => {
          const itemCb = document.querySelector(`input[data-lsra="${key}"][data-item="${idx}"]`);
          if (itemCb) itemCb.checked = true;
        });
      }
    });

    // Restore signature states
    if (JsaState.xtoSignatures.issuer.signature) {
      const statusEl = document.getElementById('xto-issuer-status');
      if (statusEl) statusEl.textContent = 'Signed';
      const card = document.getElementById('xto-sig-issuer');
      if (card) card.classList.add('signed');
    }
    if (JsaState.xtoSignatures.receiver.signature) {
      const statusEl = document.getElementById('xto-receiver-status');
      if (statusEl) statusEl.textContent = 'Signed';
      const card = document.getElementById('xto-sig-receiver');
      if (card) card.classList.add('signed');
    }
  }, 50);
  JsaState._jsaPrefillTimers.push(xtoTimer);

  modal.classList.remove('hidden');
}

// ============================================
// EDIT JSA REPORT (Any Mode)
// ============================================
function editJSAReport(reportId, opts) {
  // TASK-9371 — returns false when the report is not in JsaState.reports yet
  // (jobboard.js openJsaToEdit retries on that, quietly); true once the form is
  // open. Same contract viewJSADetail already has.
  const report = JsaState.reports.find(r => r.id === reportId);
  if (!report) { if (!opts?.quiet) window.showToast?.('Report not found', 'error'); return false; }
  // TASK-398: new form generation — invalidate any in-flight prefill (incl. the
  // nested #jsa-job timer) from a previous edit so it can't clobber THIS form.
  bumpJsaFormGen();

  // XTO reports use existing editXTOReport flow via detail modal
  if (report.formMode === 'xto') {
    currentDetailJsaId = reportId;
    currentDetailReport = report;
    editXTOReport();
    return true;
  }

  // TASK-4839 — template report editing. 🔴 Before this branch every non-xto
  // report fell into the Standard path below, which sets formMode='tits' and
  // saveJSA then updateDoc'd formMode:'tits' — one HR edit silently converted a
  // template sheet into a Standard one (COD Edge Case + Code Reviewer). The
  // report's OWN frozen snapshot renders the form; the live template is not read.
  if (report.formMode === 'template') {
    JsaState.editingJsaId = reportId;
    JsaState.editingReport = report;
    JsaState.prefillJobId = null;
    JsaState.prefillJobDayId = null;
    JsaState.crewSignatures = {};
    JsaState.taskRows = [];
    JsaState.selectedHazards.clear();
    JsaState.selectedPPE.clear();
    JsaState.selectedTopics.clear();
    JsaState.lsraChecks = {};
    restoreTemplateEditState(report);

    const tplModal = document.getElementById('modal-jsa');
    if (!tplModal) return;
    const tplToggle = document.getElementById('jsa-mode-toggle-container');
    if (tplToggle) tplToggle.innerHTML = getTemplateModeBadgeHTML(report.templateName || JsaState.templateSnapshot?.name, JsaState.templateSnapshot?.version);
    setJsaModalTitle(true);
    renderFormBody();
    populateCustomerDropdown(report.customerId);
    populateJobDropdown(report.customerId || '');

    // TASK-398 generation guard, same as the Standard prefill below.
    const tplGen = JsaState.jsaFormGen;
    const tplTimer = setTimeout(() => {
      JsaState._jsaPrefillTimers = JsaState._jsaPrefillTimers.filter(t => t !== tplTimer);
      if (JsaState.jsaFormGen !== tplGen) return;
      prefillTemplateFormFromReport(report);
    }, 50);
    JsaState._jsaPrefillTimers.push(tplTimer);
    tplModal.classList.remove('hidden');
    return true; // TASK-9371 contract: opened (jobboard openJsaToEdit stops retrying)
  }

  // TITS report editing
  JsaState.editingJsaId = reportId;
  JsaState.editingReport = report;
  JsaState.formMode = 'tits';
  // TASK-794: clear any new-JSA prefill so it can't leak into an edit's save.
  JsaState.prefillJobId = null;
  JsaState.prefillJobDayId = null;

  // Reset state then restore from report
  JsaState.crewSignatures = {};
  JsaState.taskRows = [];
  JsaState.selectedHazards.clear();
  JsaState.selectedPPE.clear();
  JsaState.selectedTopics.clear();
  JsaState.emergencyFacility = report.emergencyFacility || null;
  JsaState.lsraChecks = {};

  // Restore hazards
  (report.hazards || []).forEach(h => JsaState.selectedHazards.add(h));

  // Restore PPE
  (report.ppeEquipment || []).forEach(p => JsaState.selectedPPE.add(p));

  // Restore topics (founder multi-select)
  (report.lifeSavingRulesTopics || []).forEach(t => JsaState.selectedTopics.add(t));

  // Restore task rows
  JsaState.taskRows = (report.tasks && report.tasks.length > 0)
    ? report.tasks.map(t => ({ task: t.task || '', potentialHazards: t.potentialHazards || '', controlMeasures: t.controlMeasures || '' }))
    : [{ task: '', potentialHazards: '', controlMeasures: '' }];

  // Restore crew signatures
  (report.crewAcknowledgments || []).forEach(crew => {
    JsaState.crewSignatures[crew.personId] = {
      name: crew.name,
      signature: crew.signature || null,
      acknowledgedAt: crew.acknowledgedAt?.toDate?.() || null
    };
  });

  const modal = document.getElementById('modal-jsa');
  if (!modal) return;

  // Set mode toggle (TASK-4839: shared renderer; no template buttons on an edit)
  const toggleContainer = document.getElementById('jsa-mode-toggle-container');
  if (toggleContainer) {
    toggleContainer.innerHTML = getModeToggleHTML('tits', null, []);
  }
  setJsaModalTitle(true);

  // Render TITS form
  renderFormBody();

  // Populate dropdowns first, then pre-fill
  populateCustomerDropdown();
  populateSupervisorDropdown();

  // TASK-398: capture the form generation this prefill belongs to (bumped at the
  // top of editJSAReport). If the user opens a DIFFERENT report before these
  // delayed writes fire, jsaFormGen advances and we MUST abort — otherwise this
  // report's job/fields get stamped onto the other report's form and saveJSA
  // persists the cross-record corruption.
  const myGen = JsaState.jsaFormGen;
  const outerTimer = setTimeout(() => {
    JsaState._jsaPrefillTimers = JsaState._jsaPrefillTimers.filter(t => t !== outerTimer);
    if (JsaState.jsaFormGen !== myGen) return; // form moved on — abort all writes
    const el = (id) => document.getElementById(id);

    // Date
    if (el('jsa-date')) {
      const d = report.date?.toDate?.() || new Date(report.date);
      el('jsa-date').value = window.RecordDate.ymd(d, window.RecordDate.zone()); // TASK-9923
    }

    // Customer
    if (el('jsa-customer') && report.customerId) {
      _jsaSetCustomer(el('jsa-customer'), report.customerId);
      // Trigger job dropdown population
      populateJobDropdown(report.customerId);
      // TASK-398: the NESTED timer is the dangerous one the audit flagged — it
      // writes #jsa-job from the captured report.jobId 100ms later. Re-check the
      // generation again inside it (the form could move on between outer + inner).
      const innerTimer = setTimeout(() => {
        JsaState._jsaPrefillTimers = JsaState._jsaPrefillTimers.filter(t => t !== innerTimer);
        if (JsaState.jsaFormGen !== myGen) return; // form moved on — abort
        if (el('jsa-job') && report.jobId) el('jsa-job').value = report.jobId;
      }, 100);
      JsaState._jsaPrefillTimers.push(innerTimer);
    }

    // Supervisor
    if (el('jsa-supervisor') && report.supervisorId) {
      el('jsa-supervisor').value = report.supervisorId;
    }

    // Simple text fields
    if (el('jsa-person-responsible')) el('jsa-person-responsible').value = report.personResponsible || '';
    if (el('jsa-total-hours')) el('jsa-total-hours').value = report.totalHours || 8;
    if (el('jsa-permit-required')) el('jsa-permit-required').checked = report.permitRequired || false;
    if (el('jsa-hazard-other')) el('jsa-hazard-other').value = report.hazardOther || '';
    if (report.hazardOther && el('jsa-hazard-other-group')) el('jsa-hazard-other-group').classList.remove('hidden');
    if (el('jsa-ppe-required')) el('jsa-ppe-required').checked = report.ppeRequired !== false;
    if (el('jsa-ppe-section')) el('jsa-ppe-section').style.display = report.ppeRequired !== false ? '' : 'none';
    if (el('jsa-ppe-other')) el('jsa-ppe-other').value = report.ppeOther || '';
    if (report.ppeOther && el('jsa-ppe-other-group')) el('jsa-ppe-other-group').classList.remove('hidden');
    if (el('jsa-life-saving-rule')) el('jsa-life-saving-rule').value = report.lifeSavingRulesTopic || '';
    if (el('jsa-tailgate-items')) el('jsa-tailgate-items').value = report.tailgateItemsDiscussed || '';
    if (el('jsa-one-call')) el('jsa-one-call').checked = report.oneCallCompleted || false;
    if (el('jsa-one-call-number')) el('jsa-one-call-number').value = report.oneCallNumber || '';
    if (el('jsa-work-layout')) el('jsa-work-layout').value = report.workLayoutPlan || '';
    if (el('jsa-muster-point')) el('jsa-muster-point').value = report.musterPoint || '';
    if (el('jsa-facility-manual') && report.emergencyFacility?.name) el('jsa-facility-manual').value = report.emergencyFacility.name;

    // Restore chip selections
    document.querySelectorAll('.jsa-hazard-chip').forEach(c => {
      if (JsaState.selectedHazards.has(c.dataset.id)) c.classList.add('selected');
    });
    document.querySelectorAll('.jsa-ppe-chip').forEach(c => {
      if (JsaState.selectedPPE.has(c.dataset.id)) c.classList.add('selected');
    });
    document.querySelectorAll('.jsa-topic-chip').forEach(c => {
      if (JsaState.selectedTopics.has(c.dataset.id)) c.classList.add('selected');
    });

    // Render task rows and crew list
    renderTaskRows();
    renderCrewList();
  }, 50);
  JsaState._jsaPrefillTimers.push(outerTimer);

  modal.classList.remove('hidden');
  return true;
}

// ============================================
// UTILITIES
// ============================================

function getTruckDisplayFromJobDay(jobDay) {
  if (!jobDay?.trucks?.length) return '';
  return jobDay.trucks.map(ref => {
    const r = String(ref);
    const truck = _resolveFleetRef(r, JsaState.trucks);
    if (!truck) return `#${r}`;
    return window.getUnitDisplay ? window.getUnitDisplay(truck) : `#${truck.unit}`;
  }).join(', ');
}

function getReportTruckDisplay(report) {
  // Use denormalized field if present
  if (report.truckDisplay) return report.truckDisplay;
  // Fallback: try to resolve from today's jobDays
  if (report.jobDayId) {
    const jd = JsaState.jobDays.find(d => d.id === report.jobDayId);
    if (jd) return getTruckDisplayFromJobDay(jd);
  }
  return '';
}

// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js). Escapes the
// identical set (&<>"', '->&#39;) for every string/falsy input; the shared helper
// also String()-coerces, so a non-string truthy input now escapes instead of
// throwing (the former `str.replace` crashed on those) — strictly safer, never a
// rendering regression. Hoisted wrapper, window lookup deferred to call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

// ============================================
// EXPOSE TO WINDOW
// ============================================
function printFromCard(jsaId) {
  const report = JsaState.reports.find(r => r.id === jsaId);
  if (!report) return;
  currentDetailReport = report;
  currentDetailJsaId = jsaId;
  currentDetailSignIns = null;
  if (report.formMode === 'xto') { printXTOReport(); }
  else if (report.formMode === 'template') { printTemplateReport(); } // TASK-4839
  else { printTITSReport(); }
}

function goToDispatchAddJob() {
  // Close JSA modal, navigate to Dispatch
  bumpJsaFormGen(); // TASK-398: cancel any pending prefill on form teardown
  document.getElementById('modal-jsa')?.classList.add('hidden');
  const navBtn = document.querySelector('[data-module="dispatch"]');
  if (navBtn) navBtn.click();
  window.showToast?.('Add a job in Dispatch — customers are created when you add your first job', 'info');
}

function goToCRM() {
  // Close JSA modal, navigate to CRM
  bumpJsaFormGen(); // TASK-398: cancel any pending prefill on form teardown
  document.getElementById('modal-jsa')?.classList.add('hidden');
  const navBtn = document.querySelector('[data-module="crm"]');
  if (navBtn) navBtn.click();
  window.showToast?.('Add customers in CRM under the Customers tab', 'info');
}

// ============================================
// MULTI-PARTY SIGNING PORTAL — admin actions
// ============================================
// Lazy-loaded QR library. Loads on first QR render only.
// TASK-793: self-host the bundle (public/vendor/qrcode.min.js, qrcode@1.5.1).
// The CDN path it used before — qrcode@1.5.3/build/qrcode.min.js — 404s because
// the npm package dropped its prebuilt browser bundle in 1.5.2+. Self-hosting
// also makes "works offline once loaded" true on first use (oilfield PWA).
let _qrLibPromise = null;
function _loadQrLib() {
  if (_qrLibPromise) return _qrLibPromise;
  _qrLibPromise = new Promise((resolve, reject) => {
    if (window.QRCode) return resolve(window.QRCode);
    const s = document.createElement('script');
    s.src = '/vendor/qrcode.min.js';
    s.async = true;
    s.onload = () => resolve(window.QRCode);
    s.onerror = () => { _qrLibPromise = null; reject(new Error('QR library failed to load')); };
    document.head.appendChild(s);
  });
  return _qrLibPromise;
}
async function renderQrInto(targetId, url) {
  const target = document.getElementById(targetId);
  if (!target) return;
  target.innerHTML = '<div style="text-align:center;padding:14px;color:var(--text-secondary);font-size:13px;">Generating QR…</div>';
  try {
    const QR = await _loadQrLib();
    target.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;gap:8px;background:#fff;border:1px solid var(--border-light);border-radius:8px;padding:14px;">
        <canvas id="${targetId}-canvas" style="max-width:100%;"></canvas>
        <div style="font-size:11px;color:var(--text-secondary);text-align:center;line-height:1.4;">Aim a phone camera at this QR to open the signing page. Works offline once loaded.</div>
      </div>`;
    await QR.toCanvas(document.getElementById(`${targetId}-canvas`), url, { width: 220, margin: 1 });
  } catch (e) {
    console.error('[JSA] QR render error:', e);
    target.innerHTML = `<div style="text-align:center;padding:14px;color:var(--action-danger);font-size:13px;">Could not generate QR (${(e?.message || 'unknown').replace(/[<>]/g,'')}).</div>`;
  }
}
// expose for jobboard.js to reuse via window
window.JSAModule = window.JSAModule || {};
window.JSAModule._renderQrInto = renderQrInto;

async function showJsaSignatureLink(jsaId) {
  const tenantId = window.AppState?.tenant;
  if (!tenantId || !jsaId) return;
  // Open a placeholder modal immediately so the user has feedback while we
  // hit the callable. If generation succeeds, we replace the body with the
  // actual link + actions.
  let overlay = document.getElementById('jsa-sig-link-overlay');
  if (overlay) overlay.remove();
  overlay = document.createElement('div');
  overlay.id = 'jsa-sig-link-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  overlay.innerHTML = `
    <div id="jsa-sig-link-card" style="background:#fff;border-radius:14px;max-width:520px;width:100%;padding:20px;box-shadow:0 10px 40px rgba(0,0,0,0.25);">
      <div style="text-align:center;padding:30px 20px;color:var(--text-secondary);">
        <span class="material-icons" style="font-size:36px;color:var(--brand-blue);">hourglass_empty</span>
        <div style="margin-top:8px;">Generating signing link…</div>
      </div>
    </div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);

  try {
    const fn = httpsCallable(functions, 'generateJsaSignatureToken');
    const res = await fn({ tenantId, jsaId });
    const r = res.data || {};
    if (!r.ok || !r.url) throw new Error('Could not generate link');
    const expiresStr = r.expiresAt ? new Date(r.expiresAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'in 7 days';
    const card = document.getElementById('jsa-sig-link-card');
    if (!card) return;
    card.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:12px;">
        <h3 style="margin:0;font-size:18px;color:var(--text-primary);">JSA Signing Link</h3>
        <button type="button" onclick="document.getElementById('jsa-sig-link-overlay').remove()" style="background:transparent;border:0;cursor:pointer;color:var(--text-secondary);">
          <span class="material-icons">close</span>
        </button>
      </div>
      <p style="margin:0 0 12px;font-size:13px;color:var(--text-secondary);">
        Share this link with anyone on site. They open it on their phone, sign once, and their signature is recorded with this JSA. Up to <strong>${r.signatureCap}</strong> signatures, expires <strong>${expiresStr}</strong>.
      </p>
      <div style="display:flex;gap:6px;margin-bottom:12px;">
        <input id="jsa-sig-link-input" readonly value="${escapeHtml(r.url)}" style="flex:1;padding:10px 12px;border:1px solid #CBD5E1;border-radius:8px;font-size:13px;background:#F8FAFC;color:var(--text-primary);font-family:monospace;">
        <button type="button" id="jsa-sig-link-copy-btn" style="background:var(--brand-blue);color:#fff;border:0;border-radius:8px;padding:10px 14px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">content_copy</span> Copy
        </button>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button type="button" onclick="window.open('${r.url}','_blank','noopener')" class="btn btn-secondary btn-sm">
          <span class="material-icons" style="font-size:14px;">open_in_new</span> Open in browser
        </button>
        <button type="button" id="jsa-sig-qr-btn" class="btn btn-secondary btn-sm">
          <span class="material-icons" style="font-size:14px;">qr_code_2</span> Show QR
        </button>
        ${window.AppState?.driverModePin === true ? '' : `<button type="button" onclick="window.JSAModule.revokeJsaSignatureLink('${window.escJsAttr(jsaId)}')" class="btn btn-sm" style="background:#FEF2F2;color:#991B1B;border:1px solid #FCA5A5;">
          <span class="material-icons" style="font-size:14px;">block</span> Revoke this link
        </button>`}
      </div>
      <div id="jsa-sig-qr-target" style="margin-top:14px;"></div>
      <p style="margin:14px 0 0;font-size:11px;color:var(--text-secondary);line-height:1.4;">
        Open auth: anyone with the link can sign with their name + email. After cap or expiry, the link stops accepting signatures. Signers receive an email receipt of their signature.
      </p>
    `;
    document.getElementById('jsa-sig-link-copy-btn').addEventListener('click', () => {
      const inp = document.getElementById('jsa-sig-link-input');
      navigator.clipboard.writeText(inp.value).then(() => {
        const btn = document.getElementById('jsa-sig-link-copy-btn');
        btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">check</span> Copied';
        setTimeout(() => {
          btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">content_copy</span> Copy';
        }, 2000);
      }).catch(() => {
        inp.select();
      });
    });
    document.getElementById('jsa-sig-qr-btn').addEventListener('click', () => renderQrInto('jsa-sig-qr-target', r.url));
  } catch (e) {
    console.error('[JSA] generate signature link error:', e);
    const card = document.getElementById('jsa-sig-link-card');
    if (card) {
      card.innerHTML = `
        <div style="text-align:center;padding:20px;color:var(--action-danger);">
          <span class="material-icons" style="font-size:36px;">error_outline</span>
          <div style="margin-top:8px;">${escapeHtml(e?.message || 'Could not generate link')}</div>
          <button type="button" onclick="document.getElementById('jsa-sig-link-overlay').remove()" class="btn btn-secondary btn-sm" style="margin-top:12px;">Close</button>
        </div>`;
    }
  }
}

async function revokeJsaSignatureLink(jsaId) {
  const tenantId = window.AppState?.tenant;
  if (!tenantId || !jsaId) return;
  const proceed = await (window.showConfirm?.('Revoke signing link?', 'Existing signatures will be preserved, but no one will be able to add new signatures with the current link. You can generate a new link any time.', { confirmText: 'Revoke', cancelText: 'Cancel', danger: true }) ?? Promise.resolve(window.confirm('Revoke signing link?')));
  if (!proceed) return;
  try {
    const fn = httpsCallable(functions, 'revokeJsaSignatureToken');
    const res = await fn({ tenantId, jsaId });
    if (res.data?.ok) {
      window.showToast?.(`Link revoked${res.data.revokedCount ? ` (${res.data.revokedCount})` : ''}`, 'success');
      document.getElementById('jsa-sig-link-overlay')?.remove();
    } else {
      window.showToast?.('Could not revoke link', 'error');
    }
  } catch (e) {
    console.error('[JSA] revoke error:', e);
    window.showToast?.(e?.message || 'Could not revoke', 'error');
  }
}

window.JSAModule = {
  showNewJSA,
  startForJob, // TASK-10011 — the one "start a JSA for this job" opener (Job Board + office)
  showJsaSignatureLink,
  revokeJsaSignatureLink,
  openDetailCrewSign, // TASK-10024 — Sign on a filed Standard JSA's crew line (server-checked)
  printFromCard,
  goToDispatchAddJob,
  goToCRM,
  onCustomerSelect,
  onJobSelect,
  addTaskRow,
  removeTaskRow,
  updateTaskRow,
  toggleHazardCategory,
  updateLessonsLearned,
  toggleHazard,
  togglePPE,
  togglePPERequired,
  toggleSection,
  initCrewSignature,
  clearJSASignature,
  confirmSignature,
  closeSignatureOverlay,
  addManualCrew,
  removeCrewMember,
  saveJSA,
  viewJSADetail,
  closeJSAModal,
  closeDetailModal,
  toggleJsaDetailMore, // TASK-9511
  closeJsaDetailMore,  // TASK-9511
  filterByDate,
  clearDateFilter,
  renderJSAArchive,
  clearJSAArchiveFilters,
  printJSAArchive,
  toggleJSAArchive, // TASK-9581
  showMoreJSAArchive, // TASK-9581
  downloadPdf,
  // Alias used by new Standard + Oilfield action bars
  downloadJsaPdf: downloadPdf,
  // Format toggle: Standard (TITS-style) and Oilfield (XTO/LSRA-style)
  switchFormMode,
  // TASK-4839 — per-tenant template mode (chips, detail-view sign-in pad, print)
  toggleTemplateCheck,
  openSignInPad,
  openTemplateSelfSignIn, // TASK-10024 — non-filer signs themselves in on a filed template sheet (server-checked)
  printTemplateReport,
  toggleTopic,
  getMyLocation,
  searchFacilities,
  selectFacility,
  clearFacility,
  openXTOSignature,
  toggleLSRA,
  toggleLSRAItem,
  printTITSReport,
  printXTOReport,
  updateXTOSignature,
  showJsaEmailModal,
  emailJsaReport,
  // Back-compat aliases — old button handlers may still reference these names
  showXTOEmailModal: showJsaEmailModal,
  emailXTOReport: emailJsaReport,
  editXTOReport,
  editJSAReport,
  // TASK-361 — My Recent JSAs (driver-side action handlers, ported from jobboard.js)
  openMyJsaEmailOverlay,
  sendMyJsaEmail,
  sendMyJsaEmailTo,
  downloadMyJsaPdf,
  getMyJsaSignatureLink,
  // Shared card renderer exposed for any future caller that wants a JSA card
  // with configurable action buttons. Internal use today is renderMyJsaSection.
  renderActionCard: renderJSAActionCard
};

export default { initJSA, getJSATabHTML, cleanupJSA };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
